/**
 * R158 · Feb 2026 — Planning Board.
 *
 * A master planning surface where every project is a draggable, resizable
 * card on a canvas and each card lists its tasks. Users save named
 * layouts (personal or shared) and filter across every project + task
 * without leaving the page.
 *
 *  Toolbar :
 *    - Layout picker (saved boards)  · New · Save · Duplicate · Delete
 *    - Zoom S / M / L  · Add sticky note · Add project
 *    - Multi-select filters : PM, status, health, client, brand, tag,
 *      priority, task-assignee, task-status, due date range
 *
 *  Canvas :
 *    - <Rnd/>-based cards, snap-to-grid (24px) with free-drag opt-in
 *    - Card header : drag handle, collapse toggle, project name +
 *      cover metrics (open/overdue/progress), status picker
 *    - Card body  : per-card task filter (per-card override) + inline
 *      add-task + task rows (click → existing TaskDetailDialog)
 *    - "View all tasks" button opens the existing Tasks page filtered
 *      to this project.
 *    - Sticky notes overlay for freeform commentary.
 *
 *  Persistence : positions, sizes, collapsed state, filters, sticky
 *  notes, and zoom persist to `planning_boards`. Auto-save (debounced)
 *  after any card move / resize / filter change.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Rnd } from "react-rnd";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, Save, Trash2, Copy, Users, LayoutGrid, StickyNote, ChevronDown,
  ChevronUp, Filter, Search, X, ExternalLink, ListChecks, AlignLeft, Loader2,
  AlertTriangle, Circle, CheckCircle2, Play, Rows, ChevronLeft, ChevronRight,
  MoreHorizontal, Eye, EyeOff, RotateCcw, Edit3, RefreshCw, MessageSquare, MessageSquarePlus, MessageCircle, ZoomIn, ZoomOut, Wand2, GripVertical, SlidersHorizontal, Calendar, Moon, Sun, Undo2,
  SkipBack, SkipForward, Download, Wallet, BadgeCheck, Receipt, Tag, Ban, Clock as ReadyIcon,
  Star,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import PageHeader from "@/components/PageHeader";
import { QuickAddTaskPopover, QuickAddTaskInline } from "@/components/tasks/QuickAddTask";
import { TaskDetail } from "@/pages/app/Tasks";
import { ProjectTasksSubTab, ProjectStatusUpdatesTab } from "@/components/project/ProjectSubTabs";
import { toggleWatch, toggleProjectWatch } from "@/components/WatchListHeaderButton";
import TaskHoverCard from "@/components/tasks/TaskHoverCard";
import PriorityIcon from "@/components/tasks/PriorityIcon";
import BillingNotesDialog from "@/components/tasks/BillingNotesDialog";
import BulkTaskCreator from "@/components/tasks/BulkTaskCreator";
import TaskFinanceDialog, { fmtMoney, symbolFor } from "@/components/tasks/TaskFinanceDialog";
import FinanceReportDialog from "@/components/tasks/FinanceReportDialog";
import ProjectStatusChangeDialog from "@/components/project/ProjectStatusChangeDialog";
import { useAssignOutsideMember } from "@/components/tasks/AssignOutsideMemberDialog";
import ProjectTeamDialog from "@/components/project/ProjectTeamDialog";
import { useProjectStatuses, DEFAULT_STATUS_META } from "@/hooks/useProjectStatuses";
import BoardTemplatesButton from "@/components/planning/BoardTemplatesButton";
import FilterPresetsBar, { presetFingerprint } from "@/components/planning/FilterPresetsBar";
import CloseTaskWithTimeDialog from "@/components/tasks/CloseTaskWithTimeDialog";
import TeamCardsView from "@/pages/app/TeamCardsView";
import TaskShortcutsRow from "@/components/tasks/TaskShortcutsRow";
import StatusTemplatesDialog from "@/components/tasks/StatusTemplatesDialog";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import useWorkspacePerms from "@/lib/useWorkspacePerms";

const GRID = 24;         // snap-to-grid step (px)
const HEADER_H = 44;
const CARD_MIN = { w: 260, h: 180 };
const ZOOM = { xs: 0.5, sm: 0.75, md: 1.0, lg: 1.25, xl: 1.5 };
const ZOOM_ORDER = ["xs", "sm", "md", "lg", "xl"];
const DEFAULT_CARD_W = 340;
const DEFAULT_CARD_H = 320;

const TASK_FILTER_LABELS = {
  open_by_due: "Open · by due date",
  all_open: "All active tasks",
  mine: "Assigned to me",
  this_week: "Due this week",
  all: "All tasks",
};

// R159zz · Feb 2026 — STATUS_META now comes from the workspace's
// custom catalogue (see Settings → Projects) via `useProjectStatuses`.
// The values below are the shipped defaults, used as fallback while
// the fetch is in-flight and also copied server-side. We clone
// `DEFAULT_STATUS_META` so we can mutate this object in place once
// the workspace overrides arrive without touching the source
// constant.
const STATUS_META = { ...Object.fromEntries(Object.entries(DEFAULT_STATUS_META).map(([k, v]) => [k, { ...v }])) };

const HEALTH_DOT = {
  // R159gg · Feb 2026 — Deeper shades so the health chip is legible
  // even at 8×8 px against the darker card border (600 range instead
  // of the old 400/500 which washed out on hover).
  // R159ll · Feb 2026 — Support the canonical DB values (on_track /
  // at_risk / off_track) as well as the short colour names used by
  // some legacy paths. Both resolve to the same dot colour.
  on_track: "bg-emerald-600",
  at_risk: "bg-amber-500",
  off_track: "bg-rose-600",
  green: "bg-emerald-600",
  yellow: "bg-amber-500",
  red: "bg-rose-600",
};

// R159gg · Feb 2026 — Card border tints keyed to project health.
// Unhealthy projects (red / yellow) are spotted at a glance across a
// dense board. Every value pairs a colour + slightly darker hover
// state so the emphasis holds after mouse-over. `neutral` is the
// fallback for projects with no health set yet.
// R159ll · Feb 2026 — Same as HEALTH_DOT, support both key shapes.
const HEALTH_BORDER = {
  on_track: "border-emerald-500 hover:border-emerald-600",
  at_risk: "border-amber-500 hover:border-amber-600",
  off_track: "border-rose-500 hover:border-rose-600",
  green: "border-emerald-500 hover:border-emerald-600",
  yellow: "border-amber-500 hover:border-amber-600",
  red: "border-rose-500 hover:border-rose-600",
};
const HEALTH_BORDER_FALLBACK = "border-neutral-300 hover:border-neutral-400";

// R160dd · Feb 2026 — Chip palette for the inline Health picker on
// the Planning Board card. Green → on track, amber → at risk,
// rose → off track / blocked, slate → new. Kept synchronised with
// HEALTH_DOT and HEALTH_BORDER for consistent visual language.
const HEALTH_CHIP = {
  on_track:  "bg-emerald-100 text-emerald-700 border-emerald-300",
  green:     "bg-emerald-100 text-emerald-700 border-emerald-300",
  at_risk:   "bg-amber-100 text-amber-700 border-amber-300",
  yellow:    "bg-amber-100 text-amber-700 border-amber-300",
  off_track: "bg-rose-100 text-rose-700 border-rose-300",
  red:       "bg-rose-100 text-rose-700 border-rose-300",
  blocked:   "bg-rose-100 text-rose-700 border-rose-300",
  new:       "bg-slate-100 text-slate-700 border-slate-300",
};

// R160hh · Feb 2026 — Project Priority palette. Two variants:
//   • CHIP: dark saturated backgrounds + light text so the priority
//           pill really pops in a busy card header.
//   • HEADER: pastel tinted background applied to the card's title
//           row so the whole card visually inherits its priority
//           without shouting for attention.
// Values: low / medium / high / urgent (see ProjectOverviewTab).
const PRIORITY_CHIP = {
  low:    "bg-slate-600 text-white border-slate-700",
  medium: "bg-sky-600 text-white border-sky-700",
  high:   "bg-amber-600 text-white border-amber-700",
  urgent: "bg-rose-600 text-white border-rose-700",
};
const PRIORITY_HEADER_TINT = {
  low:    "bg-slate-50",
  medium: "bg-sky-50",
  high:   "bg-amber-50",
  urgent: "bg-rose-50",
};
const PRIORITY_LABEL = {
  low: "Low", medium: "Medium", high: "High", urgent: "Urgent",
};

const STICKY_COLORS = {
  amber: "bg-amber-100 border-amber-300",
  emerald: "bg-emerald-100 border-emerald-300",
  blue: "bg-blue-100 border-blue-300",
  rose: "bg-rose-100 border-rose-300",
  neutral: "bg-neutral-100 border-neutral-300",
};

// R268kkkk · Feb 2026 — Deterministic pastel chip colors for the
// Department badge shown on every Planning Board card. Hashes the
// department name to a hue so the same department always uses the
// same color across cards / swimlanes.
function deptChipStyle(name) {
  const s = String(name || "").trim();
  if (!s) return { bg: "#f5f5f4", border: "#d6d3d1", text: "#57534e" };
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  const hue = Math.abs(h) % 360;
  return {
    bg: `hsl(${hue}, 70%, 94%)`,
    border: `hsl(${hue}, 55%, 65%)`,
    text: `hsl(${hue}, 55%, 28%)`,
  };
}
function DepartmentChip({ name, className = "" }) {
  if (!name) return null;
  const s = deptChipStyle(name);
  return (
    <span
      className={`inline-flex items-center max-w-[8rem] truncate text-[9px] uppercase tracking-widest font-semibold px-1.5 py-[1px] border rounded-none ${className}`}
      style={{ background: s.bg, borderColor: s.border, color: s.text }}
      title={`Department: ${name}`}
      data-testid={`planning-card-dept-chip`}
    >
      {name}
    </span>
  );
}

export default function PlanningBoard() {
  const { activeWorkspaceId, activeWorkspace, user } = useAuth();
  const perms = useWorkspacePerms();

  // R159zz · Feb 2026 — Load the workspace's custom status catalogue
  // (rename + colour). Whenever it changes, mutate the module-level
  // STATUS_META in place so every deep-nested reader (StatusPicker,
  // FiltersBar, PresentationOverlay, ProjectCard chips, …) picks up
  // the workspace's labels & colours without a prop-drill.
  const { statusMeta: workspaceStatusMeta } = useProjectStatuses(activeWorkspaceId);
  useEffect(() => {
    if (workspaceStatusMeta) Object.assign(STATUS_META, workspaceStatusMeta);
  }, [workspaceStatusMeta]);
  const canAccountsReview = perms.can("account_notes.manage")
    || perms.can("finance.view")
    || perms.can("workspace.admin")
    || perms.can("workspace.manage")
    || perms.can("task.view.billing");
  const [boards, setBoards] = useState([]);
  const [board, setBoard] = useState(null); // active board doc (or null = ephemeral)
  const [data, setData] = useState(null); // { projects, tasks, options }
  const [loading, setLoading] = useState(true);
  const [zoom, setZoom] = useState("md");
  const [filters, setFilters] = useState(() => {
    // R160ii · Feb 2026 — Persist filter chips + search text across
    // page reloads so PMs pick up exactly where they left off.
    try { const raw = localStorage.getItem("co_planning_view_state"); if (raw) return JSON.parse(raw).filters || {}; } catch { /* ignore */ }
    return {};
  });
  const [savedLayout, setSavedLayout] = useState({}); // project_id → {x,y,w,h,collapsed,task_filter}
  const [stickyNotes, setStickyNotes] = useState([]);
  const [newBoardOpen, setNewBoardOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  // R158b · Feb 2026 — Presentation mode, cross-card drag, swimlanes.
  const [presentIdx, setPresentIdx] = useState(null); // null = off, else index
  const [dragTaskId, setDragTaskId] = useState(null);
  const [hoverProjectId, setHoverProjectId] = useState(null);
  const [swimBy, setSwimBy] = useState(() => {
    try { const raw = localStorage.getItem("co_planning_view_state"); if (raw) return JSON.parse(raw).swimBy || "none"; } catch { /* ignore */ }
    return "none";
  }); // 'none' | 'pm' | 'health' | 'status'
  // R159gg · Feb 2026 — MS-Office style ribbon tabs. Persists to
  // localStorage so a user's chosen tab survives reloads. Accounts
  // tab only appears for users with the relevant permission.
  const [ribbonTab, setRibbonTab] = useState(() => {
    try { return localStorage.getItem("co_planning_ribbon_tab") || "home"; }
    catch { return "home"; }
  });
  useEffect(() => {
    try { localStorage.setItem("co_planning_ribbon_tab", ribbonTab); }
    catch { /* ignore */ }
  }, [ribbonTab]);

  // R159ii · Feb 2026 — "My Board" custom tab. Each ribbon panel gets
  // a small pin toggle; pinned panel IDs are stored per-user in
  // localStorage. The "My Board" tab then renders only the pinned
  // panels so every PM can shape the ribbon to their own workflow
  // instead of one-size-fits-all.
  // R159jj · Feb 2026 — Pins now store as an ordered ARRAY (was a
  // Set). New pins append to the end; drag reorder within the My
  // Board tab uses HTML5 dnd + CSS `order` so the JSX stays put
  // while the visual order matches the user's preference.
  const [myBoardOrder, setMyBoardOrder] = useState(() => {
    try {
      const raw = localStorage.getItem("co_planning_ribbon_mybpins");
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
  });
  useEffect(() => {
    try {
      localStorage.setItem(
        "co_planning_ribbon_mybpins",
        JSON.stringify(myBoardOrder),
      );
    } catch { /* ignore */ }
  }, [myBoardOrder]);
  const myBoardPins = useMemo(() => new Set(myBoardOrder), [myBoardOrder]);
  const togglePin = (id) => setMyBoardOrder((prev) => {
    if (prev.includes(id)) return prev.filter((x) => x !== id);
    return [...prev, id];
  });
  // Drag re-order handlers. `myBoardDrag` tracks the panel being
  // dragged so drop can splice it in at the target's index.
  const dragRef = useRef(null);
  const onPanelDragStart = (id) => (e) => {
    if (ribbonTab !== "myboard") return;
    dragRef.current = id;
    try { e.dataTransfer.effectAllowed = "move"; } catch { /* ignore */ }
  };
  const onPanelDragOver = (e) => {
    if (ribbonTab !== "myboard" || !dragRef.current) return;
    e.preventDefault();
    try { e.dataTransfer.dropEffect = "move"; } catch { /* ignore */ }
  };
  const onPanelDrop = (targetId) => (e) => {
    if (ribbonTab !== "myboard" || !dragRef.current) return;
    e.preventDefault();
    const src = dragRef.current;
    dragRef.current = null;
    if (src === targetId) return;
    setMyBoardOrder((prev) => {
      const from = prev.indexOf(src);
      const to = prev.indexOf(targetId);
      if (from === -1 || to === -1) return prev;
      const next = [...prev];
      next.splice(from, 1);
      next.splice(to, 0, src);
      return next;
    });
  };
  // Helper: should this panel render given the current tab + pins?
  const showPanel = (panelId, homeTabs) => {
    if (ribbonTab === "myboard") return myBoardPins.has(panelId);
    return homeTabs.includes(ribbonTab);
  };
  // Extra flex `order` for My Board so panels lay out in the
  // user-defined sequence without moving JSX.
  const panelOrder = (panelId) => {
    if (ribbonTab !== "myboard") return undefined;
    const idx = myBoardOrder.indexOf(panelId);
    return idx === -1 ? undefined : idx;
  };
  // R159hh · Feb 2026 — Full mobile bottom-sheet per quick-tab.
  // Values: null | "view" | "filter" | "bulk". Tapping a quick tab
  // opens the matching sheet — phones no longer need the wrap-row
  // toolbar at all.
  const [mobileSheet, setMobileSheet] = useState(null);
  // R159jj · Feb 2026 — Toggle for the FiltersBar chip row on mobile.
  // Both the ribbon + chip row are `hidden md:block` by default so
  // phones start at zero-chrome — the Filter sheet has an explicit
  // "Show all filters" switch that flips this on.
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  // R158c · Feb 2026 — Reuse existing app-wide dialogs instead of the
  // Planning-Board-specific ones.
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  const [tasksModalProject, setTasksModalProject] = useState(null);
  const [updatesModalProject, setUpdatesModalProject] = useState(null);
  const [members, setMembers] = useState([]);
  const [watchedIds, setWatchedIds] = useState(() => new Set());
  const [watchedProjectIds, setWatchedProjectIds] = useState(() => new Set());
  const [hideEmptyCards, setHideEmptyCards] = useState(() => {
    try { const raw = localStorage.getItem("co_planning_view_state"); if (raw) return !!JSON.parse(raw).hideEmptyCards; } catch { /* ignore */ }
    return false;
  });
  // Iter · Feb 2026 — Board mode: "projects" (classic free-form) or
  // "team" (one card per active member with status-update shortcuts).
  const [boardMode, setBoardMode] = useState(() => {
    try {
      const raw = localStorage.getItem("co_planning_view_state");
      if (raw) {
        const m = JSON.parse(raw).boardMode;
        if (m === "team" || m === "projects") return m;
      }
    } catch { /* ignore */ }
    return "projects";
  });
  // Iter · Feb 2026 — Workspace status-message templates for the
  // per-card Email/WhatsApp/Copy shortcuts. Fetched once when the
  // board mounts; refreshed when the templates dialog closes.
  const [statusTemplates, setStatusTemplates] = useState(null);
  const [templatesDialogOpen, setTemplatesDialogOpen] = useState(false);
  // Admin flag — derived from the member row for the current user.
  const isAdmin = useMemo(() => {
    if (!user?.id) return false;
    const me = (members || []).find((m) => (m.user_id || m.id) === user.id);
    return !!me && (me.role === "owner" || me.role === "admin");
  }, [members, user?.id]);
  // R159g · Feb 2026 — Quick due-date filter chips. "all" is the
  // default off state. "overdue" = due < today & not done. "today" =
  // due exactly today & not done. "nearing" = due within the next 3
  // days (excl today) & not done. Applied AFTER the per-card
  // `task_filter` so the chip narrows what's already visible.
  const [quickDueFilter, setQuickDueFilter] = useState(() => {
    try { const raw = localStorage.getItem("co_planning_view_state"); if (raw) return JSON.parse(raw).quickDueFilter || "all"; } catch { /* ignore */ }
    return "all";
  });
  // R159o · Feb 2026 — User-configurable default card size. Falls back
  // to DEFAULT_CARD_W / DEFAULT_CARD_H when unset. Persisted with the
  // board so a "wide monitor" preset survives reload.
  const [defaultCardSize, setDefaultCardSize] = useState({ w: DEFAULT_CARD_W, h: DEFAULT_CARD_H });
  const [showCardSizeDialog, setShowCardSizeDialog] = useState(false);
  // R158x · Feb 2026 — "Only watched" filter. Shows only projects that
  // the current user has added to their project-watchlist. Off by
  // default; the toggle lives in the secondary View Options bar.
  const [onlyWatched, setOnlyWatched] = useState(() => {
    try { const raw = localStorage.getItem("co_planning_view_state"); if (raw) return !!JSON.parse(raw).onlyWatched; } catch { /* ignore */ }
    return false;
  });
  // R159u · Feb 2026 — Unbilled filter. When on, only cards whose task
  // list still has at least one billable-but-not-billed (or billed-but-
  // not-paid) task remain visible. Toggled by clicking the per-card
  // billing roll-up chip, or the header "Outstanding $" pill.
  const [unbilledFilter, setUnbilledFilter] = useState(() => {
    try { const raw = localStorage.getItem("co_planning_view_state"); if (raw) return !!JSON.parse(raw).unbilledFilter; } catch { /* ignore */ }
    return false;
  });
  // R158o · Feb 2026 — Per-lane collapse state (swimlanes only). Keyed
  // by lane label. When a lane is collapsed we force every card in it
  // to `collapsed: true` in the render pass.
  const [laneCollapse, setLaneCollapse] = useState({});
  // R158p · Feb 2026 — Custom lane ordering per swimBy dimension.
  // Shape: { pm: ["Alice","Bob",...], client: [...], ... }. Keys not
  // listed fall to the end (alphabetical). Dragged lane labels update
  // this map, which is persisted to the backend `lane_orders` field.
  const [laneOrders, setLaneOrders] = useState({});
  const [dragLaneKey, setDragLaneKey] = useState(null);
  const [dragOverLaneKey, setDragOverLaneKey] = useState(null);
  // R159e · Feb 2026 — Snooze state. IDs of project cards the user has
  // temporarily hidden while working through a large board. Cleared
  // whenever filters change (so re-applying / resetting filters brings
  // every card back). Persisted to the board doc so refresh / re-login
  // resumes exactly where the user left off.
  const [snoozedIds, setSnoozedIds] = useState(() => new Set());
  // R159e · Feb 2026 — Remember the last tidy-up args so we can
  // re-apply the same arrangement after a card is snoozed (matches the
  // user ask "remaining cards will move up or tidy up as per current
  // tidy up option").
  const lastTidyRef = useRef(null); // { sortBy, cols, groupBy }
  // R159e · Feb 2026 — Canvas scroll persistence. We save the scroll
  // offset (throttled via the debounced writer) and restore it after
  // the board first renders.
  const [scrollPos, setScrollPos] = useState({ x: 0, y: 0 });
  const canvasRef = useRef(null);
  const scrollRestoredRef = useRef(false);
  // R159f · Feb 2026 — Mobile viewport detection. On phones (< md)
  // absolute-positioned Rnd cards + swimlane labels overflow the
  // narrow viewport and overlap each other. Detect the size once and
  // switch to a flow-layout stacked list. Also used by All Tasks and
  // TaskDetail dialogs to shed sticky/scroll traps that eat vertical
  // room on small screens.
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches,
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 767px)");
    const on = (e) => setIsMobile(e.matches);
    try { mq.addEventListener("change", on); } catch { mq.addListener(on); }
    return () => {
      try { mq.removeEventListener("change", on); } catch { mq.removeListener(on); }
    };
  }, []);
  // R158u · Feb 2026 — Header redesign: secondary "View options" bar
  // starts collapsed on mobile, expanded on desktop. User toggle persists
  // per browser (localStorage).
  const [viewBarOpen, setViewBarOpen] = useState(() => {
    if (typeof window === "undefined") return true;
    const saved = window.localStorage.getItem("acq-plan-view-bar-open");
    if (saved !== null) return saved === "1";
    return window.matchMedia("(min-width: 768px)").matches;
  });
  useEffect(() => {
    try { window.localStorage.setItem("acq-plan-view-bar-open", viewBarOpen ? "1" : "0"); } catch { /* ignore */ }
  }, [viewBarOpen]);
  // R158s · Feb 2026 — Accounts Notes dialog for project / task scopes.
  // Reused from Billing view so PMs can send private notes to Accounts
  // straight from the planning board & presentation mode.
  const [notesFor, setNotesFor] = useState(null); // { scope: 'project'|'task', id, label }
  // R159w · Feb 2026 — Accounts Review Mode. Toggle in the View-options
  // bar. When on we (a) fetch the last accounts-note per visible project
  // in one round-trip and paint a strip on each card, (b) surface a
  // "Needs review" filter chip, (c) show an age-heatmap tint (green
  // <3d · amber 3-7d · red >7d), (d) expose a Sort-by-newest-note
  // action, and (e) add a per-card Mark-reviewed button. Persisted
  // ack timestamps are kept in localStorage per-workspace so the
  // review queue survives reloads without a new backend collection.
  const [accountsReviewOn, setAccountsReviewOn] = useState(() => {
    try { const raw = localStorage.getItem("co_planning_view_state"); if (raw) return !!JSON.parse(raw).accountsReviewOn; } catch { /* ignore */ }
    return false;
  });
  // R160ii · Feb 2026 — Persist the full board view (filters, group,
  // sticky-bar chip, watched/unbilled/hide-empty toggles, accounts
  // review mode) as a single JSON blob so a PM's daily planning view
  // stays exactly where they left it across sessions. Placed AFTER
  // every dependency is declared to avoid a temporal-dead-zone crash
  // (`Cannot access 'X' before initialization`) at render time.
  useEffect(() => {
    try {
      localStorage.setItem("co_planning_view_state", JSON.stringify({
        filters, swimBy, quickDueFilter, unbilledFilter,
        onlyWatched, hideEmptyCards, accountsReviewOn, boardMode,
      }));
    } catch { /* quota */ }
  }, [filters, swimBy, quickDueFilter, unbilledFilter, onlyWatched, hideEmptyCards, accountsReviewOn, boardMode]);
  const [accountsSummary, setAccountsSummary] = useState({}); // {pid: {body, author_id, author_name, created_at, count}}
  const [taskAccountsSummary, setTaskAccountsSummary] = useState({}); // {task_id: {...}}
  const [accountsLastReadAt, setAccountsLastReadAt] = useState(null); // workspace-level marker
  const [needsReviewOnly, setNeedsReviewOnly] = useState(false);
  const [accountsAck, setAccountsAck] = useState(() => {
    try {
      const ws = (typeof window !== "undefined" && localStorage.getItem("co_ws")) || "";
      const raw = localStorage.getItem(`co_accounts_ack_${ws}`);
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });
  // R159w · Feb 2026 — Bumped whenever the BillingNotesDialog closes so
  // the summary effect refetches and any new notes surface without a
  // page refresh.
  const [accountsRefreshTick, setAccountsRefreshTick] = useState(0);
  // R159y · Feb 2026 — Task Finance dialog target and the Finance
  // Report modal. `financeTask` is the task object being edited (also
  // works from the report list). `showFinanceReport` toggles the full
  // table dialog. `ageFilter` is a single-select chip; "all" is off.
  const [financeTask, setFinanceTask] = useState(null);
  const [showFinanceReport, setShowFinanceReport] = useState(false);
  const [ageFilter, setAgeFilter] = useState("all"); // 'all' | 'fresh' | 'warm' | 'stale'
  const canFinanceEdit = perms.can("finance.view")
    || perms.can("workspace.admin")
    || perms.can("workspace.manage")
    || perms.can("account_notes.manage");
  // R159y · Feb 2026 — Bulk-add tasks perm gate reused verbatim from
  // the Tasks module. The Planning Board's per-card button opens the
  // same BulkTaskCreator scoped to that project.
  const canBulkAddTask = perms.can("task.bulk_create")
    || perms.can("workspace.admin")
    || perms.can("workspace.manage");
  const [bulkAddProject, setBulkAddProject] = useState(null);
  const saveTimer = useRef(null);
  const skipAutoSave = useRef(true);

  const loadBoards = useCallback(async () => {
    if (!activeWorkspaceId) return;
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/planning-boards`);
      setBoards(data.rows || []);
      return data.rows || [];
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load boards"));
      return [];
    }
  }, [activeWorkspaceId]);

  const loadData = useCallback(async (activeFilters) => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    const params = new URLSearchParams();
    const push = (k, v) => v && v.length && params.set(k, v.join(","));
    push("owner_ids", activeFilters?.owner_ids);
    push("statuses", activeFilters?.statuses);
    push("healths", activeFilters?.healths);
    push("client_ids", activeFilters?.client_ids);
    push("brand_ids", activeFilters?.brand_ids);
    push("department_ids", activeFilters?.department_ids);
    push("tags", activeFilters?.tags);
    push("priorities", activeFilters?.priorities);
    push("project_ids", activeFilters?.project_ids);
    push("task_assignee_ids", activeFilters?.task_assignee_ids);
    push("task_statuses", activeFilters?.task_statuses);
    if (activeFilters?.task_due_from) params.set("task_due_from", activeFilters.task_due_from);
    if (activeFilters?.task_due_to) params.set("task_due_to", activeFilters.task_due_to);
    if (activeFilters?.q) params.set("q", activeFilters.q);
    try {
      const url = `/workspaces/${activeWorkspaceId}/planning-board-data${params.toString() ? `?${params}` : ""}`;
      const { data } = await api.get(url);
      setData(data);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load projects"));
    } finally {
      setLoading(false);
    }
  }, [activeWorkspaceId]);

  useEffect(() => {
    if (!activeWorkspaceId) { setMembers([]); return; }
    (async () => {
      try {
        const { data } = await api.get(`/workspaces/${activeWorkspaceId}/members`);
        setMembers(data || []);
      } catch { setMembers([]); }
    })();
  }, [activeWorkspaceId]);

  // Iter · Feb 2026 — Load workspace status-message templates once
  // per workspace; refresh when the templates dialog closes.
  const loadTemplates = useCallback(async () => {
    if (!activeWorkspaceId) { setStatusTemplates(null); return; }
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/status-templates`);
      setStatusTemplates(data || {});
    } catch { setStatusTemplates({}); }
  }, [activeWorkspaceId]);
  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  // R158d · Watched-task ids (per user) — used to render the eye icon
  // on every task row + resync when the header popup toggles a task.
  const loadWatchedIds = useCallback(async () => {
    if (!activeWorkspaceId) { setWatchedIds(new Set()); setWatchedProjectIds(new Set()); return; }
    try {
      const [{ data: td }, { data: pd }] = await Promise.all([
        api.get(`/workspaces/${activeWorkspaceId}/task-watch/ids`),
        api.get(`/workspaces/${activeWorkspaceId}/project-watch/ids`).catch(() => ({ data: { project_ids: [] } })),
      ]);
      setWatchedIds(new Set(td?.task_ids || []));
      setWatchedProjectIds(new Set(pd?.project_ids || []));
    } catch { /* silent */ }
  }, [activeWorkspaceId]);

  useEffect(() => { loadWatchedIds(); }, [loadWatchedIds]);
  useEffect(() => {
    const h = () => loadWatchedIds();
    window.addEventListener("acq:watch-refresh", h);
    return () => window.removeEventListener("acq:watch-refresh", h);
  }, [loadWatchedIds]);

  const onToggleWatch = useCallback(async (taskId, currentlyWatched) => {
    // Optimistic update — toggleWatch fires the acq:watch-refresh event
    // that re-syncs via loadWatchedIds as well.
    setWatchedIds((prev) => {
      const next = new Set(prev);
      if (currentlyWatched) next.delete(taskId); else next.add(taskId);
      return next;
    });
    await toggleWatch(activeWorkspaceId, taskId, currentlyWatched);
  }, [activeWorkspaceId]);

  const onToggleProjectWatch = useCallback(async (projectId, currentlyWatched) => {
    setWatchedProjectIds((prev) => {
      const next = new Set(prev);
      if (currentlyWatched) next.delete(projectId); else next.add(projectId);
      return next;
    });
    await toggleProjectWatch(activeWorkspaceId, projectId, currentlyWatched);
  }, [activeWorkspaceId]);

  // R158e · Feb 2026 — Per-project reload after task add / status change.
  // Avoids the full-board refresh (which re-fetches every project) when
  // only one card's contents changed. Falls back gracefully if the
  // tasks-search endpoint is unavailable.
  const reloadProjectTasks = useCallback(async (projectId) => {
    if (!activeWorkspaceId || !projectId) return;
    try {
      const { data: fresh } = await api.get(
        `/workspaces/${activeWorkspaceId}/tasks-search?project_id=${projectId}&limit=500`,
      );
      const freshTasks = Array.isArray(fresh) ? fresh : (fresh?.rows || []);
      // Normalise to the lean shape the board uses (id, title, status,
      // priority, due_date, start_date, assignee_id, assignee_name,
      // project_id, project_name, progress_percent, est_hours, tags,
      // created_at) — extra fields are harmless.
      setData((prev) => {
        if (!prev) return prev;
        const others = (prev.tasks || []).filter((t) => t.project_id !== projectId);
        return { ...prev, tasks: [...others, ...freshTasks] };
      });
    } catch (e) {
      // Fallback: full board refresh so nothing gets stale.
      // eslint-disable-next-line no-console
      console.warn("Per-project reload failed; falling back to full refresh", e);
      loadData(filters);
    }
  }, [activeWorkspaceId, filters, loadData]);

  // R158t · Feb 2026 — Optimistic task insert. When QuickAddTask returns
  // a newly-created task, we splice it into `data.tasks` immediately so
  // it's guaranteed to appear regardless of any race with the follow-up
  // tasks-search reload (network hiccups, presentIdx navigation, top-N
  // slice on the Present view). The reconciliation reload still runs
  // for eventual consistency.
  const insertNewTaskOptimistic = useCallback((task) => {
    if (!task || !task.id) return;
    setData((prev) => {
      if (!prev) return prev;
      // De-dupe by id in case the reload already landed first.
      const existing = (prev.tasks || []).filter((t) => t.id !== task.id);
      return { ...prev, tasks: [task, ...existing] };
    });
    if (task.project_id) reloadProjectTasks(task.project_id);
  }, [reloadProjectTasks]);

  // R159w · Feb 2026 — Fetch accounts-notes summary + workspace read
  // marker whenever Accounts Review mode is turned on OR the visible
  // project list changes. One aggregation-backed call returns
  // {pid: {body, author_id, author_name, created_at, count}} — no N+1.
  useEffect(() => {
    if (!accountsReviewOn || !activeWorkspaceId || !data?.projects?.length) return;
    let cancelled = false;
    (async () => {
      try {
        const ids = data.projects.map((p) => p.id).filter(Boolean);
        if (ids.length === 0) return;
        // Backend accepts repeated `ids` query params.
        const qs = ids.map((id) => `ids=${encodeURIComponent(id)}`).join("&");
        const { data: summary } = await api.get(
          `/workspaces/${activeWorkspaceId}/billing-notes/summary?scope=project&${qs}`,
        );
        if (cancelled) return;
        setAccountsSummary(summary || {});
      } catch { if (!cancelled) setAccountsSummary({}); }
      // R159x · Feb 2026 — Also fetch task-scope notes for every task on
      // the visible boards. We reuse the same `summary` endpoint so
      // there's no new backend contract; grouping by project happens
      // client-side using the task.project_id already loaded.
      try {
        const taskIds = (data.tasks || []).map((t) => t.id).filter(Boolean);
        if (taskIds.length === 0) { if (!cancelled) setTaskAccountsSummary({}); }
        else {
          // Chunk to keep URL length sane (400 ids ≈ 15 kb).
          const chunks = [];
          for (let i = 0; i < taskIds.length; i += 400) chunks.push(taskIds.slice(i, i + 400));
          const merged = {};
          for (const chunk of chunks) {
            const qs2 = chunk.map((id) => `ids=${encodeURIComponent(id)}`).join("&");
            try {
              const { data: ts } = await api.get(
                `/workspaces/${activeWorkspaceId}/billing-notes/summary?scope=task&${qs2}`,
              );
              Object.assign(merged, ts || {});
            } catch { /* per-chunk silent */ }
          }
          if (!cancelled) setTaskAccountsSummary(merged);
        }
      } catch { if (!cancelled) setTaskAccountsSummary({}); }
      try {
        const { data: unread } = await api.get(
          `/workspaces/${activeWorkspaceId}/billing-notes/unread-count`,
        );
        if (!cancelled) setAccountsLastReadAt(unread?.last_read_at || null);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [accountsReviewOn, activeWorkspaceId, data?.projects, data?.tasks, accountsRefreshTick]);

  // Persist per-project ack timestamps so the Needs-Review queue survives
  // reloads without a new backend collection.
  useEffect(() => {
    try {
      if (!activeWorkspaceId) return;
      localStorage.setItem(`co_accounts_ack_${activeWorkspaceId}`, JSON.stringify(accountsAck));
    } catch { /* quota / private-mode — silent */ }
  }, [accountsAck, activeWorkspaceId]);

  const markAccountsReviewed = useCallback((pid) => {
    const note = accountsSummary[pid];
    if (!note?.created_at) return;
    setAccountsAck((prev) => ({ ...prev, [pid]: note.created_at }));
    toast.success("Marked reviewed", { duration: 1400 });
  }, [accountsSummary]);

  const needsReviewForProject = useCallback((pid) => {
    const note = accountsSummary[pid];
    if (!note) return false;
    // A card needs review when the last note isn't authored by me AND
    // I haven't ack'd up to its timestamp (either via per-project ack
    // or via the workspace-wide mark-all-read marker).
    if (note.author_id === user?.id) return false;
    const ack = accountsAck[pid] || accountsLastReadAt || "";
    return note.created_at > ack;
  }, [accountsSummary, accountsAck, accountsLastReadAt, user?.id]);

  // R159x · Feb 2026 — Group task-scope notes by their parent project.
  // Sorted newest-first, capped at 5 per project to keep the popover
  // tight. `label` is the task title (falls back to id when unloaded).
  const taskNotesByProject = useMemo(() => {
    if (!accountsReviewOn) return {};
    const byPid = {};
    const taskById = new Map((data?.tasks || []).map((t) => [t.id, t]));
    Object.entries(taskAccountsSummary).forEach(([tid, note]) => {
      const t = taskById.get(tid);
      if (!t?.project_id) return;
      const arr = byPid[t.project_id] || (byPid[t.project_id] = []);
      arr.push({ ...note, task_id: tid, task_name: t.title || tid });
    });
    Object.values(byPid).forEach((arr) => {
      arr.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
      arr.length = Math.min(arr.length, 5);
    });
    return byPid;
  }, [accountsReviewOn, taskAccountsSummary, data?.tasks]);

  // R159x · Feb 2026 — Inline reply — post a new project-scope note
  // straight from the card, then bump the refresh tick so every strip
  // updates without a page reload.
  const postInlineAccountsReply = useCallback(async (projectId, body) => {
    const text = String(body || "").trim();
    if (!text || !activeWorkspaceId) return false;
    try {
      await api.post(`/workspaces/${activeWorkspaceId}/billing-notes`, {
        scope: "project", scope_id: projectId, body: text,
      });
      setAccountsRefreshTick((t) => t + 1);
      toast.success("Accounts note added");
      return true;
    } catch {
      toast.error("Couldn't post note — check your permissions");
      return false;
    }
  }, [activeWorkspaceId]);

  // R159x · Feb 2026 — Copy digest of every visible card's last accounts
  // note. Plain-text formatted so it drops straight into Slack / Gmail.
  const copyAccountsDigest = useCallback(async () => {
    const projects = data?.projects || [];
    const visible = projects.filter((p) => {
      if (snoozedIds.has(p.id)) return false;
      if (onlyWatched && !watchedProjectIds.has(p.id)) return false;
      if (accountsReviewOn && needsReviewOnly && !needsReviewForProject(p.id)) return false;
      return true;
    });
    const lines = [
      `Accounts digest · ${new Date().toLocaleString()} · ${visible.length} project${visible.length === 1 ? "" : "s"}`,
      "─".repeat(48),
    ];
    let notedCount = 0;
    visible.forEach((p) => {
      const n = accountsSummary[p.id];
      if (!n) {
        lines.push(`• ${p.name || p.id} — (no accounts note yet)`);
        return;
      }
      notedCount += 1;
      const body = String(n.body || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
      const dt = n.created_at ? new Date(n.created_at).toLocaleDateString() : "";
      lines.push(`• ${p.name || p.id}`);
      lines.push(`    "${body}"`);
      lines.push(`    — ${n.author_name || "unknown"} · ${dt}${n.count > 1 ? ` · ${n.count} notes` : ""}`);
    });
    const text = lines.join("\n");
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`Copied digest · ${notedCount} noted / ${visible.length} visible`);
    } catch {
      toast.error("Clipboard blocked — grant permission and retry");
    }
  }, [data?.projects, snoozedIds, onlyWatched, watchedProjectIds, accountsReviewOn, needsReviewOnly, needsReviewForProject, accountsSummary]);

  // R159y · Feb 2026 — CSV export of the same digest. Downloads as
  // <project,body,author,date,needs_review,note_count>. Uses the same
  // visible-cards filter as `copyAccountsDigest` so the two stay in sync.
  const downloadAccountsDigestCSV = useCallback(() => {
    const projects = data?.projects || [];
    const visible = projects.filter((p) => {
      if (snoozedIds.has(p.id)) return false;
      if (onlyWatched && !watchedProjectIds.has(p.id)) return false;
      if (accountsReviewOn && needsReviewOnly && !needsReviewForProject(p.id)) return false;
      return true;
    });
    const escape = (v) => {
      if (v == null) return "";
      const s = String(v);
      if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const rows = [["Project", "Client", "Brand", "Last note body", "Author", "Date", "Needs review", "Note count"]];
    visible.forEach((p) => {
      const n = accountsSummary[p.id];
      const body = n ? String(n.body || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim() : "";
      rows.push([
        p.name || p.id,
        p.client_name || "",
        p.brand_name || "",
        body,
        n?.author_name || "",
        n?.created_at || "",
        n && needsReviewForProject(p.id) ? "yes" : "",
        n?.count || 0,
      ]);
    });
    const csv = rows.map((r) => r.map(escape).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `accounts-digest-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 500);
    toast.success(`Exported CSV · ${visible.length} projects`);
  }, [data?.projects, snoozedIds, onlyWatched, watchedProjectIds, accountsReviewOn, needsReviewOnly, needsReviewForProject, accountsSummary]);

  // R159y · Feb 2026 — Per-project finance roll-up. Sums est_amount /
  // paid_amount / balance per currency across the project's tasks —
  // mixed-currency projects render each currency on its own row so
  // there is never an ambiguous cross-currency total.
  const projectFinanceByPid = useMemo(() => {
    if (!accountsReviewOn) return {};
    const byPid = {};
    (data?.tasks || []).forEach((t) => {
      const est = Number(t.est_amount) || 0;
      const paid = Number(t.paid_amount) || 0;
      if (est === 0 && paid === 0) return;
      const c = t.currency || "INR";
      const g = byPid[t.project_id] || (byPid[t.project_id] = {});
      const pc = g[c] || (g[c] = { est: 0, paid: 0, balance: 0 });
      pc.est += est;
      pc.paid += paid;
      pc.balance += (est - paid);
    });
    return byPid;
  }, [accountsReviewOn, data?.tasks]);

  // R159y · Feb 2026 — Board-level total (per currency) computed over
  // ONLY visible cards so the header chip matches what the reviewer
  // actually sees. Recomputed on every filter change.
  const boardFinanceRollup = useMemo(() => {
    if (!accountsReviewOn) return {};
    const acc = {};
    const visibleIds = new Set(
      (data?.projects || [])
        .filter((p) => !snoozedIds.has(p.id))
        .filter((p) => !onlyWatched || watchedProjectIds.has(p.id))
        .filter((p) => !(accountsReviewOn && needsReviewOnly) || needsReviewForProject(p.id))
        .map((p) => p.id),
    );
    Object.entries(projectFinanceByPid).forEach(([pid, byC]) => {
      if (!visibleIds.has(pid)) return;
      Object.entries(byC).forEach(([c, v]) => {
        const pc = acc[c] || (acc[c] = { est: 0, paid: 0, balance: 0 });
        pc.est += v.est;
        pc.paid += v.paid;
        pc.balance += v.balance;
      });
    });
    return acc;
  }, [accountsReviewOn, projectFinanceByPid, data?.projects, snoozedIds, onlyWatched, watchedProjectIds, needsReviewOnly, needsReviewForProject]);

  // R159cc · Feb 2026 — Per-project effort roll-up. Computes sum-of-
  // est-hours vs sum-of-total-logged (manual + timesheet + actual)
  // across ALL tasks on the project. Used by the Effort-Overrun
  // ribbon on ProjectCard and matches the Finance Report's total.
  const projectEffortByPid = useMemo(() => {
    const byPid = {};
    (data?.tasks || []).forEach((t) => {
      if (!t.project_id) return;
      const est = Number(t.est_hours) || 0;
      const logged = (Number(t.manual_effort_hours) || 0)
        + (Number(t.logged_hours) || Number(t.timesheet_hours) || 0)
        + (Number(t.actual_hours) || 0);
      const g = byPid[t.project_id] || (byPid[t.project_id] = { est: 0, logged: 0 });
      g.est += est;
      g.logged += logged;
    });
    return byPid;
  }, [data?.tasks]);

  const openTaskFinance = useCallback((task) => {
    setFinanceTask(task);
  }, []);
  const onFinanceSaved = useCallback((updated) => {
    // Optimistic patch of the loaded task list so the roll-up + row
    // reflect the change immediately.
    setData((prev) => {
      if (!prev) return prev;
      return { ...prev, tasks: (prev.tasks || []).map((t) => t.id === updated.id ? { ...t, ...updated } : t) };
    });
    setAccountsRefreshTick((t) => t + 1);
  }, []);


  // R158d · Quick status change from the task row's colour dot.
  // Iter · Feb 2026 — When a user flips a task to `done` we OPEN the
  // CloseTaskWithTimeDialog first so the task can't be closed silently
  // with zero logged effort. The dialog handles the atomic close +
  // time-log via /tasks/{id}/close-with-time; here we just intercept
  // and hand off. Any other status transition proceeds as before.
  const [closeGuardTask, setCloseGuardTask] = useState(null); // full task object
  const onChangeTaskStatus = useCallback(async (taskId, newStatus) => {
    if (newStatus === "done") {
      const t = (data?.tasks || []).find((x) => x.id === taskId);
      if (t && t.status !== "done") {
        setCloseGuardTask(t);
        return; // dialog will call the API
      }
    }
    try {
      await api.patch(`/tasks/${taskId}`, { status: newStatus });
      setData((prev) => prev ? {
        ...prev,
        tasks: prev.tasks.map((t) => t.id === taskId ? { ...t, status: newStatus } : t),
      } : prev);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Status update failed"));
    }
  }, [data?.tasks]);

  // R158l · Feb 2026 — Quick priority change from the task row's icon.
  const onChangeTaskPriority = useCallback(async (taskId, newPriority) => {
    try {
      await api.patch(`/tasks/${taskId}`, { priority: newPriority });
      setData((prev) => prev ? {
        ...prev,
        tasks: prev.tasks.map((t) => t.id === taskId ? { ...t, priority: newPriority } : t),
      } : prev);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Priority update failed"));
    }
  }, []);

  useEffect(() => {
    (async () => {
      const rows = await loadBoards();
      // Pick the most recently updated board (mine first, then shared)
      const mine = rows.filter((b) => b.owner_id === user?.id);
      const first = mine[0] || rows[0];
      if (first) selectBoard(first);
      else {
        // R158d · Feb 2026 — Auto-create a personal "My board" the first
        // time a user opens the Planning Board so any card move / resize /
        // filter change auto-persists (previously all layout changes on
        // the ephemeral view were lost on refresh).
        try {
          const { data } = await api.post(`/workspaces/${activeWorkspaceId}/planning-boards`, {
            name: "My board",
            is_shared: false,
          });
          if (data?.id) {
            await loadBoards();
            selectBoard(data);
          } else {
            loadData({});
          }
        } catch (e) {
          console.warn("Auto-create board failed", e);
          loadData({});
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeWorkspaceId]);

  const selectBoard = (b) => {
    skipAutoSave.current = true;
    setBoard(b);
    setZoom(b.zoom || "md");
    setFilters(b.filters || {});
    setSavedLayout(b.layout || {});
    setStickyNotes(b.sticky_notes || []);
    setSwimBy(b.swim_by || "none");
    setLaneOrders(b.lane_orders || {});
    // R159e · Feb 2026 — Restore snooze + scroll.
    setSnoozedIds(new Set(b.snoozed_ids || []));
    setScrollPos(b.scroll || { x: 0, y: 0 });
    // R159h · Feb 2026 — Restore the persisted quick-due chip so a PM's
    // bookmarked "Overdue-only" preset survives reload / re-login.
    setQuickDueFilter(b.quick_due_filter || "all");
    // R159o · Feb 2026 — Restore custom default card size.
    setDefaultCardSize(b.default_card_size || { w: DEFAULT_CARD_W, h: DEFAULT_CARD_H });
    scrollRestoredRef.current = false;
    loadData(b.filters || {});
    setTimeout(() => { skipAutoSave.current = false; }, 500);
  };

  // R159e · Feb 2026 — After data loads for the first time on a board,
  // scroll the canvas back to the saved offset so the user resumes
  // exactly where they left off. `scrollRestoredRef` prevents re-apply
  // on every subsequent data refresh.
  useEffect(() => {
    if (loading || !data?.projects || scrollRestoredRef.current) return;
    if (!canvasRef.current) return;
    const { x, y } = scrollPos || {};
    if (x || y) {
      canvasRef.current.scrollTo({ left: x || 0, top: y || 0, behavior: "instant" });
    }
    scrollRestoredRef.current = true;
  }, [loading, data, scrollPos]);

  // R159yy · Feb 2026 — Free-text search now HIGHLIGHTS matches instead
  // of filtering the board. Every card stays visible; the search bar
  // shows a `n / m` counter with prev / next arrows to cycle through
  // matches. `searchedProjects` therefore just returns the full list.
  const searchedProjects = data?.projects || [];
  const searchMatchIds = useMemo(() => {
    const q = (filters.q || "").trim().toLowerCase();
    if (!q || !data?.projects) return [];
    return (data.projects || [])
      .filter((p) => {
        const hay = `${p.name || ""} ${p.client_name || ""} ${p.brand_name || ""} ${p.pm_name || ""} ${p.owner_name || ""}`.toLowerCase();
        return hay.includes(q);
      })
      .map((p) => p.id);
  }, [filters.q, data?.projects]);
  const [searchIdx, setSearchIdx] = useState(0);

  // R159r · Feb 2026 — Surgical single-project patch. Splices the
  // updated fields into the loaded data.projects list without a full
  // reload — used by inline Client/Brand/PM reassign so only the
  // affected card re-renders.
  const patchProjectLocal = useCallback((projectId, patch) => {
    setData((prev) => {
      if (!prev?.projects) return prev;
      return {
        ...prev,
        projects: prev.projects.map((p) => (p.id === projectId ? { ...p, ...patch } : p)),
      };
    });
  }, []);

  // R160aa · Feb 2026 — Refetch a SINGLE project + its tasks and merge
  // the fresh rows into `data` without a full board reload. Used by
  // status-change confirm, per-card BulkTaskCreator, ProjectTeamDialog
  // save, and any other card-scoped mutation. Falls back to a full
  // `loadData(filters)` if the granular fetch fails so the user isn't
  // left staring at stale data.
  const refetchOneProject = useCallback(async (projectId) => {
    if (!projectId) return;
    try {
      const [projRes, tasksRes] = await Promise.all([
        api.get(`/projects/${projectId}`),
        api.get(`/tasks`, { params: { project_id: projectId, workspace_id: activeWorkspaceId } }),
      ]);
      const fresh = projRes?.data || null;
      const freshTasks = Array.isArray(tasksRes?.data) ? tasksRes.data : (tasksRes?.data?.items || []);
      setData((prev) => {
        if (!prev) return prev;
        // Keep the denormalised project_member_ids from the board load
        // if the single-project endpoint doesn't provide them.
        const prevProject = (prev.projects || []).find((p) => p.id === projectId) || {};
        const nextProjects = fresh
          ? (prev.projects || []).map((p) => p.id === projectId
              ? { ...prevProject, ...fresh, project_member_ids: fresh.project_member_ids || prevProject.project_member_ids || [] }
              : p)
          : (prev.projects || []);
        const nextTasks = [
          ...(prev.tasks || []).filter((t) => t.project_id !== projectId),
          ...freshTasks,
        ];
        return { ...prev, projects: nextProjects, tasks: nextTasks };
      });
    } catch {
      // Silent fallback — if the surgical refresh failed, reload the
      // whole board so the user still sees consistent data.
      loadData(filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeWorkspaceId, filters]);

  // R138j · Feb 2026 — Even more surgical: refetch ONLY the project
  // document, not its tasks. Used from ProjectTeamDialog / other meta
  // updates (member add / remove, rename, etc.) that don't touch tasks
  // — avoids the perceptible flicker of the tasks list re-rendering
  // across every card on the board.
  const refetchOneProjectMeta = useCallback(async (projectId) => {
    if (!projectId) return;
    try {
      const projRes = await api.get(`/projects/${projectId}`);
      const fresh = projRes?.data || null;
      if (!fresh) return;
      setData((prev) => {
        if (!prev) return prev;
        const prevProject = (prev.projects || []).find((p) => p.id === projectId) || {};
        const nextProjects = (prev.projects || []).map((p) => p.id === projectId
          ? { ...prevProject, ...fresh, project_member_ids: fresh.project_member_ids || prevProject.project_member_ids || [] }
          : p);
        // Keep the tasks array reference identical so unrelated cards
        // don't re-render.
        return { ...prev, projects: nextProjects };
      });
    } catch { /* silent — the dialog's own load() still shows the roster */ }
  }, []);

  const [flashProjectId, setFlashProjectId] = useState(null);

  // R159yy · Feb 2026 — Cycle-to-match helper for the highlight-only
  // search bar. Scrolls the Nth matching project into view + flashes
  // it. Resets `searchIdx` when the match list changes so the counter
  // stays sane.
  const scrollToMatch = useCallback((idx) => {
    const list = searchMatchIds;
    if (!list || list.length === 0) return;
    const safe = ((idx % list.length) + list.length) % list.length;
    const pid = list[safe];
    setSearchIdx(safe);
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="planning-card-${pid}"]`);
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
      }
      setFlashProjectId(pid);
      setTimeout(() => setFlashProjectId(null), 2200);
    }, 60);
  }, [searchMatchIds]);
  // Any time the query changes → jump to the first match automatically.
  useEffect(() => {
    if (searchMatchIds.length > 0) scrollToMatch(0);
    else setSearchIdx(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchMatchIds.join("|")]);

  // R159z · Feb 2026 — Deep-link handler for the "accounts_mention"
  // notification. Reads `thread_scope`, `thread_id`, `thread_ws` from
  // the URL; opens the BillingNotesDialog at that scope and flashes
  // the target project card once the planning-board data has loaded.
  // The params are cleared after consumption so a refresh doesn't
  // re-trigger the dialog.
  const [searchParams, setSearchParams] = useSearchParams();
  const consumedDeepLinkRef = useRef(false);
  useEffect(() => {
    if (consumedDeepLinkRef.current) return;
    const scope = searchParams.get("thread_scope");
    const sid = searchParams.get("thread_id");
    const tws = searchParams.get("thread_ws");
    if (!scope || !sid || !data) return;
    if (tws && activeWorkspaceId && tws !== activeWorkspaceId) return; // workspace-switch in flight
    consumedDeepLinkRef.current = true;
    // Resolve a friendly label + project id to flash.
    let label = sid;
    let flashPid = scope === "project" ? sid : null;
    if (scope === "task") {
      const t = (data.tasks || []).find((x) => x.id === sid);
      if (t) { label = t.title || sid; flashPid = t.project_id || null; }
    } else {
      const p = (data.projects || []).find((x) => x.id === sid);
      if (p) label = p.name || sid;
    }
    setNotesFor({ scope, id: sid, label });
    if (flashPid) {
      setFlashProjectId(flashPid);
      setTimeout(() => setFlashProjectId(null), 3000);
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="planning-card-${flashPid}"]`);
        if (el?.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
      }, 200);
    }
    // Clean up the URL so a subsequent refresh doesn't re-open.
    const next = new URLSearchParams(searchParams);
    next.delete("thread_scope");
    next.delete("thread_id");
    next.delete("thread_ws");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, activeWorkspaceId]);

  // Debounced auto-save on layout / filters / sticky / zoom changes.
  const persist = useCallback(async () => {
    if (!board?.id || skipAutoSave.current) return;
    setSaving(true);
    try {
      await api.patch(`/workspaces/${activeWorkspaceId}/planning-boards/${board.id}`, {
        layout: savedLayout,
        sticky_notes: stickyNotes,
        filters,
        zoom,
        swim_by: swimBy,
        lane_orders: laneOrders,
        // R159e · Feb 2026 — Snooze + scroll persistence.
        snoozed_ids: Array.from(snoozedIds),
        scroll: scrollPos,
        // R159h · Feb 2026 — Persist the quick-due chip on the board doc.
        quick_due_filter: quickDueFilter,
        // R159o · Feb 2026 — Persist user-configured default card size.
        default_card_size: defaultCardSize,
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("Board save failed", e);
    } finally {
      setSaving(false);
    }
  }, [board?.id, savedLayout, stickyNotes, filters, zoom, activeWorkspaceId, swimBy, laneOrders, snoozedIds, scrollPos, quickDueFilter, defaultCardSize]);

  useEffect(() => {
    if (!board?.id) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => { persist(); }, 900);
    return () => clearTimeout(saveTimer.current);
  }, [savedLayout, stickyNotes, filters, zoom, swimBy, laneOrders, snoozedIds, scrollPos, quickDueFilter, defaultCardSize, persist, board?.id]);

  // R158c · Fetch full task doc when the user clicks a task row so the
  // existing TaskDetail dialog can render with the complete payload
  // (planning-board-data returns a lean projection).
  useEffect(() => {
    if (!editingTaskId) { setEditingTask(null); return; }
    (async () => {
      try {
        const { data } = await api.get(`/tasks/${editingTaskId}`);
        setEditingTask(data);
      } catch (e) {
        toast.error(apiErrorMessage(e, "Failed to load task"));
        setEditingTaskId(null);
      }
    })();
  }, [editingTaskId]);

  const patchTaskFromDetail = async (id, patch) => {
    // R158x · Feb 2026 — Optimistic first. Update `editingTask` and the
    // board's lean task list IMMEDIATELY so controlled selects/pickers
    // snap to the new value without waiting for the round-trip (fixes
    // the "picker snaps back to old value" flicker + the general
    // "some edits don't persist on the popup" complaint).
    setEditingTask((cur) => (cur && cur.id === id ? { ...cur, ...patch } : cur));
    setData((prev) => prev ? {
      ...prev,
      tasks: prev.tasks.map((t) => t.id === id ? { ...t, ...patch } : t),
    } : prev);
    try {
      const { data } = await api.patch(`/tasks/${id}`, patch);
      // Reconcile with server truth (in case the backend enriched extra
      // fields, or applied side-effects like status→completed_at).
      if (data && typeof data === "object") {
        setEditingTask((cur) => (cur && cur.id === id ? { ...cur, ...data } : cur));
        setData((prev) => prev ? {
          ...prev,
          tasks: prev.tasks.map((t) => t.id === id ? { ...t, ...data } : t),
        } : prev);
      }
      return data;
    } catch (e) {
      // Roll back the optimistic write on failure.
      toast.error(apiErrorMessage(e, "Update failed"));
      throw e;
    }
  };

  const projectsById = useMemo(() => {
    const m = {};
    (data?.projects || []).forEach((p) => { m[p.id] = p; });
    return m;
  }, [data]);

  // R159ss · Feb 2026 — Fast lookup of a project's active-member ids.
  // Backed by `project.project_member_ids` (owner + PM already merged
  // in server-side). Used to red-bold assignees who aren't on the
  // project's roster and to gate the assignee-picker flow.
  const projectMemberIdsById = useMemo(() => {
    const m = {};
    (data?.projects || []).forEach((p) => { m[p.id] = new Set(p.project_member_ids || []); });
    return m;
  }, [data]);
  const isOutsideMember = useCallback((task) => {
    if (!task || !task.assignee_id || !task.project_id) return false;
    const set = projectMemberIdsById[task.project_id];
    if (!set) return false;
    return !set.has(task.assignee_id);
  }, [projectMemberIdsById]);

  const { guardedAssign, dialog: assignOutsideDialog } = useAssignOutsideMember({
    onAfterAssign: () => { if (typeof loadData === "function") loadData(filters); },
  });

  const tasksByProject = useMemo(() => {
    const m = {};
    (data?.tasks || []).forEach((t) => {
      if (!m[t.project_id]) m[t.project_id] = [];
      m[t.project_id].push(t);
    });
    return m;
  }, [data]);

  // R159h · Feb 2026 — Overdue reminder chip. Counts overdue tasks
  // (due < today, status != done) across every visible project and
  // exposes a helper to scroll to the most-overdue project card. We
  // memoize both the count and the target project so the chip renders
  // in O(1) after data loads.
  // R159j · Feb 2026 — Jump Loop. Now returns the FULL ordered list of
  // overdue project ids (worst-offender first) so successive Jump-chip
  // clicks walk the user through every overdue card one-by-one.
  const overdueSummary = useMemo(() => {
    if (!data?.projects || !tasksByProject) return { count: 0, projectId: null, orderedIds: [] };
    const today = new Date();
    const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const todayStr = ymd(today);
    let count = 0;
    // Track {projectId, oldestDue} — sort by oldest overdue asc.
    const perProject = new Map();
    (data.projects || []).forEach((p) => {
      if (snoozedIds.has(p.id)) return;
      const layout = savedLayout[p.id] || { task_filter: "all_open" };
      const rows = filterTasks(tasksByProject[p.id] || [], layout.task_filter, user?.id);
      rows.forEach((t) => {
        if (t.status === "done" || !t.due_date) return;
        const d = String(t.due_date).slice(0, 10);
        if (d < todayStr) {
          count += 1;
          const cur = perProject.get(p.id);
          if (!cur || d < cur) perProject.set(p.id, d);
        }
      });
    });
    const orderedIds = [...perProject.entries()]
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([pid]) => pid);
    return { count, projectId: orderedIds[0] || null, orderedIds };
  }, [data, tasksByProject, savedLayout, snoozedIds, user?.id]);

  // R159j · Feb 2026 — Cursor index for the Jump Loop. Advances on each
  // click and wraps around at the end so the user can keep tapping to
  // walk through every overdue card without reaching for the chip bar
  // again. Resets whenever the ordered list changes (e.g. after a task
  // status flip or a filter change).
  const jumpCursorRef = useRef(0);
  useEffect(() => { jumpCursorRef.current = 0; }, [overdueSummary.orderedIds.join("|")]);

  // R159v · Feb 2026 — Track the CURRENTLY landed overdue index (last card
  // the user was scrolled to via the Floating Jump Navigator). `null`
  // means the user hasn't jumped yet this session. This lets the floating
  // navigator show a proper "3 of 8" counter and highlight First/Last.
  const [currentJumpIdx, setCurrentJumpIdx] = useState(null);
  const [navigatorDismissed, setNavigatorDismissed] = useState(false);
  // Re-arm the navigator whenever the ordered list changes (task flip,
  // filter change) — so a user who dismissed it and then generates fresh
  // overdue work sees it again.
  useEffect(() => {
    setNavigatorDismissed(false);
    setCurrentJumpIdx(null);
  }, [overdueSummary.orderedIds.join("|")]);

  // Shared scroll-to-project helper used by both the header Jump button
  // and the floating navigator. Ensures the Overdue chip stays on so the
  // landed card actually renders its overdue rows.
  const scrollToOverdueIndex = useCallback((idx) => {
    const list = overdueSummary.orderedIds || [];
    if (list.length === 0) return;
    const safe = ((idx % list.length) + list.length) % list.length;
    const pid = list[safe];
    setCurrentJumpIdx(safe);
    setQuickDueFilter("overdue");
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="planning-card-${pid}"]`);
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
      }
      setFlashProjectId(pid);
      setTimeout(() => setFlashProjectId(null), 2200);
    }, 120);
  }, [overdueSummary.orderedIds]);

  const jumpToMostOverdue = useCallback(() => {
    const list = overdueSummary.orderedIds || [];
    if (list.length === 0) {
      toast.success("You're all caught up — no overdue tasks 🎉");
      return;
    }
    const idx = jumpCursorRef.current % list.length;
    jumpCursorRef.current = (idx + 1) % list.length;
    scrollToOverdueIndex(idx);
    if (list.length > 1) {
      toast.success(`Overdue ${idx + 1} of ${list.length}${idx === list.length - 1 ? " · click Jump again to loop" : " · click Jump again for next"}`);
    }
  }, [overdueSummary.orderedIds, scrollToOverdueIndex]);


  // Auto-position new cards on a grid so unpositioned projects don't overlap.
  const arrangeGrid = () => {
    if (!data?.projects) return;
    const cardW = DEFAULT_CARD_W + 20;
    const cardH = DEFAULT_CARD_H + 20;
    const cols = Math.max(1, Math.floor((window.innerWidth - 300) / cardW));
    const next = { ...savedLayout };
    (data.projects || []).forEach((p, i) => {
      if (!next[p.id]) {
        next[p.id] = {
          x: (i % cols) * cardW + 20,
          y: Math.floor(i / cols) * cardH + 20,
          w: DEFAULT_CARD_W,
          h: DEFAULT_CARD_H,
          collapsed: false,
          // Iter · Feb 2026 — Default per-card task filter is now
          // "All active tasks" (every non-done, non-cancelled task,
          // regardless of due date). Users asked to see everything
          // sorted by due date instead of the old "Open · by due date"
          // which hid tasks without a due date.
          task_filter: "all_open",
        };
      }
    });
    setSavedLayout(next);
  };

  const freeLayoutSnapshot = useRef(null);

  // R158b · Re-arrange every card into horizontal swim-lanes grouped by
  // the chosen dimension. Lanes are stacked vertically; projects flow
  // left→right inside each lane. Called when the user picks a swimBy
  // value or toggles back to none. Preserves a snapshot of the user's
  // manual positions before entering a swimlane so switching back to
  // "Free" restores the exact layout they had.
  const arrangeSwimlanes = (dim, colsPerLane = null) => {
    if (!data?.projects) return;
    // R159o · Feb 2026 — Use configured default card size (falls back
    // to constants).
    const defW = defaultCardSize?.w || DEFAULT_CARD_W;
    const defH = defaultCardSize?.h || DEFAULT_CARD_H;
    const cardW = defW + 20;
    const cardH = defH + 20;
    if (dim === "none") {
      if (freeLayoutSnapshot.current) {
        setSavedLayout(freeLayoutSnapshot.current);
        return;
      }
      // No snapshot → produce a fresh clean grid.
      const cols = Math.max(1, Math.floor((window.innerWidth - 300) / cardW));
      const next = { ...savedLayout };
      (data.projects || []).forEach((p, i) => {
        next[p.id] = {
          ...(next[p.id] || {}),
          x: (i % cols) * cardW + 20,
          y: Math.floor(i / cols) * cardH + 20,
        };
      });
      setSavedLayout(next);
      return;
    }
    // Snapshot the current (presumably free) layout before we overwrite it.
    if (swimBy === "none") {
      freeLayoutSnapshot.current = { ...savedLayout };
    }
    // Group projects
    const groups = new Map();
    (data.projects || []).forEach((p) => {
      let key = "—";
      if (dim === "client") key = p.client_name || "No client";
      else if (dim === "brand") key = p.brand_name || "No brand";
      else if (dim === "department") key = p.department_name || "No department";
      else if (dim === "pm") key = p.pm_name || p.owner_name || "Unassigned PM";
      else if (dim === "health") key = p.health || "no health";
      else if (dim === "status") key = p.status || "no status";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(p);
    });
    // Sort keys deterministically — respect custom lane order first,
    // then any unlisted keys fall to the end alphabetically. R158p.
    const rawKeys = [...groups.keys()];
    const custom = (laneOrders && laneOrders[dim]) || [];
    const listed = custom.filter((k) => groups.has(k));
    const rest = rawKeys.filter((k) => !listed.includes(k)).sort((a, b) => a.localeCompare(b));
    const orderedKeys = [...listed, ...rest];
    const LANE_LABEL_H = 40;
    // R159p · Feb 2026 — When the user asks for N cols per lane via
    // Tidy Up, wrap each lane into a mini-grid instead of a single
    // horizontal strip.
    const perRow = Math.max(1, Number(colsPerLane) || 0);
    // R159q · Feb 2026 — Auto-widen lane cards when perRow is small
    // (1-3). Same rule as flat Tidy Up: distribute canvas width evenly
    // so a 2-per-lane group on a 27" monitor stretches to ~800 px cards
    // instead of leaving huge gaps on the right. Only kicks in when the
    // user picked 1-3 cards per lane; 4+ keeps the compact default.
    const canvasW = canvasRef.current?.clientWidth || Math.max(0, window.innerWidth - 300);
    const gap = 20;
    const cellPad = 20;
    const useWide = perRow >= 1 && perRow <= 3;
    const laneCardW = useWide
      ? Math.max(defW, Math.floor((canvasW - cellPad * 2 - gap * (perRow - 1)) / perRow))
      : defW;
    const laneCardStrideX = laneCardW + gap;
    let yOffset = 20;
    const next = { ...savedLayout };
    for (const key of orderedKeys) {
      const projs = groups.get(key);
      projs.forEach((p, i) => {
        const col = perRow > 1 ? (i % perRow) : i;
        const row = perRow > 1 ? Math.floor(i / perRow) : 0;
        next[p.id] = {
          ...(next[p.id] || { w: laneCardW, h: defH, collapsed: false, task_filter: "all_open" }),
          w: laneCardW,
          x: cellPad + col * laneCardStrideX,
          y: yOffset + LANE_LABEL_H + row * cardH,
        };
      });
      const laneRows = perRow > 1 ? Math.ceil(projs.length / perRow) : 1;
      yOffset += LANE_LABEL_H + laneRows * cardH;
    }
    setSavedLayout(next);
  };

  // R158h · Feb 2026 — Tidy up: re-flow every card into a clean grid
  // sorted by the user-chosen dimension. Distinct from swimlanes because
  // it produces ONE grid (no lane labels) with a user-picked column count.
  const tidyUpArrange = (sortBy, cols, groupBy = false) => {
    if (!data?.projects) return;
    // R159e · Feb 2026 — Remember the last tidy args so we can re-apply
    // the same arrangement after a card is snoozed.
    lastTidyRef.current = { sortBy, cols, groupBy };
    // R158n · Feb 2026 — When "Group into swimlanes" is checked, reuse
    // the existing arrangeSwimlanes helper with the same sort dimension.
    // The sort dimension map matches: client/brand/name/pm/health.
    // R159p · Feb 2026 — Pass user-picked `cols` so lanes lay cards out
    // in N-per-row instead of a single strip.
    if (groupBy) {
      const swimDim = ({ client: "client", brand: "brand", name: "none", pm: "pm", health: "health", status: "status" }[sortBy]) || "client";
      setSwimBy(swimDim);
      arrangeSwimlanes(swimDim, cols);
      toast.success(`Grouped ${data.projects.length} cards into swimlanes by ${sortBy}${cols > 1 ? ` · ${cols}/row` : ""}`);
      return;
    }
    // R159o · Feb 2026 — Auto-distribute card width when the user asks
    // for few columns (1-3). Wider monitors + a "3 across" tidy should
    // yield cards ~500px each, not stay stuck at DEFAULT_CARD_W. The
    // canvasRef.current.clientWidth gives us the real usable width.
    // Fallback to configured default when cols is large or the canvas
    // isn't measurable yet.
    const canvasW = canvasRef.current?.clientWidth || Math.max(0, window.innerWidth - 300);
    const gap = 20;
    const cellPad = 20; // outer margin
    const defW = defaultCardSize.w || DEFAULT_CARD_W;
    const defH = defaultCardSize.h || DEFAULT_CARD_H;
    const requestedCols = Math.max(1, cols || Math.floor((canvasW - cellPad * 2) / (defW + gap)));
    // Auto-widen only when 1-3 cols so grids of 4+ stay compact.
    const useWideCards = requestedCols <= 3;
    const cardTargetW = useWideCards
      ? Math.max(defW, Math.floor((canvasW - cellPad * 2 - gap * (requestedCols - 1)) / requestedCols))
      : defW;
    const cardW = cardTargetW + gap;
    const cardH = defH + gap;
    const nCols = requestedCols;
    const key = (p) => {
      switch (sortBy) {
        case "client": return (p.client_name || "zzz").toLowerCase();
        case "brand": return (p.brand_name || "zzz").toLowerCase();
        case "name": return (p.name || "zzz").toLowerCase();
        case "pm": return (p.pm_name || p.owner_name || "zzz").toLowerCase();
        case "status": {
          // Group active projects first, then paused/on-hold, then done/archived.
          const order = { active: 0, in_progress: 1, planned: 2, at_risk: 3, blocked: 4, hold: 5, paused: 6, awaiting_work: 7, idle: 8, inactive: 9, done: 10, archived: 11 };
          return `${String(order[p.status] ?? 99).padStart(2, "0")}-${(p.name || "").toLowerCase()}`;
        }
        case "health": {
          const order = { at_risk: 0, blocked: 1, off_track: 2, on_track: 3, new: 4 };
          return `${order[p.health] ?? 9}-${(p.name || "").toLowerCase()}`;
        }
        default: return (p.name || "").toLowerCase();
      }
    };
    // R159e · Feb 2026 — Skip snoozed projects so the surviving cards
    // reflow without any gaps.
    const sorted = [...(data.projects || [])]
      .filter((p) => !snoozedIds.has(p.id))
      .sort((a, b) => key(a).localeCompare(key(b)));
    const next = { ...savedLayout };
    sorted.forEach((p, i) => {
      next[p.id] = {
        ...(next[p.id] || { collapsed: false, task_filter: "all_open" }),
        w: cardTargetW,
        h: defH,
        x: (i % nCols) * cardW + cellPad,
        y: Math.floor(i / nCols) * cardH + cellPad,
      };
    });
    setSwimBy("none"); // tidy is a free-form grid, not swimlanes
    setSavedLayout(next);
    toast.success(`Tidied ${sorted.length} cards${useWideCards ? ` · ${cardTargetW}px wide` : ""}`);
  };

  // R159w · Feb 2026 — Compact snooze gaps. Preserves the user's
  // current visual order (sort by existing y,x) and reflows the
  // surviving cards into a fresh grid — no swimlane change, keeps
  // each card's w/h. Called whenever a card is snoozed if there's no
  // prior Tidy-Up preset to fall back on, so the board naturally
  // shrinks instead of leaving holes. Un-snooze re-arranges again so
  // returning cards find a slot too.
  const compactSnoozedGaps = useCallback(() => {
    if (!data?.projects || swimBy !== "none") return;
    const canvasW = canvasRef.current?.clientWidth || Math.max(0, window.innerWidth - 300);
    const defW = defaultCardSize.w || DEFAULT_CARD_W;
    const gap = 20;
    const cardStrideW = defW + gap;
    const cols = Math.max(1, Math.floor((canvasW - 40) / cardStrideW));
    const alive = (data.projects || [])
      .filter((p) => !snoozedIds.has(p.id))
      .map((p) => ({ p, l: savedLayout[p.id] || { x: 24, y: 24, w: defW, h: defaultCardSize.h || DEFAULT_CARD_H } }))
      .sort((a, b) => {
        // primary: y, secondary: x — matches visual reading order.
        if (a.l.y !== b.l.y) return a.l.y - b.l.y;
        return a.l.x - b.l.x;
      });
    // Row-height uses the tallest card in each grid row to avoid overlap
    // when cards have different heights.
    const next = { ...savedLayout };
    let rowY = 20;
    let rowMaxH = 0;
    alive.forEach((entry, i) => {
      const col = i % cols;
      if (i > 0 && col === 0) {
        rowY += rowMaxH + gap;
        rowMaxH = 0;
      }
      const cardH = entry.l.h || (defaultCardSize.h || DEFAULT_CARD_H);
      if (cardH > rowMaxH) rowMaxH = cardH;
      next[entry.p.id] = {
        ...(next[entry.p.id] || {}),
        x: col * cardStrideW + 20,
        y: rowY,
      };
    });
    setSavedLayout(next);
  }, [data?.projects, swimBy, snoozedIds, savedLayout, defaultCardSize]);


  // R159e · Feb 2026 — Snooze a project card. Adds the id to the
  // snoozed set (persisted), then re-runs the last tidy-up (if any) so
  // the remaining cards flow up to fill the gap. R159w · Feb 2026 —
  // If the user never Tidied, fall back to `compactSnoozedGaps` so the
  // board still shrinks and doesn't leave a hole where the card was.
  const snoozeProject = useCallback((projectId, projectName) => {
    setSnoozedIds((prev) => {
      const next = new Set(prev);
      next.add(projectId);
      return next;
    });
    // Compaction now runs from a useEffect on [snoozedIds] (see below) so
    // the reflow always sees the freshly-updated set. No setTimeout hack.
    toast.success(`Snoozed “${projectName}”. Change any filter to unsnooze.`, {
      action: {
        label: "Undo",
        onClick: () => setSnoozedIds((prev) => {
          const next = new Set(prev);
          next.delete(projectId);
          return next;
        }),
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const unsnoozeAll = useCallback(() => {
    if (snoozedIds.size === 0) return;
    const n = snoozedIds.size;
    setSnoozedIds(new Set());
    // Compaction runs from the useEffect on [snoozedIds] below.
    toast.success(`Un-snoozed ${n} card${n === 1 ? "" : "s"}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snoozedIds, data]);

  // R159w · Feb 2026 — Fix testing-agent finding: run compaction as a
  // side-effect of the snoozedIds state change so we always see the
  // committed set (previous setTimeout hack captured a stale closure).
  const prevSnoozeCountRef = useRef(0);
  useEffect(() => {
    // Skip the very first mount (nothing to compact).
    const prev = prevSnoozeCountRef.current;
    prevSnoozeCountRef.current = snoozedIds.size;
    if (prev === snoozedIds.size) return;
    if (!data?.projects?.length) return;
    if (lastTidyRef.current) {
      const { sortBy, cols, groupBy } = lastTidyRef.current;
      tidyUpArrange(sortBy, cols, groupBy);
    } else if (swimBy === "none") {
      compactSnoozedGaps();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snoozedIds]);
  const moveTaskToProject = async (taskId, newProjectId) => {
    // Optimistic UI: update the local caches so the task jumps to the
    // target card immediately, without waiting for the server round-trip
    // and full reload (heavy on 176-project workspaces).
    const targetProject = (data?.projects || []).find((p) => p.id === newProjectId);
    setData((prev) => prev ? {
      ...prev,
      tasks: prev.tasks.map((t) => t.id === taskId ? {
        ...t,
        project_id: newProjectId,
        project_name: targetProject?.name || t.project_name,
      } : t),
    } : prev);
    try {
      await api.patch(`/tasks/${taskId}`, { project_id: newProjectId });
      toast.success("Task moved");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Move failed"));
      // Roll back — refetch to get authoritative state.
      loadData(filters);
    }
  };

  // R158d · Feb 2026 — Presentation resume: remember the last-viewed
  // project idx per board+user so re-opening Present picks up where the
  // user left off. Storage key changes with board id (or "ephemeral").
  const presentKey = useCallback(() => {
    const bid = board?.id || "ephemeral";
    return `acq-plan-present-${activeWorkspaceId || "-"}-${user?.id || "-"}-${bid}`;
  }, [board?.id, activeWorkspaceId, user?.id]);

  const startPresent = useCallback(() => {
    let start = 0;
    try {
      const raw = window.localStorage.getItem(presentKey());
      const parsed = raw ? parseInt(raw, 10) : 0;
      const projs = data?.projects || [];
      if (!Number.isNaN(parsed) && parsed >= 0 && parsed < projs.length) {
        start = parsed;
      }
    } catch { /* private mode */ }
    setPresentIdx(start);
  }, [presentKey, data?.projects]);

  // Persist on every idx change while presenting.
  useEffect(() => {
    if (presentIdx === null) return;
    try { window.localStorage.setItem(presentKey(), String(presentIdx)); } catch { /* ignore */ }
  }, [presentIdx, presentKey]);

  useEffect(() => {
    // Seed positions for ANY project that doesn't yet have a layout
    // entry on every data load (previous version only ran when the
    // project count changed which missed new projects added between
    // loads).
    if (data?.projects?.length) {
      const missing = data.projects.some((p) => !savedLayout[p.id]);
      if (missing) arrangeGrid();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.projects]);

  const updateCard = (pid, patch) => {
    setSavedLayout((prev) => ({
      ...prev,
      [pid]: { ...(prev[pid] || {}), ...patch },
    }));
  };

  // ══════════════ Board CRUD actions ═══════════════
  const createBoard = async (name, isShared, startBlank = false) => {
    try {
      // R158j · Feb 2026 — Blank view: create a board with no layout /
      // filters / sticky notes so users can build from scratch. Any
      // change to filters or layout auto-saves via the existing
      // debounced writer (see the `persist` effect below).
      const payload = startBlank
        ? { name, is_shared: isShared, layout: {}, sticky_notes: [], filters: {}, zoom: "md", swim_by: "none" }
        : { name, is_shared: isShared, layout: savedLayout, sticky_notes: stickyNotes, filters, zoom, swim_by: swimBy };
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/planning-boards`, payload);
      const rows = await loadBoards();
      const created = rows.find((b) => b.id === data.id) || data;
      // If starting blank, reset local state so the auto-save writer
      // doesn't immediately push the user's previous filters back.
      if (startBlank) {
        setSavedLayout({});
        setStickyNotes([]);
        setFilters({});
        setZoom("md");
        setSwimBy("none");
      }
      selectBoard(created);
      toast.success(startBlank ? `Blank view "${name}" ready — pick filters and drag cards to build it up.` : `Saved as "${name}"`);
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
  };

  const duplicateBoard = async () => {
    if (!board) return;
    createBoard(`${board.name} copy`, false, false);
  };

  const deleteBoard = async () => {
    if (!board?.id) return;
    if (!window.confirm(`Delete "${board.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/workspaces/${activeWorkspaceId}/planning-boards/${board.id}`);
      const rows = await loadBoards();
      toast.success("Board deleted");
      if (rows[0]) selectBoard(rows[0]);
      else { setBoard(null); setSavedLayout({}); setStickyNotes([]); setFilters({}); }
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  const toggleSharing = async () => {
    if (!board?.id) return;
    try {
      const { data } = await api.patch(`/workspaces/${activeWorkspaceId}/planning-boards/${board.id}`, {
        is_shared: !board.is_shared,
      });
      setBoard(data);
      loadBoards();
      toast.success(data.is_shared ? "Board is now shared" : "Board is now private");
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
  };

  const addSticky = () => {
    setStickyNotes((prev) => [
      ...prev,
      { id: crypto.randomUUID(), x: 24, y: 24, w: 220, h: 160, color: "amber", text: "" },
    ]);
  };

  // ══════════════ Render ═══════════════

  if (!activeWorkspaceId) return null;
  const options = data?.options || { users: [], clients: [], brands: [], all_projects: [], project_tags: [], statuses: [], healths: [], priorities: [], task_statuses: [] };
  const zoomLevel = ZOOM[zoom] || 1;

  return (
    <>
      <PageHeader
        eyebrow="Delivery"
        title="Planning board"
        description="Master project + task view. Drag, resize, filter and save layouts."
        testid="planning-header"
        actions={
          <div className="flex items-center gap-1 justify-end min-w-0" data-testid="planning-primary-bar">
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground shrink-0" data-testid="planning-saving" title="Saving…" />}
            <BoardPicker
              boards={boards}
              board={board}
              onSelect={selectBoard}
              onNew={() => setNewBoardOpen(true)}
            />
            {board && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="ghost" className="rounded-none h-8 w-8 p-0 shrink-0" title="Board options" data-testid="planning-board-menu">
                    <MoreHorizontal className="w-3.5 h-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="rounded-none">
                  <DropdownMenuItem onClick={toggleSharing} data-testid="planning-share-toggle">
                    <Users className="w-3.5 h-3.5 mr-2" /> {board.is_shared ? "Make private" : "Share with workspace"}
                  </DropdownMenuItem>
                  {board.owner_id === user?.id && (
                    <>
                      <DropdownMenuItem onClick={duplicateBoard} data-testid="planning-duplicate">
                        <Copy className="w-3.5 h-3.5 mr-2" /> Duplicate board
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={deleteBoard} className="text-rose-600 focus:text-rose-700" data-testid="planning-delete">
                        <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete board
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <div className="border-l border-border h-6 mx-1 hidden sm:block" />
            <Button
              size="sm"
              variant={viewBarOpen ? "secondary" : "ghost"}
              className="rounded-none h-8 text-xs shrink-0"
              onClick={() => setViewBarOpen((v) => !v)}
              title="Show / hide View options"
              data-testid="planning-view-options-toggle"
              aria-expanded={viewBarOpen}
              aria-controls="planning-secondary-bar"
            >
              <SlidersHorizontal className="w-3.5 h-3.5 sm:mr-1" />
              <span className="hidden sm:inline">View</span>
              {viewBarOpen ? <ChevronUp className="w-3 h-3 ml-1 hidden sm:inline" /> : <ChevronDown className="w-3 h-3 ml-1 hidden sm:inline" />}
            </Button>
            <Button size="sm" variant="outline" className="rounded-none h-8 text-xs shrink-0" onClick={startPresent} disabled={!(data?.projects || []).length} data-testid="planning-present">
              <Play className="w-3.5 h-3.5 sm:mr-1" /> <span className="hidden sm:inline">Present</span>
            </Button>
          </div>
        }
      />

      {/* R158u · Feb 2026 — Secondary "View options" toolbar. Slides open
          from the primary bar. Horizontally scrollable on mobile so no
          button gets clipped.
          R159v · Feb 2026 — Wrapped together with FiltersBar in a
          `sticky top-0` container so both stay pinned to the top of the
          scroll viewport (Jump, quick chips, unbilled pill etc. remain
          reachable while the user scrolls through cards). */}
      <div className="sticky top-0 z-40 bg-background/95 supports-[backdrop-filter]:backdrop-blur border-b border-border" data-testid="planning-sticky-header">
      {/* R159ff · Feb 2026 — Mobile-only quick-action strip. Four tap
          targets (View / Filter / Group / Bulk) that either expand the
          secondary bar or trigger the most common action for that
          category. Keeps the phone workflow one-tap instead of forcing
          users to scroll through the wrap-row toolbar. Hidden on md+
          where the full toolbar has enough room. */}
      <div className="md:hidden flex items-stretch border-b border-border bg-neutral-50" data-testid="planning-mobile-quick-tabs">
        {/* R159xx · Feb 2026 — Same red-dot cue on mobile tabs. */}
        {(() => {
          const _f = filters || {};
          const filterActive = !!(_f.search || _f.pm_ids?.length || _f.statuses?.length || _f.healths?.length || _f.client_ids?.length || _f.brand_ids?.length || _f.department_ids?.length || _f.project_ids?.length || _f.priorities?.length || (_f.effort_status && _f.effort_status !== "any") || unbilledFilter);
          const viewActive = quickDueFilter && quickDueFilter !== "all";
          // Group active dot lives directly on the label below via `swimBy !== "none"`.
          return (
            <>
              <button
                type="button"
                onClick={() => setMobileSheet("view")}
                className={`flex-1 h-9 flex items-center justify-center gap-1 text-[10px] uppercase tracking-widest border-r border-border ${mobileSheet === "view" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted"}`}
                data-testid="planning-mobile-tab-view"
              >
                <SlidersHorizontal className="w-3.5 h-3.5" /> View
                {viewActive && <span className="w-1.5 h-1.5 rounded-full bg-rose-500 shadow-[0_0_0_1.5px_rgba(255,255,255,1)] ml-0.5" data-testid="planning-mobile-tab-view-active-dot" />}
              </button>
              <button
                type="button"
                onClick={() => setMobileSheet("filter")}
                className={`flex-1 h-9 flex items-center justify-center gap-1 text-[10px] uppercase tracking-widest border-r border-border ${mobileSheet === "filter" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted"}`}
                title={filterActive ? "Filters are active" : "Open filters"}
                data-testid="planning-mobile-tab-filter"
              >
                <Filter className="w-3.5 h-3.5" /> Filter
                {filterActive && <span className="w-1.5 h-1.5 rounded-full bg-rose-500 shadow-[0_0_0_1.5px_rgba(255,255,255,1)] ml-0.5" data-testid="planning-mobile-tab-filter-active-dot" />}
              </button>
            </>
          );
        })()}
        <label
          className={`flex-1 h-9 relative flex items-center justify-center gap-1 text-[10px] uppercase tracking-widest border-r border-border ${swimBy === "none" ? "bg-transparent text-muted-foreground" : "bg-primary/10 text-primary font-semibold"}`}
          title="Group projects into lanes"
        >
          <Rows className="w-3.5 h-3.5" /> {swimBy === "none" ? "Group" : `Group · ${swimBy}`}
          {swimBy && swimBy !== "none" && <span className="w-1.5 h-1.5 rounded-full bg-rose-500 shadow-[0_0_0_1.5px_rgba(255,255,255,1)] ml-0.5" data-testid="planning-mobile-tab-group-active-dot" />}
          <select
            value={swimBy}
            onChange={(e) => { setSwimBy(e.target.value); arrangeSwimlanes(e.target.value); }}
            className="absolute inset-0 opacity-0 appearance-none cursor-pointer"
            data-testid="planning-mobile-tab-group"
          >
            <option value="none">Group</option>
            <option value="client">Group · Client</option>
            <option value="brand">Group · Brand</option>
            <option value="department">Group · Department</option>
            <option value="pm">Group · PM</option>
            <option value="health">Group · Health</option>
            <option value="status">Group · Status</option>
          </select>
        </label>
        {/* Iter · Feb 2026 — Mobile access to the Team/Projects mode
            toggle. The desktop ribbon that hosts this toggle is hidden
            below md, so users on phones need a first-class button here. */}
        <button
          type="button"
          onClick={() => setBoardMode(boardMode === "team" ? "projects" : "team")}
          className={`flex-1 h-9 flex items-center justify-center gap-1 text-[10px] uppercase tracking-widest border-r border-border ${boardMode === "team" ? "bg-primary/10 text-primary font-semibold" : "text-muted-foreground hover:bg-muted"}`}
          title={boardMode === "team" ? "Switch to Projects cards" : "Switch to Team cards"}
          data-testid="planning-mobile-tab-mode"
        >
          <Users className="w-3.5 h-3.5" /> {boardMode === "team" ? "Team" : "Projects"}
        </button>
        {canAccountsReview && (
          <button
            type="button"
            onClick={() => setMobileSheet("bulk")}
            className={`flex-1 h-9 flex items-center justify-center gap-1 text-[10px] uppercase tracking-widest ${mobileSheet === "bulk" ? "bg-amber-500 text-white" : (accountsReviewOn ? "bg-amber-500 text-white" : "text-muted-foreground hover:bg-muted")}`}
            title="Open accounts / bulk actions sheet"
            data-testid="planning-mobile-tab-bulk"
          >
            <Wallet className="w-3.5 h-3.5" /> Accounts
          </button>
        )}
      </div>
      {viewBarOpen && (
        <div
          id="planning-secondary-bar"
          className="hidden md:block border-b border-border bg-neutral-50/60"
          data-testid="planning-secondary-bar"
        >
          {/* R159gg · Feb 2026 — MS-Office style ribbon tab strip.
              Filters the wrap-row below to show only the panels
              relevant to the selected tab. The tab strip itself is
              always visible so switching tabs is one click. Accounts
              tab is permission-gated. */}
          <div className="hidden md:flex items-stretch border-b border-border bg-white" data-testid="planning-ribbon-tabs">
            {/* R159xx · Feb 2026 — Red dot on Filter / Group / View tabs
                whenever a chip / filter is currently narrowing the board
                so it's obvious even when the tab isn't focused. */}
            {(() => {
              const _f = filters || {};
              const filterActive = !!(_f.search || _f.pm_ids?.length || _f.statuses?.length || _f.healths?.length || _f.client_ids?.length || _f.brand_ids?.length || _f.department_ids?.length || _f.project_ids?.length || _f.priorities?.length || (_f.effort_status && _f.effort_status !== "any") || unbilledFilter);
              const groupActive = swimBy && swimBy !== "none";
              const viewActive = quickDueFilter && quickDueFilter !== "all";
              const dot = (
                <span
                  className="w-1.5 h-1.5 rounded-full bg-rose-500 shadow-[0_0_0_2px_rgba(255,255,255,1)] ml-0.5"
                  data-testid="planning-ribbon-tab-active-dot"
                  aria-label="Active filter"
                />
              );
              return [
                { key: "home", label: "Home", icon: LayoutGrid, hot: false },
                { key: "view", label: "View", icon: Eye, hot: viewActive },
                { key: "filter", label: "Filter", icon: Filter, hot: filterActive },
                { key: "group", label: "Group", icon: Rows, hot: groupActive },
                ...(canAccountsReview || canFinanceEdit ? [{ key: "accounts", label: "Accounts", icon: Wallet, hot: false }] : []),
                { key: "myboard", label: "My Board", icon: Star, star: true, hot: false },
              ].map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setRibbonTab(t.key)}
                  className={`h-8 px-3 text-[11px] uppercase tracking-widest border-b-2 -mb-px transition-colors inline-flex items-center gap-1.5 ${ribbonTab === t.key ? "border-primary text-primary font-semibold bg-primary/5" : "border-transparent text-muted-foreground hover:text-foreground hover:bg-muted"}`}
                  data-testid={`planning-ribbon-tab-${t.key}`}
                  title={t.star ? "Show only the ribbon panels you pinned" : (t.hot ? `${t.label} — filter is currently active. Click to review.` : undefined)}
                >
                  {t.icon && <t.icon className="w-3 h-3" />} {t.label}
                  {t.hot && dot}
                  {t.star && myBoardPins.size > 0 && (
                    <span className="text-[9px] text-primary opacity-80">· {myBoardPins.size}</span>
                  )}
                </button>
              ));
            })()}
          </div>
          <div className="flex items-start gap-0 px-4 py-1.5 flex-wrap" data-testid="planning-toolbar-row">
            {/* R159ii · Feb 2026 — Empty state for the "My Board" tab
                when the user hasn't pinned anything yet. Points them
                at the pin button that appears on every other panel on
                hover. */}
            {ribbonTab === "myboard" && myBoardPins.size === 0 && (
              <div className="flex flex-col items-center gap-1 pt-1 px-3" data-testid="planning-myboard-empty">
                <div className="flex items-center gap-2 h-8">
                  <Star className="w-4 h-4 text-amber-500" />
                  <span className="text-[11px] text-muted-foreground">
                    No panels pinned yet · switch to any tab, hover a panel and click the pin icon to add it here.
                  </span>
                </div>
                <div className="flex items-center gap-0.5 text-[9px] uppercase tracking-widest text-muted-foreground">
                  <Star className="w-2.5 h-2.5" /> Pin panels
                </div>
              </div>
            )}
            {/* R159hh · Feb 2026 — Ribbon panels: each panel is a small
                vertical box (controls on top, a tiny label + icon
                underneath) mimicking the Word / Excel ribbon. Groups
                are toggled by tab via display:flex vs display:none.
                On mobile the entire ribbon is hidden — the
                planning-mobile-quick-tabs strip handles that surface
                (see R159ii). */}
            <div
              style={{ display: showPanel("layout", ["home", "group"]) ? "flex" : "none", order: panelOrder("layout") }}
              className={`flex-col items-center gap-1 border-r border-border pr-2 mr-1 pt-0.5 group ${ribbonTab === "myboard" ? "cursor-grab" : ""}`}
              draggable={ribbonTab === "myboard"}
              onDragStart={onPanelDragStart("layout")}
              onDragOver={onPanelDragOver}
              onDrop={onPanelDrop("layout")}
              data-ribbon-panel="layout">
              <div className="flex items-center gap-1">
                <SwimlaneToggle value={swimBy} onChange={(v) => { setSwimBy(v); arrangeSwimlanes(v); }} />
              </div>
              <div className="flex items-center gap-0.5 text-[9px] uppercase tracking-widest text-muted-foreground">
                <Rows className="w-2.5 h-2.5" /> Layout
                <button
                  type="button"
                  onClick={() => togglePin("layout")}
                  className={`ml-1 opacity-0 group-hover:opacity-100 hover:text-primary transition ${myBoardPins.has("layout") ? "opacity-100 text-primary" : ""}`}
                  title={myBoardPins.has("layout") ? "Unpin from My Board" : "Pin to My Board"}
                  data-testid="planning-pin-layout"
                >
                  <Star className="w-2.5 h-2.5" fill={myBoardPins.has("layout") ? "currentColor" : "none"} />
                </button>
              </div>
            </div>
            <div
              style={{ display: showPanel("zoom", ["home"]) ? "flex" : "none", order: panelOrder("zoom") }}
              className={`flex-col items-center gap-1 border-r border-border pr-2 mr-1 pt-0.5 group ${ribbonTab === "myboard" ? "cursor-grab" : ""}`}
              draggable={ribbonTab === "myboard"}
              onDragStart={onPanelDragStart("zoom")}
              onDragOver={onPanelDragOver}
              onDrop={onPanelDrop("zoom")}
              data-ribbon-panel="zoom">
              <div className="flex items-center gap-1">
                <ZoomToggle zoom={zoom} onChange={setZoom} />
              </div>
              <div className="flex items-center gap-0.5 text-[9px] uppercase tracking-widest text-muted-foreground">
                <ZoomIn className="w-2.5 h-2.5" /> Zoom
                <button
                  type="button"
                  onClick={() => togglePin("zoom")}
                  className={`ml-1 opacity-0 group-hover:opacity-100 hover:text-primary transition ${myBoardPins.has("zoom") ? "opacity-100 text-primary" : ""}`}
                  title={myBoardPins.has("zoom") ? "Unpin from My Board" : "Pin to My Board"}
                  data-testid="planning-pin-zoom"
                >
                  <Star className="w-2.5 h-2.5" fill={myBoardPins.has("zoom") ? "currentColor" : "none"} />
                </button>
              </div>
            </div>
            <div
              style={{ display: showPanel("arrange", ["group"]) ? "flex" : "none", order: panelOrder("arrange") }}
              className={`flex-col items-center gap-1 border-r border-border pr-2 mr-1 pt-0.5 group ${ribbonTab === "myboard" ? "cursor-grab" : ""}`}
              draggable={ribbonTab === "myboard"}
              onDragStart={onPanelDragStart("arrange")}
              onDragOver={onPanelDragOver}
              onDrop={onPanelDrop("arrange")}
              data-ribbon-panel="arrange">
              <div className="flex items-center gap-1">
                <TidyUpButton onApply={tidyUpArrange} />
              </div>
              <div className="flex items-center gap-0.5 text-[9px] uppercase tracking-widest text-muted-foreground">
                <Wand2 className="w-2.5 h-2.5" /> Arrange
                <button
                  type="button"
                  onClick={() => togglePin("arrange")}
                  className={`ml-1 opacity-0 group-hover:opacity-100 hover:text-primary transition ${myBoardPins.has("arrange") ? "opacity-100 text-primary" : ""}`}
                  title={myBoardPins.has("arrange") ? "Unpin from My Board" : "Pin to My Board"}
                  data-testid="planning-pin-arrange"
                >
                  <Star className="w-2.5 h-2.5" fill={myBoardPins.has("arrange") ? "currentColor" : "none"} />
                </button>
              </div>
            </div>
            {/* R159gg · Feb 2026 — View tab: bulk collapse + display
                filters. */}
            <div
              style={{ display: showPanel("display", ["view"]) ? "flex" : "none", order: panelOrder("display") }}
              className={`flex-col items-center gap-1 border-r border-border pr-2 mr-1 pt-0.5 group ${ribbonTab === "myboard" ? "cursor-grab" : ""}`}
              draggable={ribbonTab === "myboard"}
              onDragStart={onPanelDragStart("display")}
              onDragOver={onPanelDragOver}
              onDrop={onPanelDrop("display")}
              data-ribbon-panel="display">
              <div className="flex items-center gap-1">
            <Button
              size="sm" variant="ghost" className="rounded-none h-8 text-xs shrink-0"
              onClick={() => setSavedLayout((prev) => {
                const next = { ...prev };
                for (const p of (data?.projects || [])) {
                  next[p.id] = { ...(next[p.id] || {}), collapsed: true };
                }
                return next;
              })}
              title="Collapse every card to just its header"
              data-testid="planning-collapse-all"
            >
              <ChevronUp className="w-3.5 h-3.5 mr-1" /> Collapse all
            </Button>
            <Button
              size="sm" variant="ghost" className="rounded-none h-8 text-xs shrink-0"
              onClick={() => setSavedLayout((prev) => {
                const next = { ...prev };
                for (const p of (data?.projects || [])) {
                  next[p.id] = { ...(next[p.id] || {}), collapsed: false };
                }
                return next;
              })}
              title="Expand every card"
              data-testid="planning-expand-all"
            >
              <ChevronDown className="w-3.5 h-3.5 mr-1" /> Expand all
            </Button>
            {/* R159o · Feb 2026 — Configurable default card dimensions */}
            {isAdmin && (
              <Button
                size="sm" variant="ghost" className="rounded-none h-8 text-xs shrink-0"
                onClick={() => setTemplatesDialogOpen(true)}
                title="Edit workspace-wide status message templates for the Email / WhatsApp shortcuts"
                data-testid="planning-templates-btn"
              >
                <MessageCircle className="w-3.5 h-3.5 mr-1" /> Templates
              </Button>
            )}
            <Button
              size="sm" variant="ghost" className="rounded-none h-8 text-xs shrink-0"
              onClick={() => setShowCardSizeDialog(true)}
              title="Set the default card width & height for Tidy Up and new cards"
              data-testid="planning-card-size-btn"
            >
              <SlidersHorizontal className="w-3.5 h-3.5 mr-1" /> Card size ({defaultCardSize.w}×{defaultCardSize.h})
            </Button>
            {/* Iter · Feb 2026 — Board mode switch. "Projects" is the
                classic free-form canvas; "Team" swaps to per-assignee
                cards with status-update shortcuts. */}
            <div className="inline-flex border border-border rounded-none shrink-0" data-testid="planning-board-mode">
              <button
                type="button"
                onClick={() => setBoardMode("projects")}
                className={`px-2 h-8 text-[10px] uppercase tracking-widest ${boardMode === "projects" ? "bg-foreground text-background" : "text-muted-foreground hover:bg-muted"}`}
                data-testid="planning-board-mode-projects"
                title="Cards grouped by project (default)"
              >
                Projects
              </button>
              <button
                type="button"
                onClick={() => setBoardMode("team")}
                className={`px-2 h-8 text-[10px] uppercase tracking-widest ${boardMode === "team" ? "bg-foreground text-background" : "text-muted-foreground hover:bg-muted"}`}
                data-testid="planning-board-mode-team"
                title="Cards grouped by assignee with status-update shortcuts"
              >
                Team
              </button>
            </div>
            <label
              className={`inline-flex items-center gap-1 px-2 h-8 border rounded-none text-[10px] uppercase tracking-widest cursor-pointer select-none shrink-0 ${hideEmptyCards ? "bg-primary/10 text-primary border-primary/40" : "border-border hover:bg-muted"}`}
              title={boardMode === "team" ? "Hide team cards with no matching tasks" : "Hide project cards that have no tasks matching the current filters"}
              data-testid="planning-hide-empty-toggle"
            >
              <input
                type="checkbox"
                checked={hideEmptyCards}
                onChange={(e) => setHideEmptyCards(e.target.checked)}
                className="w-3 h-3"
              />
              Hide empty
            </label>
            <label
              className={`inline-flex items-center gap-1 px-2 h-8 border rounded-none text-[10px] uppercase tracking-widest cursor-pointer select-none shrink-0 ${onlyWatched ? "bg-primary/10 text-primary border-primary/40" : "border-border hover:bg-muted"}`}
              title="Show only projects you're watching"
              data-testid="planning-only-watched-toggle"
            >
              <input
                type="checkbox"
                checked={onlyWatched}
                onChange={(e) => setOnlyWatched(e.target.checked)}
                className="w-3 h-3"
              />
              <Eye className="w-3 h-3" />
              Only watched{onlyWatched && watchedProjectIds.size > 0 ? ` (${watchedProjectIds.size})` : ""}
            </label>
            {/* Iter · Feb 2026 — Persistent "Manage snoozed" entry so
                the snoozed-cards manager is discoverable EVEN when no
                cards are snoozed yet. The existing chip up in the top
                toolbar renders only when snoozedIds.size > 0; users
                asked for a stable location in the ribbon they can
                muscle-memory. Popover mirrors that chip. */}
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className={`inline-flex items-center gap-1 px-2 h-8 border rounded-none text-[10px] uppercase tracking-widest shrink-0 ${snoozedIds.size > 0 ? "border-primary/40 bg-primary/10 text-primary hover:bg-primary/20" : "border-border text-muted-foreground hover:bg-muted"}`}
                  title="Review & selectively bring back snoozed project cards"
                  data-testid="planning-view-snoozed-manager"
                >
                  <Moon className="w-3 h-3" />
                  {snoozedIds.size > 0 ? `${snoozedIds.size} snoozed · review` : "Snoozed · none"}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-0 rounded-none" align="start">
                <div className="px-3 py-2 border-b border-border flex items-center justify-between bg-muted/40">
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                    Snoozed cards ({snoozedIds.size})
                  </span>
                  {snoozedIds.size > 0 && (
                    <button
                      type="button"
                      onClick={unsnoozeAll}
                      className="text-[10px] uppercase tracking-widest text-primary hover:underline"
                      data-testid="planning-view-snoozed-unsnooze-all"
                    >
                      Unsnooze all
                    </button>
                  )}
                </div>
                {snoozedIds.size === 0 ? (
                  <div className="px-3 py-4 text-xs text-muted-foreground italic">
                    No snoozed cards. Right-click any project card and choose &ldquo;Snooze&rdquo; to temporarily hide it from the board.
                  </div>
                ) : (
                  <ul className="max-h-64 overflow-y-auto divide-y divide-border" data-testid="planning-view-snoozed-list">
                    {Array.from(snoozedIds).map((pid) => {
                      const proj = (data?.projects || []).find((p) => p.id === pid);
                      if (!proj) return null;
                      return (
                        <li key={pid} className="px-3 py-2 flex items-center gap-2 text-xs">
                          <div className="flex-1 min-w-0">
                            <div className="truncate font-medium" title={proj.name}>{proj.name}</div>
                            {proj.client_name && (
                              <div className="text-[10px] text-muted-foreground truncate">{proj.client_name}{proj.brand_name ? ` · ${proj.brand_name}` : ""}</div>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => setSnoozedIds((prev) => {
                              const next = new Set(prev);
                              next.delete(pid);
                              return next;
                            })}
                            className="shrink-0 inline-flex items-center gap-1 px-2 h-6 border border-primary/40 bg-primary/10 text-primary text-[10px] uppercase tracking-widest rounded-none hover:bg-primary/20"
                            title="Un-snooze this card"
                            data-testid={`planning-view-snoozed-unsnooze-${pid}`}
                          >
                            <Undo2 className="w-3 h-3" />
                            Unsnooze
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </PopoverContent>
            </Popover>
              </div>
              <div className="flex items-center gap-0.5 text-[9px] uppercase tracking-widest text-muted-foreground">
                <SlidersHorizontal className="w-2.5 h-2.5" /> Display
                <button
                  type="button"
                  onClick={() => togglePin("display")}
                  className={`ml-1 opacity-0 group-hover:opacity-100 hover:text-primary transition ${myBoardPins.has("display") ? "opacity-100 text-primary" : ""}`}
                  title={myBoardPins.has("display") ? "Unpin from My Board" : "Pin to My Board"}
                  data-testid="planning-pin-display"
                >
                  <Star className="w-2.5 h-2.5" fill={myBoardPins.has("display") ? "currentColor" : "none"} />
                </button>
              </div>
            </div>{/* /R159hh ribbon-panel view */}
            {/* R159gg · Feb 2026 — Filter tab hint. All filter chips
                live in the sticky FiltersBar below the ribbon so they
                stay one tap away regardless of ribbon tab. */}
            <div style={{ display: showPanel("filter-hint", ["filter"]) ? "flex" : "none" }}
                 className="flex-col items-center gap-1 border-r border-border pr-2 mr-1 pt-0.5 group">
              <div className="flex items-center gap-1 h-8">
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground italic px-2">
                  Search + filter chips are pinned below · always visible
                </span>
              </div>
              <div className="flex items-center gap-0.5 text-[9px] uppercase tracking-widest text-muted-foreground">
                <Filter className="w-2.5 h-2.5" /> Filters
              </div>
            </div>
            {/* R159gg · Feb 2026 — Accounts tab wrapper. Only renders
                for users with the permission. The `canAccountsReview`
                block below still handles its own gating for defensive
                purposes; the wrapper here just controls tab visibility. */}
            <div
              style={{ display: showPanel("accounts", ["accounts"]) ? "flex" : "none", order: panelOrder("accounts") }}
              className={`flex-col items-center gap-1 border-r border-border pr-2 mr-1 pt-0.5 group ${ribbonTab === "myboard" ? "cursor-grab" : ""}`}
              draggable={ribbonTab === "myboard"}
              onDragStart={onPanelDragStart("accounts")}
              onDragOver={onPanelDragOver}
              onDrop={onPanelDrop("accounts")}
              data-ribbon-panel="accounts">
              <div className="flex items-center gap-1 flex-wrap">
            {/* R159w · Feb 2026 — Accounts Review Mode. Toggle only
                renders for users who can read billing notes. When on,
                each card shows the last accounts-note strip and a set
                of review-oriented chips ("Needs review", "Sort ▾
                newest note") appear beside it. */}
            {canAccountsReview && (
              <>
                <div className="border-l border-border h-6 mx-1 shrink-0" />
                <label
                  className={`inline-flex items-center gap-1 px-2 h-8 border rounded-none text-[10px] uppercase tracking-widest cursor-pointer select-none shrink-0 ${accountsReviewOn ? "bg-amber-500 text-white border-amber-600" : "border-amber-300 text-amber-700 bg-amber-50 hover:bg-amber-100"}`}
                  title="Show the last Accounts note under every project card so a reviewer can scan the whole board in one pass"
                  data-testid="planning-accounts-review-toggle"
                >
                  <input
                    type="checkbox"
                    checked={accountsReviewOn}
                    onChange={(e) => setAccountsReviewOn(e.target.checked)}
                    className="w-3 h-3"
                  />
                  <span>$</span>
                  Accounts review
                </label>
                {accountsReviewOn && (
                  <>
                    <button
                      type="button"
                      onClick={() => setNeedsReviewOnly((v) => !v)}
                      className={`inline-flex items-center gap-1 px-2 h-8 border rounded-none text-[10px] uppercase tracking-widest shrink-0 ${needsReviewOnly ? "bg-amber-600 text-white border-amber-700" : "border-amber-300 text-amber-700 bg-white hover:bg-amber-50"}`}
                      title="Show only projects whose last accounts note is not yours and hasn't been reviewed"
                      data-testid="planning-needs-review-toggle"
                    >
                      <AlertTriangle className="w-3 h-3" />
                      Needs review
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const notes = accountsSummary;
                        const canvasW = canvasRef.current?.clientWidth || Math.max(0, window.innerWidth - 300);
                        const defW = defaultCardSize.w || DEFAULT_CARD_W;
                        const cols = Math.max(1, Math.floor((canvasW - 40) / (defW + 20)));
                        // Sort projects by last accounts-note desc; cards without
                        // any note go to the bottom.
                        const sorted = [...(data?.projects || [])]
                          .filter((p) => !snoozedIds.has(p.id))
                          .sort((a, b) => {
                            const ta = notes[a.id]?.created_at || "";
                            const tb = notes[b.id]?.created_at || "";
                            if (ta === tb) return (a.name || "").localeCompare(b.name || "");
                            return tb.localeCompare(ta);
                          });
                        const gap = 20;
                        const cardW = defW + gap;
                        const cardH = (defaultCardSize.h || DEFAULT_CARD_H) + gap;
                        const next = { ...savedLayout };
                        sorted.forEach((p, i) => {
                          next[p.id] = {
                            ...(next[p.id] || { collapsed: false, task_filter: "all_open" }),
                            x: (i % cols) * cardW + 20,
                            y: Math.floor(i / cols) * cardH + 20,
                          };
                        });
                        setSwimBy("none");
                        setSavedLayout(next);
                        toast.success(`Sorted ${sorted.length} cards · newest accounts note first`);
                      }}
                      className="inline-flex items-center gap-1 px-2 h-8 border rounded-none text-[10px] uppercase tracking-widest shrink-0 border-amber-300 text-amber-700 bg-white hover:bg-amber-50"
                      title="Reorder the board so cards with the freshest accounts note float to the top-left"
                      data-testid="planning-sort-accounts-note"
                    >
                      <RotateCcw className="w-3 h-3" />
                      Sort · newest note
                    </button>
                    <button
                      type="button"
                      onClick={copyAccountsDigest}
                      className="inline-flex items-center gap-1 px-2 h-8 border rounded-none text-[10px] uppercase tracking-widest shrink-0 border-amber-300 text-amber-700 bg-white hover:bg-amber-50"
                      title="Copy a plain-text digest of every visible card's last accounts note — paste into Slack or email"
                      data-testid="planning-accounts-copy-digest"
                    >
                      <Copy className="w-3 h-3" />
                      Copy digest
                    </button>
                    <button
                      type="button"
                      onClick={downloadAccountsDigestCSV}
                      className="inline-flex items-center gap-1 px-2 h-8 border rounded-none text-[10px] uppercase tracking-widest shrink-0 border-amber-300 text-amber-700 bg-white hover:bg-amber-50"
                      title="Download the same digest as CSV — Excel-friendly for pivot tables"
                      data-testid="planning-accounts-csv-digest"
                    >
                      <Download className="w-3 h-3" />
                      CSV
                    </button>
                    {/* R159y · Feb 2026 — Age filter chip set. Single-
                        select (fresh · warm · stale · all). */}
                    <div className="inline-flex border border-amber-300 shrink-0 divide-x divide-amber-300" data-testid="planning-age-filter-group">
                      {[
                        { k: "all", label: "All" },
                        { k: "fresh", label: "Fresh <3d", cls: "text-emerald-700 bg-emerald-50", on: "bg-emerald-500 text-white border-emerald-600" },
                        { k: "warm", label: "3-7d", cls: "text-amber-700 bg-amber-50", on: "bg-amber-500 text-white border-amber-600" },
                        { k: "stale", label: ">7d", cls: "text-rose-700 bg-rose-50", on: "bg-rose-500 text-white border-rose-600" },
                      ].map((o) => (
                        <button
                          key={o.k}
                          type="button"
                          onClick={() => setAgeFilter(o.k)}
                          className={`h-8 px-2 text-[10px] uppercase tracking-widest transition-colors ${ageFilter === o.k ? (o.on || "bg-neutral-800 text-white") : (o.cls || "text-neutral-600 bg-white hover:bg-neutral-50")}`}
                          data-testid={`planning-age-filter-${o.k}`}
                        >
                          {o.label}
                        </button>
                      ))}
                    </div>
                    {/* R159y · Feb 2026 — Finance Report launcher */}
                    {canFinanceEdit && (
                      <button
                        type="button"
                        onClick={() => setShowFinanceReport(true)}
                        className="inline-flex items-center gap-1 px-2 h-8 border rounded-none text-[10px] uppercase tracking-widest shrink-0 border-amber-500 bg-amber-500 text-white hover:bg-amber-600"
                        title="Open the task-level finance report — search, sort, group by project/brand/client, export CSV"
                        data-testid="planning-finance-report-open"
                      >
                        ₹ Finance report
                      </button>
                    )}
                    {/* R159y · Feb 2026 — Board finance totals (per
                        currency) computed over visible cards only. */}
                    {canFinanceEdit && Object.keys(boardFinanceRollup).length > 0 && (
                      <div className="inline-flex items-center gap-1 shrink-0" data-testid="planning-finance-board-total">
                        {Object.entries(boardFinanceRollup).map(([c, v]) => (
                          <span key={c} className="inline-flex items-center gap-1 px-2 h-8 border border-amber-300 bg-amber-50 text-[10px] uppercase tracking-widest font-mono">
                            <span className="font-semibold">{c}</span>
                            <span>{fmtMoney(v.est, c)}</span>
                            <span className="opacity-40">·</span>
                            <span>{fmtMoney(v.paid, c)}</span>
                            <span className="opacity-40">·</span>
                            <span className={v.balance > 0 ? "text-rose-700" : v.balance < 0 ? "text-emerald-700" : ""}>{fmtMoney(v.balance, c)}</span>
                          </span>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </>
            )}
              </div>
              <div className="flex items-center gap-0.5 text-[9px] uppercase tracking-widest text-muted-foreground">
                <Wallet className="w-2.5 h-2.5" /> Accounts
                <button
                  type="button"
                  onClick={() => togglePin("accounts")}
                  className={`ml-1 opacity-0 group-hover:opacity-100 hover:text-primary transition ${myBoardPins.has("accounts") ? "opacity-100 text-primary" : ""}`}
                  title={myBoardPins.has("accounts") ? "Unpin from My Board" : "Pin to My Board"}
                  data-testid="planning-pin-accounts"
                >
                  <Star className="w-2.5 h-2.5" fill={myBoardPins.has("accounts") ? "currentColor" : "none"} />
                </button>
              </div>
            </div>{/* /R159hh ribbon-panel accounts */}
            {/* R159u · Feb 2026 — Unbilled toggle. Chip in the header
                mirrors the per-card bill roll-up click filter so it
                can also be turned OFF from the toolbar when it's on.
                We only render it as a visible pill when active — the
                primary interaction is on the card chip. */}
            {unbilledFilter && (
              <button
                type="button"
                onClick={() => setUnbilledFilter(false)}
                className="inline-flex items-center gap-1 px-2 h-8 border rounded-none text-[10px] uppercase tracking-widest shrink-0 bg-emerald-600 text-white border-emerald-700 hover:bg-emerald-700"
                title="Filter is showing only cards with unbilled/unpaid work — click to clear"
                data-testid="planning-unbilled-clear"
              >
                <span>$</span>
                Unbilled · click to clear
              </button>
            )}
            {/* R159gg · Feb 2026 — Quick-due chips live in the Home tab
                (they're the most-used at-a-glance chips) but are ALSO
                shown in the Filter tab so users hunting for filters
                still see them. */}
            <div
              style={{ display: showPanel("timing", ["home", "filter"]) ? "flex" : "none", order: panelOrder("timing") }}
              className={`flex-col items-center gap-1 pr-2 mr-1 pt-0.5 group ${ribbonTab === "myboard" ? "cursor-grab" : ""}`}
              draggable={ribbonTab === "myboard"}
              onDragStart={onPanelDragStart("timing")}
              onDragOver={onPanelDragOver}
              onDrop={onPanelDrop("timing")}
              data-ribbon-panel="timing">
              <div className="flex items-center gap-1 flex-wrap">
            {/* R159g · Feb 2026 — Quick due-date filter chips. Click a
                chip to narrow every card down to only overdue / today /
                nearing (next 3 days) tasks. Click the active chip
                again to clear. Chips act like a radio group. */}
            <div className="border-l border-border h-6 mx-1 shrink-0" />
            {[
              { v: "overdue", l: "Overdue", cls: "text-red-700 border-red-300 bg-red-50 hover:bg-red-100", activeCls: "bg-red-600 text-white border-red-700" },
              { v: "today",   l: "Due today", cls: "text-amber-800 border-amber-300 bg-amber-50 hover:bg-amber-100", activeCls: "bg-amber-500 text-white border-amber-600" },
              { v: "nearing", l: "Nearing (3d)", cls: "text-sky-800 border-sky-300 bg-sky-50 hover:bg-sky-100", activeCls: "bg-sky-600 text-white border-sky-700" },
            ].map((c) => (
              <button
                key={c.v}
                type="button"
                onClick={() => setQuickDueFilter(quickDueFilter === c.v ? "all" : c.v)}
                className={`inline-flex items-center gap-1 px-2 h-8 border rounded-none text-[10px] uppercase tracking-widest shrink-0 ${quickDueFilter === c.v ? c.activeCls : c.cls}`}
                title={c.v === "overdue" ? "Show only tasks whose due date has already passed" : c.v === "today" ? "Show only tasks due today" : "Show only tasks due within the next 3 days"}
                data-testid={`planning-quick-due-${c.v}`}
              >
                <Calendar className="w-3 h-3" />
                {c.l}
                {c.v === "overdue" && overdueSummary.count > 0 && (
                  <span className="ml-0.5 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[9px] bg-white text-red-700 font-bold">{overdueSummary.count}</span>
                )}
              </button>
            ))}
            {/* R159h · Feb 2026 — Jump to most-overdue project. Clicking
                flips on the Overdue chip AND scrolls the canvas so the
                worst-offender card is centred + flashed. If there are
                no overdue tasks, we cheer the user on with a toast. */}
            {overdueSummary.count > 0 && (
              <button
                type="button"
                onClick={jumpToMostOverdue}
                className="inline-flex items-center gap-1 px-2 h-8 border border-red-400 rounded-none text-[10px] uppercase tracking-widest shrink-0 bg-white text-red-700 hover:bg-red-50"
                title={`Click to jump to the oldest overdue project · ${overdueSummary.count} overdue in total · click again to walk through each one`}
                data-testid="planning-jump-overdue"
              >
                <span aria-hidden>🔥</span>
                Jump{overdueSummary.orderedIds.length > 1 ? ` · ${overdueSummary.orderedIds.length} projects` : ` · ${overdueSummary.count}`}
              </button>
            )}
            <div className="border-l border-border h-6 mx-1 shrink-0" />
            <Button size="sm" variant="ghost" className="rounded-none h-8 text-xs shrink-0" onClick={addSticky} data-testid="planning-add-sticky">
              <StickyNote className="w-3.5 h-3.5 mr-1" /> Sticky
            </Button>
            {snoozedIds.size > 0 && (
              <>
                <div className="border-l border-border h-6 mx-1 shrink-0" />
                {/* Iter · Feb 2026 — Snoozed cards manager. Replaces the
                    old "unsnooze all" button with a popover that lists
                    every snoozed project so PMs can selectively bring
                    back the ones they want to review, or nuke the
                    whole set. Fixes the "I hid the wrong card and now
                    lost my whole board arrangement" pain. */}
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 px-2 h-8 border border-primary/40 bg-primary/10 text-primary text-[10px] uppercase tracking-widest rounded-none hover:bg-primary/20 shrink-0"
                      title="Review snoozed cards"
                      data-testid="planning-snoozed-manager"
                    >
                      <Moon className="w-3 h-3" />
                      {snoozedIds.size} snoozed · review
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-72 p-0 rounded-none" align="start">
                    <div className="px-3 py-2 border-b border-border flex items-center justify-between bg-muted/40">
                      <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                        Snoozed cards ({snoozedIds.size})
                      </span>
                      <button
                        type="button"
                        onClick={unsnoozeAll}
                        className="text-[10px] uppercase tracking-widest text-primary hover:underline"
                        data-testid="planning-snoozed-unsnooze-all"
                      >
                        Unsnooze all
                      </button>
                    </div>
                    <ul className="max-h-64 overflow-y-auto divide-y divide-border" data-testid="planning-snoozed-list">
                      {Array.from(snoozedIds).map((pid) => {
                        const proj = (data?.projects || []).find((p) => p.id === pid);
                        if (!proj) return null;
                        return (
                          <li key={pid} className="px-3 py-2 flex items-center gap-2 text-xs">
                            <div className="flex-1 min-w-0">
                              <div className="truncate font-medium" title={proj.name}>{proj.name}</div>
                              {proj.client_name && (
                                <div className="text-[10px] text-muted-foreground truncate">{proj.client_name}{proj.brand_name ? ` · ${proj.brand_name}` : ""}</div>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => setSnoozedIds((prev) => {
                                const next = new Set(prev);
                                next.delete(pid);
                                return next;
                              })}
                              className="shrink-0 inline-flex items-center gap-1 px-2 h-6 border border-primary/40 bg-primary/10 text-primary text-[10px] uppercase tracking-widest rounded-none hover:bg-primary/20"
                              title="Un-snooze this card"
                              data-testid={`planning-snoozed-unsnooze-${pid}`}
                            >
                              <Undo2 className="w-3 h-3" />
                              Unsnooze
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </PopoverContent>
                </Popover>
              </>
            )}
              </div>
              <div className="flex items-center gap-0.5 text-[9px] uppercase tracking-widest text-muted-foreground">
                <Calendar className="w-2.5 h-2.5" /> Timing
                <button
                  type="button"
                  onClick={() => togglePin("timing")}
                  className={`ml-1 opacity-0 group-hover:opacity-100 hover:text-primary transition ${myBoardPins.has("timing") ? "opacity-100 text-primary" : ""}`}
                  title={myBoardPins.has("timing") ? "Unpin from My Board" : "Pin to My Board"}
                  data-testid="planning-pin-timing"
                >
                  <Star className="w-2.5 h-2.5" fill={myBoardPins.has("timing") ? "currentColor" : "none"} />
                </button>
              </div>
            </div>{/* /R159hh ribbon-panel quick-due */}
          </div>
        </div>
      )}

      {/* Filter toolbar */}
      {/* R159jj · Feb 2026 — Hidden on mobile by default (both the
          secondary ribbon and this chip row were together taking half
          the phone screen). The mobile Filter sheet exposes the
          essential due-date chips + a "Show all filters" toggle that
          flips `mobileFiltersOpen` on to reveal this bar when a PM
          actually needs the full chip set. */}
      <div className={`${mobileFiltersOpen ? "block" : "hidden"} md:block`}>
      <FiltersBar
        filters={filters}
        onChange={(next) => {
          // R159e · Feb 2026 — Any filter change wipes the snooze list
          // so the user can bring every card back by simply toggling a
          // filter chip or clearing everything.
          if (snoozedIds.size) setSnoozedIds(new Set());
          setFilters(next);
          loadData(next);
        }}
        onLocalSearchChange={(q) => {
          // R159r · Feb 2026 — Free-text search now narrows the already
          // loaded cards without a server round-trip. Snooze is NOT
          // cleared because the user is just skimming.
          setFilters((prev) => ({ ...prev, q }));
        }}
        searchMatchCount={searchMatchIds.length}
        searchIdx={searchIdx}
        onSearchNext={() => scrollToMatch(searchIdx + 1)}
        onSearchPrev={() => scrollToMatch(searchIdx - 1)}
        options={options}
        currentUserId={user?.id}
        onResetAll={() => {
          // R160ff · Feb 2026 — Reset every filter surface at once:
          //   • Filter chips + free-text search (`filters`)
          //   • Group by (`swimBy`)
          //   • Sticky-bar quick due filter (`quickDueFilter`)
          //   • Unbilled toggle + onlyWatched toggle
          //   • Hide-empty cards + snoozed cards
          // Then triggers a full reload so we see every non-archived
          // project in the workspace.
          setFilters({});
          setSwimBy("none");
          setQuickDueFilter("all");
          setUnbilledFilter(false);
          setOnlyWatched(false);
          setHideEmptyCards(false);
          if (snoozedIds.size) setSnoozedIds(new Set());
          loadData({});
        }}
        hasOtherFilters={swimBy !== "none" || quickDueFilter !== "all" || unbilledFilter || onlyWatched || hideEmptyCards}
        presetsListSlot={
          <FilterPresetsBar
            slot="list"
            workspaceId={activeWorkspaceId}
            getCurrentState={() => ({
              filters, swimBy, quickDueFilter, onlyWatched,
            })}
            activeFingerprint={presetFingerprint({
              filters, swimBy, quickDueFilter, onlyWatched,
            })}
            applyState={(s) => {
              if (!s) return;
              const nextFilters = s.filters || {};
              setFilters(nextFilters);
              if (s.swimBy !== undefined) setSwimBy(s.swimBy || "none");
              if (s.quickDueFilter !== undefined) setQuickDueFilter(s.quickDueFilter || "all");
              if (s.onlyWatched !== undefined) setOnlyWatched(!!s.onlyWatched);
              if (snoozedIds.size) setSnoozedIds(new Set());
              loadData(nextFilters);
            }}
          />
        }
        extraStateForPreset={{ swimBy, quickDueFilter, onlyWatched }}
        // eslint-disable-next-line react/no-unstable-nested-components
        presetsSaveRenderer={(stateSnapshot) => (
          <FilterPresetsBar
            slot="save"
            workspaceId={activeWorkspaceId}
            getCurrentState={() => stateSnapshot}
          />
        )}
        templatesSlot={
          <BoardTemplatesButton
            workspaceId={activeWorkspaceId}
            getCurrentState={() => ({
              filters, swimBy, quickDueFilter, unbilledFilter,
              onlyWatched, hideEmptyCards, accountsReviewOn,
            })}
            applyState={(s) => {
              if (!s) return;
              if (s.filters !== undefined) setFilters(s.filters || {});
              if (s.swimBy !== undefined) setSwimBy(s.swimBy || "none");
              if (s.quickDueFilter !== undefined) setQuickDueFilter(s.quickDueFilter || "all");
              if (s.unbilledFilter !== undefined) setUnbilledFilter(!!s.unbilledFilter);
              if (s.onlyWatched !== undefined) setOnlyWatched(!!s.onlyWatched);
              if (s.hideEmptyCards !== undefined) setHideEmptyCards(!!s.hideEmptyCards);
              if (s.accountsReviewOn !== undefined) setAccountsReviewOn(!!s.accountsReviewOn);
              loadData(s.filters || {});
            }}
          />
        }
      />
      </div>{/* /R159jj FiltersBar mobile toggle wrapper */}
      </div>{/* /R159v sticky header wrapper */}

      {/* Canvas */}
      <div
        ref={canvasRef}
        className="relative w-full overflow-auto bg-neutral-50"
        style={{ minHeight: "calc(100vh - 220px)" }}
        data-testid="planning-canvas"
        onScroll={(e) => {
          // R159e · Feb 2026 — Throttle-persist canvas scroll so the
          // user resumes exactly where they left off. The 900ms
          // debounced writer already batches saves.
          const el = e.currentTarget;
          setScrollPos({ x: el.scrollLeft, y: el.scrollTop });
        }}
      >
        {loading ? (
          <div className="p-12 text-center text-sm text-muted-foreground">
            <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin" />
            Loading projects…
          </div>
        ) : boardMode === "team" ? (
          /* Iter · Feb 2026 — Team cards mode: one card per active
             workspace member with their filtered tasks + status-update
             shortcuts. Reuses the same `data.tasks` list so every filter
             above the toolbar still applies. */
          <TeamCardsView
            users={options.users}
            members={members}
            tasks={data?.tasks || []}
            projects={data?.projects || []}
            brands={options.brands || []}
            clients={options.clients || []}
            hideEmpty={hideEmptyCards}
            workspaceName={activeWorkspace?.name || ""}
            currentUserId={user?.id}
            statusTemplates={statusTemplates}
            senderName={user?.name || ""}
            onOpenTask={(tid) => setEditingTaskId(tid)}
          />
        ) : (data?.projects || []).length === 0 ? (
          <div className="p-12 text-center text-sm text-muted-foreground">
            <LayoutGrid className="w-8 h-8 mx-auto mb-2 opacity-50" />
            No projects match the current filters. Try clearing them, or invite the PM to create the first project.
          </div>
        ) : (isMobile || swimBy !== "none") /* R159ii · Feb 2026 —
             Flow layout ONLY for phones and grouped modes. R159gg
             tried to use flow layout even in Free (no groups) mode,
             but that broke drag / resize / reposition — the whole
             point of Free mode. Restored the Rnd freeform canvas
             for Free mode on desktop while keeping the flow layout
             everywhere else. */ ? (
          /* R159f · Feb 2026 — Mobile stacked view. On phones (< md)
             absolute positioning + Rnd drag/resize don't work well —
             cards overflow, lane labels get clipped and collapse
             behavior breaks. Replace with a plain vertical stack
             grouped by swimlane (if enabled).
             R159ee · Feb 2026 — Also use this stacked layout on desktop
             whenever a Group By dimension is active. The fixed 4000×3000
             absolute canvas clipped cards when a lane had many projects
             (the reported "Konark Solartech" bug). The stacked layout
             lets each lane grow with its content and wraps cards into
             a responsive grid on wide screens.
             R159gg · Feb 2026 — Extend the flow layout to Free (no
             groups) mode too. */
          <MobileStackedBoard
            desktop={!isMobile}
            projects={searchedProjects}
            tasksByProject={tasksByProject}
            savedLayout={savedLayout}
            snoozedIds={snoozedIds}
            hideEmptyCards={hideEmptyCards}
            onlyWatched={onlyWatched}
            quickDueFilter={quickDueFilter}
            unbilledFilter={unbilledFilter}
            unbilledActive={unbilledFilter}
            onToggleUnbilled={() => setUnbilledFilter((v) => !v)}
            accountsReviewOn={accountsReviewOn}
            accountsSummary={accountsSummary}
            taskNotesByProject={taskNotesByProject}
            postInlineAccountsReply={postInlineAccountsReply}
            needsReviewOnly={needsReviewOnly}
            needsReviewForProject={needsReviewForProject}
            onMarkAccountsReviewed={markAccountsReviewed}
            flashProjectId={flashProjectId}
            watchedProjectIds={watchedProjectIds}
            watchedIds={watchedIds}
            filters={filters}
            swimBy={swimBy}
            laneOrders={laneOrders}
            laneCollapse={laneCollapse}
            setLaneCollapse={setLaneCollapse}
            options={options}
            members={members}
            user={user}
            onOpenTask={(tid) => setEditingTaskId(tid)}
            onOpenAllTasks={setTasksModalProject}
            onOpenStatusUpdates={setUpdatesModalProject}
            onToggleProjectWatch={onToggleProjectWatch}
            onToggleWatch={onToggleWatch}
            onChangeTaskStatus={onChangeTaskStatus}
            onChangeTaskPriority={onChangeTaskPriority}
            onOpenNotes={setNotesFor}
            onSnooze={snoozeProject}
            onCardLayoutChange={updateCard}
            onTasksReload={reloadProjectTasks}
            onProjectReload={refetchOneProject}
            onProjectMetaReload={refetchOneProjectMeta}
            onProjectLocalPatch={patchProjectLocal}
            canBulkAddTask={canBulkAddTask}
            onOpenBulkAdd={setBulkAddProject}
            statusTemplates={statusTemplates}
            onOpenTaskFinance={openTaskFinance}
            showTaskFinanceIcons={accountsReviewOn && canFinanceEdit}
            projectFinanceByPid={projectFinanceByPid}
            projectEffortByPid={projectEffortByPid}
            mentionCandidates={members || []}
          />
        ) : (
          <div className="relative" style={{ width: 4000, height: 3000, transform: `scale(${zoomLevel})`, transformOrigin: "top left" }}>
            {/* R158o · Feb 2026 — Swimlane labels with per-lane collapse.
                Only rendered when swimBy != "none". */}
            {swimBy !== "none" && (() => {
              const groups = new Map();
              (data.projects || []).forEach((p) => {
                let key = "—";
                if (swimBy === "client") key = p.client_name || "No client";
                else if (swimBy === "brand") key = p.brand_name || "No brand";
                else if (swimBy === "department") key = p.department_name || "No department";
                else if (swimBy === "pm") key = p.pm_name || p.owner_name || "Unassigned PM";
                else if (swimBy === "health") key = p.health || "no health";
                else if (swimBy === "status") key = p.status || "no status";
                if (!groups.has(key)) groups.set(key, []);
                groups.get(key).push(p);
              });
              const rawKeys = [...groups.keys()];
              const custom = (laneOrders && laneOrders[swimBy]) || [];
              const listed = custom.filter((k) => groups.has(k));
              const rest = rawKeys.filter((k) => !listed.includes(k)).sort((a, b) => a.localeCompare(b));
              const orderedKeys = [...listed, ...rest];
              // R158v · Feb 2026 — Dynamic lane heights: a collapsed lane
              // only takes the label row (32px + 20px breathing room)
              // instead of a full card row, so subsequent lanes float up
              // to fill the whitespace.
              const LANE_LABEL_H = 40;
              const laneH = LANE_LABEL_H + DEFAULT_CARD_H + 20;
              const COLLAPSED_LANE_H = LANE_LABEL_H + HEADER_H + 12;
              // Precompute lane Y offsets based on current collapse state
              // AND card positions inside each lane for the toggle handler.
              const laneYs = {};
              {
                let yOffset = 20;
                for (const k of orderedKeys) {
                  laneYs[k] = yOffset;
                  yOffset += (laneCollapse[k] ? COLLAPSED_LANE_H : laneH);
                }
              }
              // R158p · Drop handler — commit a new lane order and
              // re-flow all cards so the visual matches.
              const handleLaneDrop = (targetKey) => {
                if (!dragLaneKey || dragLaneKey === targetKey) {
                  setDragLaneKey(null);
                  setDragOverLaneKey(null);
                  return;
                }
                const next = orderedKeys.filter((k) => k !== dragLaneKey);
                const idx = next.indexOf(targetKey);
                next.splice(idx < 0 ? next.length : idx, 0, dragLaneKey);
                setLaneOrders((prev) => ({ ...prev, [swimBy]: next }));
                setDragLaneKey(null);
                setDragOverLaneKey(null);
                // Re-flow cards immediately using the new order.
                setTimeout(() => arrangeSwimlanes(swimBy), 0);
                toast.success(`Moved lane "${dragLaneKey}"`);
              };
              // R158u · Feb 2026 — Keyboard nudge for accessibility. When
              // a lane label is focused, Alt+ArrowUp / Alt+ArrowDown moves
              // that lane up/down by one position and re-flows the cards.
              const handleLaneKeyNudge = (key, direction) => {
                const idx = orderedKeys.indexOf(key);
                if (idx < 0) return;
                const targetIdx = direction === "up" ? idx - 1 : idx + 1;
                if (targetIdx < 0 || targetIdx >= orderedKeys.length) return;
                const next = [...orderedKeys];
                next.splice(idx, 1);
                next.splice(targetIdx, 0, key);
                setLaneOrders((prev) => ({ ...prev, [swimBy]: next }));
                setTimeout(() => arrangeSwimlanes(swimBy), 0);
                toast.success(`Moved lane "${key}" ${direction}`);
              };
              return orderedKeys.map((key, i) => {
                const laneY = laneYs[key];
                const collapsed = !!laneCollapse[key];
                const isDragging = dragLaneKey === key;
                const isDropTarget = dragOverLaneKey === key && dragLaneKey && dragLaneKey !== key;
                return (
                  <div
                    key={`lane-${key}`}
                    draggable
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (!e.altKey) return;
                      if (e.key === "ArrowUp") { e.preventDefault(); handleLaneKeyNudge(key, "up"); }
                      else if (e.key === "ArrowDown") { e.preventDefault(); handleLaneKeyNudge(key, "down"); }
                    }}
                    onDragStart={(e) => {
                      setDragLaneKey(key);
                      try { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", key); } catch { /* noop */ }
                    }}
                    onDragOver={(e) => {
                      if (dragLaneKey && dragLaneKey !== key) {
                        e.preventDefault();
                        try { e.dataTransfer.dropEffect = "move"; } catch { /* noop */ }
                        if (dragOverLaneKey !== key) setDragOverLaneKey(key);
                      }
                    }}
                    onDragLeave={() => { if (dragOverLaneKey === key) setDragOverLaneKey(null); }}
                    onDrop={(e) => { e.preventDefault(); handleLaneDrop(key); }}
                    onDragEnd={() => { setDragLaneKey(null); setDragOverLaneKey(null); }}
                    className={`absolute flex items-center gap-2 px-3 border-b-2 bg-primary/5 backdrop-blur-sm z-0 cursor-grab active:cursor-grabbing transition-all outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 ${isDragging ? "opacity-40 border-primary" : isDropTarget ? "border-emerald-500 bg-emerald-500/15 ring-2 ring-emerald-500/40" : "border-primary/30"}`}
                    style={{ left: 8, top: laneY - 4, width: 200, height: 32 }}
                    data-testid={`planning-swim-lane-${key}`}
                    title="Drag to reorder · or focus + Alt+↑/↓ to nudge"
                  >
                    <GripVertical className="w-3 h-3 text-primary/60 shrink-0" />
                    <button
                      type="button"
                      draggable={false}
                      onDragStart={(e) => e.stopPropagation()}
                      onClick={() => {
                        // R158v · Toggle every card in this lane's collapsed
                        // state AND re-flow cards below by the height delta
                        // so no whitespace remains between lanes.
                        const nextCollapsed = !collapsed;
                        const dy = (nextCollapsed ? COLLAPSED_LANE_H - laneH : laneH - COLLAPSED_LANE_H);
                        const laneIdx = orderedKeys.indexOf(key);
                        // Build a set of project ids in the CURRENT and
                        // subsequent lanes so we know which cards to
                        // reposition.
                        const subsequentLaneKeys = orderedKeys.slice(laneIdx + 1);
                        const subsequentPids = new Set();
                        for (const k of subsequentLaneKeys) {
                          for (const pr of (groups.get(k) || [])) subsequentPids.add(pr.id);
                        }
                        setSavedLayout((prev) => {
                          const next = { ...prev };
                          // Collapse/expand cards in this lane.
                          for (const proj of groups.get(key)) {
                            next[proj.id] = { ...(next[proj.id] || {}), collapsed: nextCollapsed };
                          }
                          // Shift all cards in subsequent lanes by dy.
                          for (const pid of subsequentPids) {
                            const cur = next[pid] || {};
                            next[pid] = { ...cur, y: (cur.y || 0) + dy };
                          }
                          return next;
                        });
                        setLaneCollapse((prev) => ({ ...prev, [key]: nextCollapsed }));
                      }}
                      className="p-0.5 hover:bg-primary/10 rounded"
                      title={collapsed ? "Expand this lane" : "Collapse this lane"}
                      data-testid={`planning-swim-lane-toggle-${key}`}
                    >
                      {collapsed ? <ChevronRight className="w-3.5 h-3.5 text-primary" /> : <ChevronDown className="w-3.5 h-3.5 text-primary" />}
                    </button>
                    <span className="text-[10px] uppercase tracking-widest font-semibold text-primary truncate">{key}</span>
                    <span className="text-[9px] text-muted-foreground ml-auto">{groups.get(key).length}</span>
                  </div>
                );
              });
            })()}
            {searchedProjects.map((p) => {
              // R158x · "Only watched" filter — skip projects not in
              // the user's watchlist when the pill is enabled.
              if (onlyWatched && !watchedProjectIds.has(p.id)) return null;
              // R159e · Feb 2026 — Snooze filter. Skip project cards
              // the user has temporarily hidden.
              if (snoozedIds.has(p.id)) return null;
              // R159u · Feb 2026 — Unbilled filter. Skip cards that
              // don't have any outstanding billable/billed work.
              if (unbilledFilter && !hasUnbilledWork(tasksByProject[p.id])) return null;
              // R159w · Feb 2026 — Needs-Review filter. Only applies
              // when Accounts Review mode is on.
              if (accountsReviewOn && needsReviewOnly && !needsReviewForProject(p.id)) return null;
              // R159y · Feb 2026 — Age filter chip. When set to a
              // bucket (fresh/warm/stale), only cards whose last
              // accounts-note falls in that bucket survive.
              if (accountsReviewOn && ageFilter !== "all") {
                const n = accountsSummary[p.id];
                if (!n?.created_at) return null;
                const days = (Date.now() - new Date(n.created_at).getTime()) / 86400000;
                const bucket = days < 3 ? "fresh" : days < 7 ? "warm" : "stale";
                if (bucket !== ageFilter) return null;
              }
              const layout = savedLayout[p.id] || { x: 24, y: 24, w: DEFAULT_CARD_W, h: DEFAULT_CARD_H, collapsed: false, task_filter: "all_open" };
              const tasks = applyQuickDueFilter(
                filterTasks(tasksByProject[p.id] || [], layout.task_filter, user?.id),
                quickDueFilter,
              );
              // R158n · Feb 2026 — Hide empty cards toggle: skip cards
              // whose filtered task list is empty so the canvas stays
              // clean when zoning in on a specific assignee/filter.
              if (hideEmptyCards && tasks.length === 0) return null;
              // R159k · Feb 2026 — Collapsed-card header height must grow
              // when the project name wraps to a second line, otherwise
              // the last line + action-chip row get clipped. Approximates
              // by dividing the pixel width of the header by ~7.5 px/char
              // (14 px semibold sans-serif) and rounding up. Row 1
              // contributes `lines * 18 + 12`; row 2 chips are ~32 px.
              const nameLen = (p.name || "").length;
              const usableW = Math.max(120, (layout.w || DEFAULT_CARD_W) - 40); // dot + padding
              const charsPerLine = Math.max(10, Math.floor(usableW / 7.5));
              const nameLines = Math.max(1, Math.min(4, Math.ceil(nameLen / charsPerLine)));
              const collapsedH = 18 * nameLines + 12 + 32;
              return (
                <Rnd
                  key={p.id}
                  size={{ width: layout.w, height: layout.collapsed ? collapsedH : layout.h }}
                  position={{ x: layout.x, y: layout.y }}
                  minWidth={CARD_MIN.w}
                  minHeight={layout.collapsed ? collapsedH : CARD_MIN.h}
                  bounds="parent"
                  dragHandleClassName="planning-card-drag"
                  dragGrid={[GRID, GRID]}
                  resizeGrid={[GRID, GRID]}
                  onDragStop={(e, d) => updateCard(p.id, { x: d.x, y: d.y })}
                  onResizeStop={(e, dir, ref, delta, pos) => updateCard(p.id, {
                    w: parseInt(ref.style.width, 10),
                    h: parseInt(ref.style.height, 10),
                    x: pos.x, y: pos.y,
                  })}
                  className={`z-10 ${flashProjectId === p.id ? "acq-flash-card" : ""}`}
                >
                  <ProjectCard
                    project={p}
                    tasks={tasksByProject[p.id] || []}
                    filteredTasks={tasks}
                    layout={layout}
                    onLayoutChange={(patch) => updateCard(p.id, patch)}
                    onTasksReload={() => reloadProjectTasks(p.id)}
                    onProjectReload={() => refetchOneProject(p.id)}
                    onProjectMetaReload={() => refetchOneProjectMeta(p.id)}
                    onProjectLocalPatch={patchProjectLocal}
                    users={options.users}
                    clients={options.clients}
                    brands={options.brands}
                    members={members}
                    projects={data?.projects || []}
                    onOpenTask={(tid) => setEditingTaskId(tid)}
                    onOpenAllTasks={() => setTasksModalProject(p)}
                    onOpenStatusUpdates={() => setUpdatesModalProject(p)}
                    projectWatched={watchedProjectIds.has(p.id)}
                    onToggleProjectWatch={onToggleProjectWatch}
                    watchedIds={watchedIds}
                    onToggleWatch={onToggleWatch}
                    onChangeTaskStatus={onChangeTaskStatus}
                    onChangeTaskPriority={onChangeTaskPriority}
                    onOpenNotes={setNotesFor}
                    onSnooze={() => snoozeProject(p.id, p.name)}
                    unbilledActive={unbilledFilter}
                    onToggleUnbilled={() => setUnbilledFilter((v) => !v)}
                    accountsReviewOn={accountsReviewOn}
                    accountsNote={accountsSummary[p.id]}
                    accountsNeedsReview={accountsReviewOn && needsReviewForProject(p.id)}
                    onMarkAccountsReviewed={() => markAccountsReviewed(p.id)}
                    accountsTaskNotes={taskNotesByProject[p.id] || []}
                    onInlineAccountsReply={postInlineAccountsReply}
                    onOpenTaskNote={(taskId) => setEditingTaskId(taskId)}
                    onOpenTaskFinance={openTaskFinance}
                    showTaskFinanceIcons={accountsReviewOn && canFinanceEdit}
                    projectFinanceRollup={projectFinanceByPid[p.id] || {}}
                    projectEffortRollup={projectEffortByPid[p.id] || null}
                    projectMemberIds={p.project_member_ids || []}
                    mentionCandidates={members || []}
                    canBulkAddTask={canBulkAddTask}
                    onOpenBulkAdd={setBulkAddProject}
                    dragTaskId={dragTaskId}
                    hoverProjectId={hoverProjectId}
                    onTaskDragStart={setDragTaskId}
                    onTaskDragEnd={() => { setDragTaskId(null); setHoverProjectId(null); }}
                    onCardDragOver={(pid) => setHoverProjectId(pid)}
                    onCardDrop={(pid) => {
                      if (!dragTaskId) return;
                      const cur = (data?.tasks || []).find((x) => x.id === dragTaskId);
                      if (cur && cur.project_id !== pid) {
                        moveTaskToProject(dragTaskId, pid);
                      }
                      setDragTaskId(null); setHoverProjectId(null);
                    }}
                    statusTemplates={statusTemplates}
                    senderName={user?.name || ""}
                  />
                </Rnd>
              );
            })}

            {/* Sticky notes */}
            {stickyNotes.map((n) => (
              <Rnd
                key={n.id}
                size={{ width: n.w, height: n.h }}
                position={{ x: n.x, y: n.y }}
                minWidth={140}
                minHeight={100}
                bounds="parent"
                dragHandleClassName="sticky-drag"
                dragGrid={[GRID, GRID]}
                onDragStop={(e, d) => setStickyNotes((prev) => prev.map((s) => s.id === n.id ? { ...s, x: d.x, y: d.y } : s))}
                onResizeStop={(e, dir, ref, delta, pos) => setStickyNotes((prev) => prev.map((s) => s.id === n.id ? { ...s, w: parseInt(ref.style.width, 10), h: parseInt(ref.style.height, 10), x: pos.x, y: pos.y } : s))}
                className="z-20"
              >
                <StickyNoteCard
                  note={n}
                  onChange={(patch) => setStickyNotes((prev) => prev.map((s) => s.id === n.id ? { ...s, ...patch } : s))}
                  onDelete={() => setStickyNotes((prev) => prev.filter((s) => s.id !== n.id))}
                />
              </Rnd>
            ))}
          </div>
        )}
      </div>

      {newBoardOpen && (
        <NewBoardDialog
          onClose={() => setNewBoardOpen(false)}
          onCreate={(name, isShared, startBlank) => { setNewBoardOpen(false); createBoard(name, isShared, startBlank); }}
        />
      )}

      {/* R158b · Presentation mode overlay */}
      {presentIdx !== null && (data?.projects || [])[presentIdx] && (
        <PresentationOverlay
          project={(data.projects || [])[presentIdx]}
          idx={presentIdx}
          total={(data.projects || []).length}
          tasks={tasksByProject[(data.projects || [])[presentIdx].id] || []}
          users={options.users}
          allProjects={data?.projects || []}
          watchedIds={watchedIds}
          wsId={activeWorkspaceId}
          members={members}
          projectWatched={watchedProjectIds.has((data.projects || [])[presentIdx]?.id)}
          onToggleProjectWatch={onToggleProjectWatch}
          onOpenTask={(tid) => setEditingTaskId(tid)}
          onToggleWatch={onToggleWatch}
          onChangeStatus={onChangeTaskStatus}
          onChangePriority={onChangeTaskPriority}
          onOpenNotes={setNotesFor}
          onOpenStatusUpdates={setUpdatesModalProject}
          onProjectRefresh={() => loadData(filters)}
          onTaskCreated={insertNewTaskOptimistic}
          onTasksReload={() => reloadProjectTasks((data.projects || [])[presentIdx].id)}
          onJumpTo={(newIdx) => setPresentIdx(newIdx)}
          onRestart={() => setPresentIdx(0)}
          onPrev={() => setPresentIdx((i) => Math.max(0, (i ?? 0) - 1))}
          onNext={() => setPresentIdx((i) => Math.min((data.projects || []).length - 1, (i ?? 0) + 1))}
          onClose={() => setPresentIdx(null)}
        />
      )}

      {/* R158c · Shared TaskDetail dialog reused from the Tasks page */}
      {editingTask && (
        <TaskDetail
          task={editingTask}
          members={members}
          allTasks={data?.tasks || []}
          projectMemberIds={editingTask?.project_id ? [...(projectMemberIdsById[editingTask.project_id] || new Set())] : []}
          onClose={() => { setEditingTaskId(null); setEditingTask(null); }}
          onPatch={(patch) => patchTaskFromDetail(editingTask.id, patch)}
          onArchiveChanged={async () => {
            try {
              const { data: refreshed } = await api.get(`/tasks/${editingTask.id}`);
              if (refreshed && refreshed.id) setEditingTask(refreshed);
              loadData(filters);
            } catch { /* ignore */ }
          }}
          currentUserId={user?.id}
        />
      )}

      {/* R159ss · Feb 2026 — Chooser dialog for assigning a task to a
          user who isn't on the project's member roster. */}
      {assignOutsideDialog}

      {/* Iter · Feb 2026 — Close-task modal. Fires when a user flips a
          task to Done from the Planning Board (dot-click / hover-card /
          bulk row). Ensures no task closes silently at zero hours. */}
      <CloseTaskWithTimeDialog
        open={!!closeGuardTask}
        task={closeGuardTask}
        onCancel={() => setCloseGuardTask(null)}
        onDone={(resp) => {
          const t = resp?.task;
          if (t?.id) {
            setData((prev) => prev ? {
              ...prev,
              tasks: prev.tasks.map((x) => x.id === t.id ? { ...x, ...t } : x),
            } : prev);
          }
          setCloseGuardTask(null);
        }}
      />

      {/* R158s · Feb 2026 — Accounts Notes dialog reused from Billing view */}
      {notesFor && (
        <BillingNotesDialog
          wsId={activeWorkspaceId}
          scope={notesFor.scope}
          scopeId={notesFor.id}
          scopeLabel={notesFor.label}
          onClose={() => { setNotesFor(null); setAccountsRefreshTick((t) => t + 1); }}
        />
      )}

      {/* R159y · Feb 2026 — Task Finance popup (Est / Paid / Currency
          + optional accounts note) */}
      <TaskFinanceDialog
        open={!!financeTask}
        wsId={activeWorkspaceId}
        task={financeTask}
        onClose={() => setFinanceTask(null)}
        onSaved={onFinanceSaved}
      />

      {/* R159y · Feb 2026 — Full Finance Report modal */}
      <FinanceReportDialog
        open={showFinanceReport}
        onClose={() => setShowFinanceReport(false)}
        tasks={data?.tasks || []}
        projects={data?.projects || []}
        wsId={activeWorkspaceId}
        onOpenTask={(tid) => { setEditingTaskId(tid); }}
        onOpenNotes={(target) => { setNotesFor(target); }}
        onOpenFinance={(t) => { openTaskFinance(t); }}
        onPatchTask={async (tid, patch) => {
          try {
            await api.patch(`/tasks/${tid}`, patch);
            setData((prev) => {
              if (!prev) return prev;
              return { ...prev, tasks: (prev.tasks || []).map((t) => t.id === tid ? { ...t, ...patch } : t) };
            });
          } catch { toast.error("Couldn't update billing state"); }
        }}
      />

      {/* R159y · Feb 2026 — Per-card Bulk Task Creator. Preselects the
          project so reviewers can dump multiple rows in one paste. */}
      {bulkAddProject && (
        <BulkTaskCreator
          open={!!bulkAddProject}
          onClose={() => setBulkAddProject(null)}
          wsId={activeWorkspaceId}
          projects={data?.projects || []}
          members={members || []}
          preset={{ project_id: bulkAddProject.id, project_name: bulkAddProject.name }}
          presetDefaults={{ project_id: bulkAddProject.id }}
          onCreated={() => { const pid = bulkAddProject.id; setBulkAddProject(null); refetchOneProject(pid); }}
        />
      )}

      {/* R159hh · Feb 2026 — Full Mobile Sheets. Each of the four
          mobile quick tabs (View / Filter / Group / Bulk) opens a
          dedicated bottom sheet with the exact controls that
          category needs — phones no longer need the desktop wrap-row
          toolbar at all. Group has no sheet: the quick-tab itself
          IS the group-by select. */}
      <Sheet open={mobileSheet === "view"} onOpenChange={(v) => !v && setMobileSheet(null)}>
        <SheetContent side="bottom" className="max-h-[80vh] overflow-y-auto rounded-t-lg" data-testid="planning-mobile-sheet-view">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2 text-base">
              <SlidersHorizontal className="w-4 h-4" /> View
            </SheetTitle>
          </SheetHeader>
          <div className="space-y-4 py-4">
            <section>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Bulk collapse</div>
              <div className="flex gap-2">
                <Button
                  size="sm" variant="outline" className="flex-1 rounded-none"
                  onClick={() => { setSavedLayout((prev) => {
                    const next = { ...prev };
                    for (const p of (data?.projects || [])) next[p.id] = { ...(next[p.id] || {}), collapsed: true };
                    return next;
                  }); setMobileSheet(null); }}
                  data-testid="planning-mobile-collapse-all"
                >
                  <ChevronUp className="w-4 h-4 mr-1" /> Collapse all
                </Button>
                <Button
                  size="sm" variant="outline" className="flex-1 rounded-none"
                  onClick={() => { setSavedLayout((prev) => {
                    const next = { ...prev };
                    for (const p of (data?.projects || [])) next[p.id] = { ...(next[p.id] || {}), collapsed: false };
                    return next;
                  }); setMobileSheet(null); }}
                  data-testid="planning-mobile-expand-all"
                >
                  <ChevronDown className="w-4 h-4 mr-1" /> Expand all
                </Button>
              </div>
            </section>
            <section>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Display filters</div>
              <label className="flex items-center gap-2 py-2 text-sm">
                <input
                  type="checkbox"
                  checked={hideEmptyCards}
                  onChange={(e) => setHideEmptyCards(e.target.checked)}
                  className="w-4 h-4"
                  data-testid="planning-mobile-hide-empty"
                />
                Hide empty cards
              </label>
              <label className="flex items-center gap-2 py-2 text-sm">
                <input
                  type="checkbox"
                  checked={onlyWatched}
                  onChange={(e) => setOnlyWatched(e.target.checked)}
                  className="w-4 h-4"
                  data-testid="planning-mobile-only-watched"
                />
                <Eye className="w-4 h-4" />
                Only watched{onlyWatched && watchedProjectIds.size > 0 ? ` (${watchedProjectIds.size})` : ""}
              </label>
            </section>
            {/* Iter · Feb 2026 — Snoozed cards manager in the mobile
                View sheet. Desktop already has this in the ribbon's
                Display panel + a floating chip; on phones the ribbon
                is hidden and users could only see the top-toolbar chip
                which only appears when N > 0. Adding it here so mobile
                PMs get a stable, always-visible entry point too. */}
            <section>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Snoozed cards</div>
              <div className="border border-border">
                <div className="px-3 py-2 border-b border-border flex items-center justify-between bg-muted/40">
                  <span className="text-xs">
                    {snoozedIds.size === 0
                      ? "No cards snoozed"
                      : `${snoozedIds.size} snoozed`}
                  </span>
                  {snoozedIds.size > 0 && (
                    <button
                      type="button"
                      onClick={() => { unsnoozeAll(); }}
                      className="text-[10px] uppercase tracking-widest text-primary hover:underline"
                      data-testid="planning-mobile-snoozed-unsnooze-all"
                    >
                      Unsnooze all
                    </button>
                  )}
                </div>
                {snoozedIds.size === 0 ? (
                  <div className="px-3 py-3 text-xs text-muted-foreground italic">
                    Long-press a project card and choose &ldquo;Snooze&rdquo; to temporarily hide it from the board.
                  </div>
                ) : (
                  <ul className="max-h-56 overflow-y-auto divide-y divide-border" data-testid="planning-mobile-snoozed-list">
                    {Array.from(snoozedIds).map((pid) => {
                      const proj = (data?.projects || []).find((p) => p.id === pid);
                      if (!proj) return null;
                      return (
                        <li key={pid} className="px-3 py-2 flex items-center gap-2 text-xs">
                          <div className="flex-1 min-w-0">
                            <div className="truncate font-medium" title={proj.name}>{proj.name}</div>
                            {proj.client_name && (
                              <div className="text-[10px] text-muted-foreground truncate">{proj.client_name}{proj.brand_name ? ` · ${proj.brand_name}` : ""}</div>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => setSnoozedIds((prev) => {
                              const next = new Set(prev);
                              next.delete(pid);
                              return next;
                            })}
                            className="shrink-0 inline-flex items-center gap-1 px-2 h-7 border border-primary/40 bg-primary/10 text-primary text-[10px] uppercase tracking-widest rounded-none hover:bg-primary/20"
                            title="Un-snooze this card"
                            data-testid={`planning-mobile-snoozed-unsnooze-${pid}`}
                          >
                            <Undo2 className="w-3 h-3" />
                            Unsnooze
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </section>
            <section>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Sizing</div>
              <Button
                variant="outline" size="sm" className="rounded-none w-full"
                onClick={() => { setShowCardSizeDialog(true); setMobileSheet(null); }}
                data-testid="planning-mobile-card-size"
              >
                <SlidersHorizontal className="w-4 h-4 mr-1" /> Card size ({defaultCardSize.w}×{defaultCardSize.h})
              </Button>
            </section>
          </div>
        </SheetContent>
      </Sheet>

      <Sheet open={mobileSheet === "filter"} onOpenChange={(v) => !v && setMobileSheet(null)}>
        <SheetContent side="bottom" className="max-h-[80vh] overflow-y-auto rounded-t-lg" data-testid="planning-mobile-sheet-filter">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2 text-base">
              <Filter className="w-4 h-4" /> Filter
            </SheetTitle>
          </SheetHeader>
          <div className="space-y-4 py-4">
            <section>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Due-date</div>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { v: "overdue", l: "Overdue", cls: "text-red-700 border-red-300 bg-red-50", activeCls: "bg-red-600 text-white border-red-700" },
                  { v: "today", l: "Due today", cls: "text-amber-800 border-amber-300 bg-amber-50", activeCls: "bg-amber-500 text-white border-amber-600" },
                  { v: "nearing", l: "Nearing (3d)", cls: "text-sky-800 border-sky-300 bg-sky-50", activeCls: "bg-sky-600 text-white border-sky-700" },
                ].map((c) => (
                  <button
                    key={c.v}
                    type="button"
                    onClick={() => setQuickDueFilter(quickDueFilter === c.v ? "all" : c.v)}
                    className={`h-9 border rounded-none text-[10px] uppercase tracking-widest ${quickDueFilter === c.v ? c.activeCls : c.cls}`}
                    data-testid={`planning-mobile-quick-due-${c.v}`}
                  >
                    {c.l}
                  </button>
                ))}
              </div>
            </section>
            {unbilledFilter && (
              <section>
                <button
                  type="button"
                  onClick={() => setUnbilledFilter(false)}
                  className="w-full h-9 border rounded-none text-[10px] uppercase tracking-widest bg-emerald-600 text-white border-emerald-700"
                  data-testid="planning-mobile-unbilled-clear"
                >
                  Clear &quot;Unbilled&quot; filter
                </button>
              </section>
            )}
            <section>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">More filters</div>
              <p className="text-xs text-muted-foreground">
                Toggle the sticky filter chip row (Search + PM / Status / Health / Client / Brand / Project / Priority) on or off so it only takes up your screen when you actually need it.
              </p>
              <label className="flex items-center gap-2 py-2 mt-2 text-sm">
                <input
                  type="checkbox"
                  checked={mobileFiltersOpen}
                  onChange={(e) => setMobileFiltersOpen(e.target.checked)}
                  className="w-4 h-4"
                  data-testid="planning-mobile-toggle-filter-chips"
                />
                Show filter chip row
              </label>
            </section>
          </div>
        </SheetContent>
      </Sheet>

      <Sheet open={mobileSheet === "bulk"} onOpenChange={(v) => !v && setMobileSheet(null)}>
        <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto rounded-t-lg" data-testid="planning-mobile-sheet-bulk">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2 text-base">
              <span className="w-4 h-4 inline-flex items-center justify-center"><Wallet className="w-4 h-4" /></span> Accounts
            </SheetTitle>
          </SheetHeader>
          <div className="space-y-4 py-4">
            {canAccountsReview && (
              <section>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Accounts review</div>
                <label className="flex items-center gap-2 py-2 text-sm">
                  <input
                    type="checkbox"
                    checked={accountsReviewOn}
                    onChange={(e) => setAccountsReviewOn(e.target.checked)}
                    className="w-4 h-4"
                    data-testid="planning-mobile-accounts-review"
                  />
                  Accounts review mode
                </label>
                {accountsReviewOn && (
                  <label className="flex items-center gap-2 py-2 text-sm">
                    <input
                      type="checkbox"
                      checked={needsReviewOnly}
                      onChange={(e) => setNeedsReviewOnly(e.target.checked)}
                      className="w-4 h-4"
                      data-testid="planning-mobile-needs-review"
                    />
                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                    Show only &quot;needs review&quot;
                  </label>
                )}
              </section>
            )}
            {canAccountsReview && accountsReviewOn && (
              <section>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Digest</div>
                <div className="grid grid-cols-2 gap-2">
                  <Button size="sm" variant="outline" className="rounded-none" onClick={() => { copyAccountsDigest(); setMobileSheet(null); }} data-testid="planning-mobile-copy-digest">
                    <Copy className="w-4 h-4 mr-1" /> Copy digest
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-none" onClick={() => { downloadAccountsDigestCSV(); setMobileSheet(null); }} data-testid="planning-mobile-csv-digest">
                    <Download className="w-4 h-4 mr-1" /> CSV
                  </Button>
                </div>
              </section>
            )}
            {canFinanceEdit && (
              <section>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Finance</div>
                <Button
                  size="sm"
                  className="rounded-none w-full bg-amber-500 hover:bg-amber-600 text-white"
                  onClick={() => { setShowFinanceReport(true); setMobileSheet(null); }}
                  data-testid="planning-mobile-finance-report-open"
                >
                  ₹ Open Finance Report
                </Button>
              </section>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* R159o · Feb 2026 — Card size settings dialog */}
      {showCardSizeDialog && (
        <Dialog open onOpenChange={(v) => !v && setShowCardSizeDialog(false)}>
          <DialogContent className="max-w-md rounded-none" data-testid="planning-card-size-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-base"><SlidersHorizontal className="w-4 h-4" /> Default card size</DialogTitle>
              <DialogDescription>Applied by Tidy Up and to newly-added projects. Existing cards keep their own size unless you Tidy Up.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <label className="text-xs uppercase tracking-widest text-muted-foreground block mb-1">Width (px)</label>
                <div className="flex items-center gap-3">
                  <input
                    type="range" min="260" max="720" step="10"
                    value={defaultCardSize.w}
                    onChange={(e) => setDefaultCardSize((s) => ({ ...s, w: Number(e.target.value) }))}
                    className="flex-1"
                    data-testid="planning-card-size-w-range"
                  />
                  <input
                    type="number" min="260" max="720" step="10"
                    value={defaultCardSize.w}
                    onChange={(e) => setDefaultCardSize((s) => ({ ...s, w: Math.max(260, Math.min(720, Number(e.target.value) || 260)) }))}
                    className="w-20 border border-border px-2 py-1 rounded-none text-sm"
                    data-testid="planning-card-size-w-input"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-muted-foreground block mb-1">Height (px)</label>
                <div className="flex items-center gap-3">
                  <input
                    type="range" min="180" max="720" step="10"
                    value={defaultCardSize.h}
                    onChange={(e) => setDefaultCardSize((s) => ({ ...s, h: Number(e.target.value) }))}
                    className="flex-1"
                    data-testid="planning-card-size-h-range"
                  />
                  <input
                    type="number" min="180" max="720" step="10"
                    value={defaultCardSize.h}
                    onChange={(e) => setDefaultCardSize((s) => ({ ...s, h: Math.max(180, Math.min(720, Number(e.target.value) || 180)) }))}
                    className="w-20 border border-border px-2 py-1 rounded-none text-sm"
                    data-testid="planning-card-size-h-input"
                  />
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Tip · When Tidy Up asks for 1–3 columns, cards auto-widen to fill the canvas so choosing 3 across on a 27&quot; monitor yields ~600 px wide cards regardless of this default.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-none" onClick={() => { setDefaultCardSize({ w: DEFAULT_CARD_W, h: DEFAULT_CARD_H }); }} data-testid="planning-card-size-reset">Reset</Button>
              <Button className="rounded-none" onClick={() => setShowCardSizeDialog(false)} data-testid="planning-card-size-save">Done</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {templatesDialogOpen && (
        <StatusTemplatesDialog
          open={templatesDialogOpen}
          onClose={() => { setTemplatesDialogOpen(false); loadTemplates(); }}
        />
      )}

      {/* R158c · Shared ProjectTasksSubTab full popup (all tasks for one project) */}
      {tasksModalProject && (
        <Dialog open onOpenChange={(v) => !v && setTasksModalProject(null)}>
          <DialogContent
            className="w-screen h-[100dvh] max-w-none max-h-none p-0 overflow-hidden rounded-none flex flex-col sm:!rounded-none top-0 left-0 translate-x-0 translate-y-0"
            data-testid="planning-all-tasks-dialog"
          >
            <DialogHeader className="px-4 py-3 border-b border-border shrink-0 flex flex-row items-center gap-2 sticky top-0 bg-background z-10" style={{ paddingTop: "max(0.75rem, env(safe-area-inset-top))" }}>
              <DialogTitle className="text-sm flex-1 min-w-0 truncate">All tasks · {tasksModalProject.name}</DialogTitle>
              <DialogDescription className="sr-only">Full task management view for this project.</DialogDescription>
              <button
                type="button"
                onClick={() => setTasksModalProject(null)}
                className="p-2 -mr-2 hover:bg-muted rounded shrink-0"
                title="Close"
                aria-label="Close"
                data-testid="planning-all-tasks-close"
              >
                <X className="w-5 h-5" />
              </button>
            </DialogHeader>
            <div className="flex-1 overflow-auto" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
              <ProjectTasksSubTab
                projectId={tasksModalProject.id}
                project={tasksModalProject}
              />
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* R158f · Project Status Updates dialog — reuses the ProjectStatusUpdatesTab
          from the Projects list quick-view, so the composer + feed + reactions
          match everywhere. */}
      {updatesModalProject && (
        <Dialog open onOpenChange={(v) => !v && setUpdatesModalProject(null)}>
          <DialogContent
            /* R159m · Feb 2026 — Explicit z-[130] so this dialog stays
               above the Present overlay (z-[100]) when the user opens
               Project Status from inside Present mode. */
            className="max-w-3xl rounded-none max-h-[85vh] overflow-y-auto z-[130]"
            data-testid="planning-status-updates-dialog"
          >
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between">
                <span className="inline-flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" /> {updatesModalProject.name} — updates
                </span>
                <button
                  type="button"
                  onClick={() => window.open(`/app/projects/${updatesModalProject.id}`, "_blank")}
                  className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                  data-testid={`planning-status-updates-open-${updatesModalProject.id}`}
                >
                  Open project <ExternalLink className="w-3 h-3" />
                </button>
              </DialogTitle>
              <DialogDescription className="sr-only">Post and view status updates for this project.</DialogDescription>
            </DialogHeader>
            <ProjectStatusUpdatesTab
              projectId={updatesModalProject.id}
              project={updatesModalProject}
              projectMembers={[]}
              onPosted={async () => {
                // R159t · Feb 2026 — Surgical refresh. Instead of
                // reloading the whole board, refetch just the affected
                // project doc and splice it into local state. Keeps
                // scroll position, snooze, and every other card intact.
                try {
                  const { data: fresh } = await api.get(`/projects/${updatesModalProject.id}`);
                  if (fresh?.id) patchProjectLocal(fresh.id, fresh);
                } catch { /* silent — dialog stays open anyway */ }
              }}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* R159v · Feb 2026 — Floating Jump Navigator. Bottom-center pill
          with recorder-style controls (First · Prev · N/M · Next · Last)
          so the user can walk through every overdue card without
          scrolling back up to the toolbar. Auto-hides when there are 0
          overdue tasks, when the user explicitly dismisses it, or when
          the Overdue chip has been intentionally turned off from the
          toolbar. */}
      {overdueSummary.orderedIds.length > 0 && !navigatorDismissed && (
        <FloatingJumpNavigator
          total={overdueSummary.orderedIds.length}
          currentIdx={currentJumpIdx}
          onFirst={() => scrollToOverdueIndex(0)}
          onPrev={() => scrollToOverdueIndex((currentJumpIdx ?? 0) - 1)}
          onNext={() => scrollToOverdueIndex((currentJumpIdx ?? -1) + 1)}
          onLast={() => scrollToOverdueIndex(overdueSummary.orderedIds.length - 1)}
          onDismiss={() => setNavigatorDismissed(true)}
        />
      )}
    </>
  );
}

// ══════════════════════════ Sub-components ══════════════════════════

// R159v · Feb 2026 — Floating Jump Navigator. Fixed-position bottom-center
// pill with recorder-style controls (⏮ First · ◀ Prev · N/M · ▶ Next ·
// ⏭ Last · ✕ Close). Lets the user step through the ordered list of
// overdue projects without scrolling back to the toolbar. Renders only
// when there is at least one overdue project and the user hasn't
// dismissed it via ✕.
function FloatingJumpNavigator({ total, currentIdx, onFirst, onPrev, onNext, onLast, onDismiss }) {
  const displayIdx = currentIdx == null ? "–" : currentIdx + 1;
  const atFirst = currentIdx === 0;
  const atLast = currentIdx === total - 1;
  // R159oo · Feb 2026 — Polished pill: single dark surface, thin inner
  // separators (no per-button borders), tighter footprint on mobile.
  // Positioned bottom-right on mobile so the ScreenshotReporter camera
  // (bottom-left) never overlaps; recenters on md+.
  const btnBase = "h-7 w-7 flex items-center justify-center text-white/90 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors";
  return (
    <div
      className="fixed bottom-4 right-4 md:right-auto md:left-1/2 md:-translate-x-1/2 z-50 flex items-center bg-neutral-900 text-white rounded-full shadow-2xl ring-1 ring-white/10 pl-2 pr-1 py-0.5"
      role="toolbar"
      aria-label="Overdue project navigator"
      data-testid="planning-jump-navigator"
    >
      <span className="inline-flex items-center gap-1 pr-2 text-[10px] uppercase tracking-widest text-rose-300 font-semibold">
        <span aria-hidden>🔥</span> <span className="hidden sm:inline">Overdue</span>
      </span>
      <button
        type="button"
        onClick={onFirst}
        disabled={atFirst || total === 0}
        className={btnBase}
        title="Jump to first overdue project"
        data-testid="planning-jump-first"
      >
        <SkipBack className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        onClick={onPrev}
        disabled={total === 0}
        className={btnBase}
        title="Previous overdue project"
        data-testid="planning-jump-prev"
      >
        <ChevronLeft className="w-3.5 h-3.5" />
      </button>
      <span
        className="px-2 h-7 flex items-center text-[11px] tabular-nums font-mono text-white/90 min-w-[52px] justify-center"
        title={`Currently on ${displayIdx} of ${total}`}
        data-testid="planning-jump-counter"
      >
        {displayIdx} <span className="opacity-40 mx-1">/</span> {total}
      </span>
      <button
        type="button"
        onClick={onNext}
        disabled={total === 0}
        className={btnBase}
        title="Next overdue project"
        data-testid="planning-jump-next"
      >
        <ChevronRight className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        onClick={onLast}
        disabled={atLast || total === 0}
        className={btnBase}
        title="Jump to last overdue project"
        data-testid="planning-jump-last"
      >
        <SkipForward className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        onClick={onDismiss}
        className="h-7 w-7 flex items-center justify-center text-white/60 hover:bg-white/10 hover:text-white transition-colors ml-0.5"
        title="Hide the navigator (returns automatically when overdue list changes)"
        data-testid="planning-jump-dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

// R159w · Feb 2026 — Accounts Review strip. Compact per-card block that
// renders the last billing_note preview (2 lines · author · relative
// date) with an age heatmap tint (green <3d · amber 3-7d · red >7d).
// Clicking the strip opens the existing BillingNotesDialog at project
// scope. When there's no note yet, we render an inviting empty state
// so the reviewer can seed the first thread. The mark-reviewed pin
// only shows when the caller flagged this card as `needsReview`.
function _relativeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "";
  const diffMs = Date.now() - then;
  const s = Math.max(0, Math.floor(diffMs / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
}

function _ageBucket(iso) {
  if (!iso) return "none";
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "none";
  const days = (Date.now() - then) / (1000 * 60 * 60 * 24);
  if (days < 3) return "fresh";
  if (days < 7) return "warm";
  return "stale";
}

function AccountsReviewStrip({ projectId, projectName, note, needsReview, taskNotes, onOpenThread, onMarkReviewed, onInlineReply, onOpenTaskNote, projectFinanceRollup, mentionCandidates }) {
  const [showTaskNotes, setShowTaskNotes] = useState(false);
  const [replyDraft, setReplyDraft] = useState("");
  const [replyBusy, setReplyBusy] = useState(false);
  const submitReply = async (e) => {
    e?.preventDefault?.();
    if (!replyDraft.trim() || !onInlineReply) return;
    setReplyBusy(true);
    const ok = await onInlineReply(projectId, replyDraft.trim());
    setReplyBusy(false);
    if (ok) setReplyDraft("");
  };
  const taskNoteCount = (taskNotes || []).length;
  const financeCurrencies = Object.keys(projectFinanceRollup || {});
  const financeBlock = financeCurrencies.length > 0 && (
    <div className="px-3 py-1 border-b border-border bg-white/70 flex items-center gap-2 flex-wrap text-[10px]" data-testid={`planning-card-finance-rollup-${projectId}`}>
      <span className="uppercase tracking-widest text-muted-foreground shrink-0">Budget</span>
      {financeCurrencies.map((c) => {
        const v = projectFinanceRollup[c];
        return (
          <span key={c} className="inline-flex items-center gap-1 border border-amber-300 bg-amber-50 px-1.5 py-0.5 font-mono">
            <span className="font-semibold text-amber-800">{c}</span>
            <span>{fmtMoney(v.est, c)}</span>
            <span className="opacity-40">·</span>
            <span>{fmtMoney(v.paid, c)}</span>
            <span className="opacity-40">·</span>
            <span className={v.balance > 0 ? "text-rose-700 font-semibold" : v.balance < 0 ? "text-emerald-700 font-semibold" : ""}>{fmtMoney(v.balance, c)}</span>
          </span>
        );
      })}
    </div>
  );
  if (!note || !note.created_at) {
    return (
      <div className="border-b border-border bg-neutral-50" data-testid={`planning-card-accounts-strip-${projectId}`}>
        {financeBlock}
        <button
          type="button"
          onClick={onOpenThread}
          className="w-full px-3 py-1.5 hover:bg-neutral-100 text-left text-[11px] text-muted-foreground flex items-center gap-2"
          title={`No accounts notes yet on ${projectName || "this project"} — click to add the first one`}
          data-testid={`planning-card-accounts-open-${projectId}`}
        >
          <span className="inline-flex items-center justify-center w-4 h-4 border border-neutral-300 bg-white text-neutral-400 font-mono text-[9px]">$</span>
          <span className="italic">No accounts notes yet · click to add</span>
          {taskNoteCount > 0 && (
            <span className="ml-auto px-1 border border-amber-300 bg-amber-50 text-amber-700 text-[9px] uppercase tracking-widest">{taskNoteCount} task notes</span>
          )}
        </button>
        {onInlineReply && (
          <InlineReplyRow
            projectId={projectId}
            value={replyDraft}
            onChange={setReplyDraft}
            onSubmit={submitReply}
            busy={replyBusy}
            mentionCandidates={mentionCandidates}
          />
        )}
      </div>
    );
  }
  const bucket = _ageBucket(note.created_at);
  const tint = bucket === "fresh"
    ? "border-emerald-300 bg-emerald-50/70"
    : bucket === "warm"
      ? "border-amber-300 bg-amber-50/70"
      : "border-rose-300 bg-rose-50/70";
  const dot = bucket === "fresh"
    ? "bg-emerald-500"
    : bucket === "warm" ? "bg-amber-500" : "bg-rose-500";
  const bodyText = String(note.body || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  return (
    <div className="border-b border-border" data-testid={`planning-card-accounts-strip-${projectId}`}>
      {financeBlock}
      <div className={`px-3 py-1.5 ${tint} text-[11px] flex items-start gap-2`}>
        <span className={`inline-block w-1.5 h-1.5 rounded-full ${dot} mt-1.5 shrink-0`} title={bucket === "fresh" ? "Fresh (< 3 days)" : bucket === "warm" ? "Aging (3-7 days)" : "Stale (> 7 days)"} />
        <button
          type="button"
          onClick={onOpenThread}
          className="flex-1 text-left min-w-0"
          title={`Open the accounts thread for ${projectName || "this project"}`}
          data-testid={`planning-card-accounts-open-${projectId}`}
        >
          <div className="line-clamp-2 text-foreground/90 leading-snug">{bodyText}</div>
          <div className="mt-0.5 text-[10px] text-muted-foreground flex items-center gap-1 flex-wrap">
            <span className="font-medium text-foreground/70">{note.author_name || "—"}</span>
            <span className="opacity-40">·</span>
            <span title={note.created_at}>{_relativeAgo(note.created_at)}</span>
            {note.count > 1 && (
              <>
                <span className="opacity-40">·</span>
                <span>{note.count} notes</span>
              </>
            )}
            {needsReview && (
              <span className="ml-1 px-1 border border-amber-400 bg-amber-500 text-white uppercase tracking-widest text-[9px]" data-testid={`planning-card-accounts-needsreview-${projectId}`}>needs review</span>
            )}
          </div>
        </button>
        <div className="flex flex-col items-end gap-1 shrink-0">
          {taskNoteCount > 0 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setShowTaskNotes((v) => !v); }}
              className={`px-1.5 h-6 border text-[10px] uppercase tracking-widest transition-colors ${showTaskNotes ? "bg-amber-500 text-white border-amber-600" : "border-amber-400 bg-white text-amber-700 hover:bg-amber-50"}`}
              title={`${taskNoteCount} task-level accounts thread${taskNoteCount === 1 ? "" : "s"} — click to peek`}
              data-testid={`planning-card-accounts-tasknotes-${projectId}`}
            >
              {taskNoteCount} task note{taskNoteCount === 1 ? "" : "s"}
            </button>
          )}
          {needsReview && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onMarkReviewed && onMarkReviewed(); }}
              className="h-6 px-1.5 border border-amber-500 bg-white text-amber-700 hover:bg-amber-100 text-[10px] uppercase tracking-widest"
              title="Mark this thread as reviewed — hides it from the Needs-Review queue"
              data-testid={`planning-card-accounts-markreviewed-${projectId}`}
            >
              <CheckCircle2 className="w-3 h-3 inline -mt-0.5" /> Reviewed
            </button>
          )}
        </div>
      </div>
      {showTaskNotes && taskNoteCount > 0 && (
        <div className="border-t border-border bg-white/70 px-3 py-1.5 space-y-1" data-testid={`planning-card-accounts-tasknotes-list-${projectId}`}>
          {taskNotes.slice(0, 5).map((tn) => {
            const tb = _ageBucket(tn.created_at);
            const tDot = tb === "fresh" ? "bg-emerald-500" : tb === "warm" ? "bg-amber-500" : "bg-rose-500";
            const tBody = String(tn.body || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
            return (
              <button
                key={tn.task_id}
                type="button"
                onClick={(e) => { e.stopPropagation(); onOpenTaskNote && onOpenTaskNote(tn.task_id); }}
                className="w-full text-left flex items-start gap-2 hover:bg-amber-50 px-1 py-1 text-[11px]"
                title="Open this task to view / reply to its accounts thread"
                data-testid={`planning-card-accounts-tasknote-${tn.task_id}`}
              >
                <span className={`inline-block w-1.5 h-1.5 rounded-full ${tDot} mt-1.5 shrink-0`} />
                <span className="flex-1 min-w-0">
                  <span className="block truncate font-medium text-foreground/90">{tn.task_name}</span>
                  <span className="block line-clamp-1 text-muted-foreground">{tBody}</span>
                  <span className="block text-[9px] text-muted-foreground/80 uppercase tracking-widest">{tn.author_name || "—"} · {_relativeAgo(tn.created_at)}{tn.count > 1 ? ` · ${tn.count}` : ""}</span>
                </span>
              </button>
            );
          })}
        </div>
      )}
      {onInlineReply && (
        <InlineReplyRow
          projectId={projectId}
          value={replyDraft}
          onChange={setReplyDraft}
          onSubmit={submitReply}
          busy={replyBusy}
          mentionCandidates={mentionCandidates}
        />
      )}
    </div>
  );
}

// R159x · Feb 2026 — Tiny inline reply row rendered under every
// accounts strip. Cmd/Ctrl+Enter also submits. Empty state renders the
// input in a subdued style so it doesn't fight for attention.
// R159y · Feb 2026 — @-mention: typing `@foo` after a space (or at the
// start) shows up to 6 matching workspace members. Enter/click inserts
// `@<name>` in the input and posts a notify via the parent onSubmit
// path (server-side notify hook picks up @<name> tokens in the note
// body). Escape closes the popover.
function InlineReplyRow({ projectId, value, onChange, onSubmit, busy, mentionCandidates }) {
  const [caret, setCaret] = useState(null);
  const query = useMemo(() => {
    if (caret == null) return null;
    const upto = String(value || "").slice(0, caret);
    const m = /(^|\s)@([\w.-]{0,24})$/.exec(upto);
    return m ? { term: m[2], start: caret - m[2].length - 1 } : null;
  }, [value, caret]);
  const suggestions = useMemo(() => {
    if (!query) return [];
    const t = query.term.toLowerCase();
    return (mentionCandidates || [])
      .filter((m) => (m.name || m.email || "").toLowerCase().includes(t))
      .slice(0, 6);
  }, [query, mentionCandidates]);
  const applyMention = (m) => {
    if (!query) return;
    const tag = (m.name || m.email || "").replace(/\s+/g, "_");
    const before = String(value || "").slice(0, query.start);
    const after = String(value || "").slice(caret ?? value.length);
    onChange(`${before}@${tag} ${after}`);
    setCaret(null);
  };
  return (
    <form
      onSubmit={onSubmit}
      className="flex items-start gap-1 border-t border-border/60 bg-neutral-50/70 px-2 py-1 relative"
      data-testid={`planning-card-accounts-reply-form-${projectId}`}
    >
      <div className="flex-1 min-w-0 relative">
        <input
          type="text"
          value={value}
          onChange={(e) => { onChange(e.target.value); setCaret(e.target.selectionStart); }}
          onKeyUp={(e) => setCaret(e.target.selectionStart)}
          onClick={(e) => setCaret(e.target.selectionStart)}
          onKeyDown={(e) => {
            if (e.key === "Escape") { setCaret(null); return; }
            if (e.key === "Enter" && suggestions.length > 0 && query) {
              e.preventDefault();
              applyMention(suggestions[0]);
              return;
            }
            if ((e.key === "Enter") && (e.metaKey || e.ctrlKey || !e.shiftKey)) {
              e.preventDefault();
              onSubmit(e);
            }
          }}
          placeholder="Add accounts note…  @mention · ↵ to send"
          disabled={busy}
          className="w-full h-6 px-2 border border-neutral-200 bg-white text-[11px] focus:outline-none focus:border-amber-400"
          data-testid={`planning-card-accounts-reply-input-${projectId}`}
        />
        {query && suggestions.length > 0 && (
          <div
            className="absolute bottom-full left-0 mb-1 z-10 min-w-[180px] max-w-[280px] border border-amber-300 bg-white shadow-lg"
            data-testid={`planning-card-accounts-reply-mentions-${projectId}`}
          >
            {suggestions.map((s) => (
              <button
                key={s.user_id || s.id || s.email}
                type="button"
                onClick={(e) => { e.preventDefault(); applyMention(s); }}
                className="w-full text-left px-2 py-1 text-[11px] hover:bg-amber-50 border-b border-border last:border-b-0"
                data-testid={`planning-card-accounts-reply-mention-${s.user_id || s.id || s.email}`}
              >
                <span className="font-medium">@{(s.name || s.email || "").replace(/\s+/g, "_")}</span>
                <span className="opacity-60 ml-1 text-[10px]">{s.email}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      <button
        type="submit"
        disabled={busy || !value.trim()}
        className="h-6 px-2 border border-amber-500 bg-amber-500 text-white text-[10px] uppercase tracking-widest disabled:opacity-40 disabled:cursor-not-allowed hover:bg-amber-600"
        data-testid={`planning-card-accounts-reply-submit-${projectId}`}
      >
        {busy ? "…" : "Send"}
      </button>
    </form>
  );
}



function ZoomToggle({ zoom, onChange }) {
  const idx = ZOOM_ORDER.indexOf(zoom);
  const canOut = idx > 0;
  const canIn = idx < ZOOM_ORDER.length - 1;
  return (
    <div className="inline-flex items-center border border-border rounded-none">
      <button
        onClick={() => canOut && onChange(ZOOM_ORDER[idx - 1])}
        disabled={!canOut}
        className="px-1.5 py-1 text-neutral-700 hover:bg-muted disabled:opacity-30"
        title="Zoom out"
        data-testid="planning-zoom-out"
      >
        <ZoomOut className="w-3.5 h-3.5" />
      </button>
      <span className="text-[10px] font-mono tabular-nums px-2 border-l border-r border-border" data-testid="planning-zoom-level">
        {Math.round((ZOOM[zoom] || 1) * 100)}%
      </span>
      <button
        onClick={() => canIn && onChange(ZOOM_ORDER[idx + 1])}
        disabled={!canIn}
        className="px-1.5 py-1 text-neutral-700 hover:bg-muted disabled:opacity-30"
        title="Zoom in"
        data-testid="planning-zoom-in"
      >
        <ZoomIn className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

// R158h · Feb 2026 — Tidy up popover: asks user for a sort dimension
// + column count, then re-flows the canvas into a clean grid.
// R158n · Feb 2026 — Added "Group into swimlanes" checkbox so tidy-up
// can visually separate cards by the same dimension.
function TidyUpButton({ onApply }) {
  const [open, setOpen] = useState(false);
  const [sortBy, setSortBy] = useState("client");
  const [cols, setCols] = useState(4);
  const [groupBy, setGroupBy] = useState(false);
  const apply = () => {
    onApply(sortBy, cols, groupBy);
    setOpen(false);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="sm" variant="ghost" className="rounded-none h-8 text-xs" data-testid="planning-tidy-up-btn">
          <Wand2 className="w-3.5 h-3.5 mr-1" /> Tidy up
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3 rounded-none space-y-2" align="end">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Sort by</div>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="w-full h-8 text-sm border border-border px-2 rounded-none"
          data-testid="planning-tidy-sort-by"
        >
          <option value="client">Client</option>
          <option value="brand">Brand</option>
          <option value="name">Project name</option>
          <option value="pm">Project Manager</option>
          <option value="status">Project status</option>
          <option value="health">Health</option>
        </select>
        <label className="flex items-center gap-2 text-xs cursor-pointer select-none" data-testid="planning-tidy-group-label">
          <input
            type="checkbox"
            checked={groupBy}
            onChange={(e) => setGroupBy(e.target.checked)}
            className="w-3 h-3"
            data-testid="planning-tidy-group-checkbox"
          />
          Group into swimlanes by selected sort
        </label>
        {/* R159p · Feb 2026 — Cols slider is now always visible. When
            "Group into swimlanes" is checked, the slider controls the
            number of cards per lane; otherwise it's the grid width. */}
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground pt-1">
          {groupBy ? "Cards per lane" : "Grid columns"}
        </div>
        <div className="flex items-center gap-2">
          <input
            type="range" min="1" max="8" step="1"
            value={cols}
            onChange={(e) => setCols(parseInt(e.target.value, 10))}
            className="flex-1"
            data-testid="planning-tidy-cols-slider"
          />
          <span className="w-6 text-center text-sm font-mono" data-testid="planning-tidy-cols-val">{cols}</span>
        </div>
        <Button
          size="sm" className="w-full rounded-none h-8 text-xs mt-1" onClick={apply}
          data-testid="planning-tidy-apply"
        >
          <Wand2 className="w-3 h-3 mr-1" /> Tidy up now
        </Button>
      </PopoverContent>
    </Popover>
  );
}

function BoardPicker({ boards, board, onSelect, onNew }) {  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" className="rounded-none h-8 text-xs" data-testid="planning-board-picker">
          <LayoutGrid className="w-3.5 h-3.5 mr-1" />
          {board?.name || "Ephemeral board"}
          <ChevronDown className="w-3 h-3 ml-1" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-1 rounded-none" align="end">
        <div className="max-h-72 overflow-y-auto">
          {boards.map((b) => (
            <button
              key={b.id}
              onClick={() => { onSelect(b); setOpen(false); }}
              className={`w-full text-left px-2 py-1.5 hover:bg-muted text-xs flex items-center gap-2 ${board?.id === b.id ? "bg-primary/10 text-primary font-medium" : ""}`}
              data-testid={`planning-board-option-${b.id}`}
            >
              <LayoutGrid className="w-3 h-3 opacity-60" />
              <span className="flex-1 truncate">{b.name}</span>
              {b.is_shared && <span className="text-[9px] uppercase tracking-widest text-emerald-600">shared</span>}
            </button>
          ))}
          {boards.length === 0 && <div className="px-2 py-3 text-xs text-muted-foreground italic">No saved boards yet.</div>}
        </div>
        <div className="border-t border-border pt-1">
          <button onClick={() => { onNew(); setOpen(false); }} className="w-full text-left px-2 py-1.5 hover:bg-muted text-xs flex items-center gap-2 text-primary" data-testid="planning-new-board">
            <Plus className="w-3 h-3" /> Save current view as new board…
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function NewBoardDialog({ onClose, onCreate }) {
  const [name, setName] = useState("");
  const [shared, setShared] = useState(false);
  const [startBlank, setStartBlank] = useState(true);
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="planning-new-board-dialog">
        <DialogHeader>
          <DialogTitle>Save board</DialogTitle>
          <DialogDescription className="text-xs">
            Save a new planning view. Start blank to build from scratch, or capture your current
            layout, filters and sticky notes.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Weekly planning · Sprint 42" autoFocus data-testid="planning-new-board-name" />
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={startBlank} onChange={(e) => setStartBlank(e.target.checked)} data-testid="planning-new-board-blank" />
            <span>
              Start blank <span className="text-muted-foreground">— empty canvas, no filters. Any change auto-saves.</span>
            </span>
          </label>
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={shared} onChange={(e) => setShared(e.target.checked)} data-testid="planning-new-board-shared" />
            Share with workspace (anyone can open + edit)
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={() => name.trim() && onCreate(name.trim(), shared, startBlank)} disabled={!name.trim()} data-testid="planning-new-board-save"><Save className="w-3 h-3 mr-1" /> Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// R158k · Feb 2026 — Priority-then-due sort applied by every card so
// urgent items float to the top before due-soon items.
const PRIORITY_RANK = { urgent: 0, high: 1, medium: 2, low: 3 };
function priorityDueSort(a, b) {
  const pa = PRIORITY_RANK[a.priority] ?? 2;
  const pb = PRIORITY_RANK[b.priority] ?? 2;
  if (pa !== pb) return pa - pb;
  // Same priority → earliest due first, undated last
  if (!a.due_date && !b.due_date) return 0;
  if (!a.due_date) return 1;
  if (!b.due_date) return -1;
  return a.due_date.localeCompare(b.due_date);
}

// Task filter helper used inside every project card.
// Iter · Feb 2026 — Sort helper that puts due-date FIRST, priority second.
// Users asked for a straight "sorted by due date" experience on the
// default `all_open` view: earliest due first, undated tasks at the
// bottom, ties broken by priority so urgent floats above medium.
function dueThenPrioritySort(a, b) {
  const ad = a.due_date ? String(a.due_date).slice(0, 10) : null;
  const bd = b.due_date ? String(b.due_date).slice(0, 10) : null;
  if (ad && bd && ad !== bd) return ad.localeCompare(bd);
  if (!ad && bd) return 1;
  if (ad && !bd) return -1;
  const pa = PRIORITY_RANK[a.priority] ?? 2;
  const pb = PRIORITY_RANK[b.priority] ?? 2;
  return pa - pb;
}

function filterTasks(tasks, mode, currentUserId) {
  if (!tasks) return [];
  let out = tasks;
  if (mode === "mine") out = tasks.filter((t) => t.assignee_id === currentUserId && t.status !== "done");
  else if (mode === "this_week") {
    const now = new Date();
    const in7 = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    out = tasks.filter((t) => t.due_date && new Date(t.due_date) <= in7 && t.status !== "done");
  }
  else if (mode === "all_open") {
    // R159zz · Feb 2026 — Every non-done task regardless of due date.
    // Cancelled tasks are also excluded so the count reflects live work.
    out = tasks.filter((t) => t.status !== "done" && t.status !== "cancelled");
  }
  else if (mode !== "all") {
    // open_by_due (default): not done
    out = tasks.filter((t) => t.status !== "done");
  }
  // Iter · Feb 2026 — `all_open` (the new default) uses a due-date-first
  // sort so users literally see the board "sorted by due date". Other
  // modes retain the older priority-first heuristic.
  if (mode === "all_open") {
    out = [...out].sort(dueThenPrioritySort);
  } else if (mode !== "all") {
    out = [...out].sort(priorityDueSort);
  }
  return out;
}

// R159g · Feb 2026 — Quick due-date chip. Narrows a task list to
// overdue / due-today / nearing (next 3 days) after the per-card
// task_filter has already run. Returns the input untouched when the
// chip is "all". `dueOf` extracts the YYYY-MM-DD portion so the check
// is timezone-agnostic (server stores dates as ISO strings).
function applyQuickDueFilter(tasks, chip) {
  if (!tasks || tasks.length === 0 || chip === "all") return tasks;
  const today = new Date();
  const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const todayStr = ymd(today);
  const in3 = new Date(today.getTime() + 3 * 24 * 60 * 60 * 1000);
  const in3Str = ymd(in3);
  return tasks.filter((t) => {
    if (t.status === "done") return false;
    if (!t.due_date) return false;
    const d = String(t.due_date).slice(0, 10);
    if (chip === "overdue") return d < todayStr;
    if (chip === "today") return d === todayStr;
    if (chip === "nearing") return d > todayStr && d <= in3Str;
    return true;
  });
}

// R159u · Feb 2026 — "Unbilled" heuristic. A project card is considered
// to have outstanding billable work when at least one of its tasks is
// (a) billable but not yet billed, OR (b) billed but not yet paid.
// Passing an empty / null list returns false so cards without any tasks
// are hidden from the filter (no work = nothing to invoice).
function hasUnbilledWork(tasks) {
  if (!tasks || tasks.length === 0) return false;
  return tasks.some((t) => (t.billable && !t.billed) || (t.billed && !t.paid));
}

function FiltersBar({ filters, onChange, onLocalSearchChange, options, currentUserId, searchMatchCount = 0, searchIdx = 0, onSearchNext, onSearchPrev, onResetAll, hasOtherFilters = false, templatesSlot = null, presetsListSlot = null, presetsSaveRenderer = null, extraStateForPreset = null }) {
  const [expanded, setExpanded] = useState(false);
  // Iter · Feb 2026 — Stage all pill filter changes locally instead of
  // firing `onChange` on every checkbox toggle. A global "Apply" bar
  // shows the pending-count in amber whenever `staged` diverges from
  // the committed `filters`; clicking Apply commits, Discard reverts.
  // The free-text search (`q`) is still live so we keep highlight-only
  // scrolling behaviour identical.
  const [staged, setStaged] = useState(filters);
  useEffect(() => { setStaged(filters); }, [filters]);
  const PILL_KEYS = ["owner_ids", "statuses", "healths", "client_ids", "brand_ids", "department_ids", "project_ids", "priorities", "task_assignee_ids", "task_statuses", "task_due_from", "task_due_to"];
  const pendingChanges = useMemo(() => {
    const norm = (v) => Array.isArray(v) ? [...v].sort().join(",") : String(v || "");
    return PILL_KEYS.filter((k) => norm(staged[k]) !== norm(filters[k])).length;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staged, filters]);
  const active = Object.entries(staged).filter(([k, v]) => k !== "q" && Array.isArray(v) && v.length > 0).length;
  // `setF` now writes to the staged buffer for PILL_KEYS. The search
  // query (`q`) still fires straight through so the highlight cursor
  // updates as you type — matches the previous behaviour.
  const setF = (k, v) => {
    if (k === "q") { onChange({ ...filters, q: v }); return; }
    setStaged((prev) => ({ ...prev, [k]: v }));
  };
  const applyStaged = () => { onChange(staged); };
  const discardStaged = () => { setStaged(filters); };
  const q = filters.q || "";
  const hasQuery = q.trim().length > 0;
  return (
    <div className="border-b border-border bg-background sticky top-0 z-30" data-testid="planning-filters">
      {/* Iter · Feb 2026 — Apply / Discard bar. Turns amber the moment
          the staged pill filters diverge from the committed set so
          PMs can queue up multiple selections without triggering a
          refetch after every checkbox toggle. */}
      {pendingChanges > 0 && (
        <div className="flex items-center gap-2 px-4 py-1.5 bg-amber-50 border-b border-amber-200" data-testid="planning-filters-apply-bar">
          <span className="text-[11px] uppercase tracking-widest text-amber-900 font-semibold">
            {pendingChanges} filter change{pendingChanges === 1 ? "" : "s"} pending
          </span>
          {presetsSaveRenderer && presetsSaveRenderer({
            filters: staged,
            ...(extraStateForPreset || {}),
          })}
          <button
            type="button"
            onClick={applyStaged}
            className="ml-auto text-xs px-3 h-7 border border-amber-600 bg-amber-600 text-white hover:bg-amber-700 rounded-none font-semibold"
            data-testid="planning-filters-apply"
          >
            Apply changes
          </button>
          <button
            type="button"
            onClick={discardStaged}
            className="text-xs px-2 h-7 border border-amber-300 bg-transparent text-amber-800 hover:bg-amber-100 rounded-none"
            data-testid="planning-filters-discard"
          >
            Discard
          </button>
        </div>
      )}
      {/* Iter · Feb 2026 — Always-visible preset chips (personal +
          workspace). Renders NOTHING when the user hasn't saved any
          yet; the "Save as preset" chip inside the amber Apply bar
          is the on-ramp. */}
      {presetsListSlot && (
        <div className="px-4 py-1 border-b border-border/60 bg-muted/20 overflow-x-auto">
          {presetsListSlot}
        </div>
      )}
      <div className="flex items-center gap-2 px-4 py-2 flex-wrap">
        {/* R159yy · Feb 2026 — Highlight-only search. Doesn't hide cards;
            just scrolls to the first match. Counter + prev/next arrows
            appear once matches are known. X clears the query.
            Enter → next match, Shift+Enter → previous match. */}
        <div className="relative flex items-center max-w-xs h-8 border border-input focus-within:border-primary bg-background">
          <Search className="w-3.5 h-3.5 text-muted-foreground ml-2 shrink-0" />
          <input
            type="text"
            value={q}
            onChange={(e) => onLocalSearchChange ? onLocalSearchChange(e.target.value) : onChange({ ...filters, q: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === "Enter" && searchMatchCount > 0) {
                e.preventDefault();
                if (e.shiftKey) onSearchPrev && onSearchPrev();
                else onSearchNext && onSearchNext();
              } else if (e.key === "Escape") {
                (onLocalSearchChange || (() => onChange({ ...filters, q: "" })))("");
              }
            }}
            placeholder="Search projects (highlights)…"
            className="h-full flex-1 min-w-0 px-2 text-sm bg-transparent outline-none"
            data-testid="planning-filter-q"
          />
          {hasQuery && searchMatchCount > 0 && (
            <span className="text-[10px] font-mono px-1.5 text-muted-foreground shrink-0" data-testid="planning-search-counter">
              {searchIdx + 1}/{searchMatchCount}
            </span>
          )}
          {hasQuery && searchMatchCount === 0 && (
            <span className="text-[10px] uppercase tracking-widest px-1.5 text-rose-500 shrink-0" data-testid="planning-search-no-match">
              0
            </span>
          )}
          {hasQuery && searchMatchCount > 1 && (
            <>
              <button type="button"
                      onClick={() => onSearchPrev && onSearchPrev()}
                      className="h-6 w-6 flex items-center justify-center hover:bg-muted text-muted-foreground hover:text-foreground shrink-0"
                      title="Previous match (Shift+Enter)"
                      data-testid="planning-search-prev">
                <ChevronUp className="w-3.5 h-3.5" />
              </button>
              <button type="button"
                      onClick={() => onSearchNext && onSearchNext()}
                      className="h-6 w-6 flex items-center justify-center hover:bg-muted text-muted-foreground hover:text-foreground shrink-0"
                      title="Next match (Enter)"
                      data-testid="planning-search-next">
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
            </>
          )}
          {hasQuery && (
            <button type="button"
                    onClick={() => (onLocalSearchChange || (() => onChange({ ...filters, q: "" })))("")}
                    className="h-6 w-6 flex items-center justify-center hover:bg-muted text-muted-foreground hover:text-foreground shrink-0 mr-1"
                    title="Clear search"
                    data-testid="planning-search-clear">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <MultiFilter label="PM" value={staged.owner_ids || []} onChange={(v) => setF("owner_ids", v)} options={options.users.map((u) => ({ v: u.id, l: u.name || u.email, hint: u.id === currentUserId ? "(you)" : "" }))} testid="planning-filter-owner" />
        <MultiFilter label="Status" value={staged.statuses || []} onChange={(v) => setF("statuses", v)} options={options.statuses.map((s) => ({ v: s, l: STATUS_META[s]?.label || s }))} testid="planning-filter-status" />
        <MultiFilter label="Health" value={staged.healths || []} onChange={(v) => setF("healths", v)} options={options.healths.map((h) => ({ v: h, l: h }))} testid="planning-filter-health" />
        <MultiFilter label="Client" value={staged.client_ids || []} onChange={(v) => setF("client_ids", v)} options={options.clients.map((c) => ({ v: c.id, l: c.name }))} testid="planning-filter-client" />
        <MultiFilter label="Brand" value={staged.brand_ids || []} onChange={(v) => setF("brand_ids", v)} options={options.brands.map((b) => ({ v: b.id, l: b.name }))} testid="planning-filter-brand" />
        <MultiFilter label="Department" value={staged.department_ids || []} onChange={(v) => setF("department_ids", v)} options={(options.departments || []).map((d) => ({ v: d.id, l: d.name }))} testid="planning-filter-department" />
        <MultiFilter label="Project" value={staged.project_ids || []} onChange={(v) => setF("project_ids", v)} options={(options.all_projects || []).map((p) => ({ v: p.id, l: p.name }))} testid="planning-filter-project" />
        <MultiFilter label="Priority" value={staged.priorities || []} onChange={(v) => setF("priorities", v)} options={options.priorities.map((p) => ({ v: p, l: p }))} testid="planning-filter-priority" />
        <button onClick={() => setExpanded((v) => !v)} className="text-xs px-2 h-8 border border-border hover:bg-muted inline-flex items-center gap-1" data-testid="planning-filters-expand">
          <Filter className="w-3 h-3" /> {expanded ? "Less" : "Task filters"}
        </button>
        {active > 0 && (
          <button onClick={() => { setStaged({}); onChange({}); }} className="text-xs px-2 h-8 border border-rose-300 text-rose-600 hover:bg-rose-50 inline-flex items-center gap-1" data-testid="planning-filters-clear">
            <X className="w-3 h-3" /> Clear ({active})
          </button>
        )}
        {/* R160ff · Feb 2026 — One-click "Reset filters" that also
            clears swimlane grouping, sticky-bar quick due, watched
            toggle, unbilled toggle, hide-empty toggle and search
            query. Visible whenever ANY narrower is active. */}
        {(active > 0 || (filters.q || "").trim() || hasOtherFilters) && (
          <button
            onClick={() => onResetAll && onResetAll()}
            className="text-xs px-2 h-8 border border-primary bg-primary text-primary-foreground hover:bg-primary/90 inline-flex items-center gap-1 font-semibold"
            title="Clear every filter, group, sticky chip and search — show all non-archived projects"
            data-testid="planning-filters-reset-all"
          >
            <RotateCcw className="w-3 h-3" /> Reset filters
          </button>
        )}
        {templatesSlot}
      </div>
      {expanded && (
        <div className="flex items-center gap-2 px-4 py-2 border-t border-border bg-muted/30 flex-wrap">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Task filters (applied inside every card):</span>
          <MultiFilter label="Assignee" value={staged.task_assignee_ids || []} onChange={(v) => setF("task_assignee_ids", v)} options={options.users.map((u) => ({ v: u.id, l: u.name || u.email }))} testid="planning-filter-task-assignee" />
          <MultiFilter label="Task status" value={staged.task_statuses || []} onChange={(v) => setF("task_statuses", v)} options={options.task_statuses.map((s) => ({ v: s, l: s }))} testid="planning-filter-task-status" />
          <div className="flex items-center gap-1">
            <span className="text-[10px] text-muted-foreground">Due:</span>
            <Input type="date" value={staged.task_due_from || ""} onChange={(e) => setF("task_due_from", e.target.value)} className="h-7 text-xs w-32" data-testid="planning-filter-task-from" />
            <span className="text-[10px] text-muted-foreground">→</span>
            <Input type="date" value={staged.task_due_to || ""} onChange={(e) => setF("task_due_to", e.target.value)} className="h-7 text-xs w-32" data-testid="planning-filter-task-to" />
          </div>
        </div>
      )}
    </div>
  );
}

function MultiFilter({ label, value, onChange, options, testid }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const sorted = useMemo(() => [...options].sort((a, b) => (a.l || "").localeCompare(b.l || "")), [options]);
  const filtered = sorted.filter((o) => !q || (o.l || "").toLowerCase().includes(q.toLowerCase()));
  const toggle = (v) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  // Iter · Feb 2026 — When a search query is active, "Select all" only
  // ticks the currently visible / matching rows and MERGES with the
  // existing selection instead of nuking it and picking every option.
  // "Unselect all" mirrors the same restraint — clears just the
  // matching rows when a search is active, everything otherwise.
  const searching = q.trim().length > 0;
  const filteredIds = filtered.map((o) => o.v);
  const allFilteredSelected = filtered.length > 0 && filteredIds.every((id) => value.includes(id));
  const anyFilteredSelected = filteredIds.some((id) => value.includes(id));
  const doSelectAll = () => {
    if (searching) {
      const merged = Array.from(new Set([...value, ...filteredIds]));
      onChange(merged);
    } else {
      onChange(sorted.map((o) => o.v));
    }
  };
  const doUnselectAll = () => {
    if (searching) {
      onChange(value.filter((v) => !filteredIds.includes(v)));
    } else {
      onChange([]);
    }
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className={`text-xs px-2 h-8 border inline-flex items-center gap-1 ${value.length ? "border-emerald-500 bg-emerald-50 text-emerald-700 font-medium" : "border-border hover:bg-muted"}`} data-testid={testid}>
          {label}{value.length ? ` · ${value.length}` : ""} <ChevronDown className="w-3 h-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-1 rounded-none" align="start">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${label.toLowerCase()}…`} className="h-7 text-xs mb-1" data-testid={`${testid}-search`} />
        <div className="flex items-center justify-between px-2 py-1 border-b border-border text-[11px]">
          <button
            onClick={doSelectAll}
            className="text-primary hover:underline disabled:opacity-40"
            disabled={searching ? (filtered.length === 0 || allFilteredSelected) : value.length === sorted.length}
            title={searching ? `Select all ${filtered.length} matching item${filtered.length === 1 ? "" : "s"}` : "Select every option"}
            data-testid={`${testid}-select-all`}
          >{searching ? `Select ${filtered.length} matching` : "Select all"}</button>
          <button
            onClick={doUnselectAll}
            className="text-muted-foreground hover:text-foreground disabled:opacity-40"
            disabled={searching ? !anyFilteredSelected : value.length === 0}
            data-testid={`${testid}-unselect-all`}
          >{searching ? "Unselect matching" : "Unselect all"}</button>
        </div>
        <div className="max-h-52 overflow-y-auto">
          {filtered.map((o) => (
            <label key={o.v} className="flex items-center gap-2 text-xs px-2 py-1 hover:bg-muted cursor-pointer">
              <input type="checkbox" checked={value.includes(o.v)} onChange={() => toggle(o.v)} data-testid={`${testid}-opt-${o.v}`} />
              <span className="flex-1 truncate">{o.l}</span>
              {o.hint && <span className="text-[10px] text-muted-foreground">{o.hint}</span>}
            </label>
          ))}
          {filtered.length === 0 && <div className="px-2 py-3 text-xs text-muted-foreground italic">No results.</div>}
        </div>
      </PopoverContent>
    </Popover>
  );
}


// ══════════════════════════════════════════════════════════════════
// R159l · Feb 2026 — Inline project-field reassign chip on the card
// header. Renders the current value as a tiny click-to-edit label;
// clicking opens a Radix Popover with a searchable command list. On
// select, PATCHes /projects/{id} and fires onSaved so the board
// refetches. Used for Client / Brand / PM.
// R159r · Feb 2026 — Rewired to accept an `onLocalPatch` callback that
// only splices the changed fields into local project state — no full
// board refetch. Kills the ~1.5 s flicker every time a PM was
// swapped.
// ══════════════════════════════════════════════════════════════════
function InlineProjectPicker({ project, field, label, value, displayName, options, onSaved, onLocalPatch, chipCls }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return options;
    return options.filter((o) => (o.l || "").toLowerCase().includes(s));
  }, [q, options]);
  const commit = async (newId) => {
    if (newId === value) { setOpen(false); return; }
    setSaving(true);
    try {
      const { data: updated } = await api.patch(`/projects/${project.id}`, { [field]: newId });
      toast.success(`${label} updated`);
      setOpen(false);
      // Prefer surgical patch so only this card re-renders.
      if (onLocalPatch) {
        // R159dd · Feb 2026 — Special-case `project_manager_id` because
        // the human label the card reads is `pm_name` (denormed by the
        // planning-board-data endpoint), NOT the naive
        // `project_manager_name` that the id→name replacer would emit.
        const nameField = field === "project_manager_id"
          ? "pm_name"
          : field.replace("_id", "_name");
        const chosen = options.find((o) => o.v === newId);
        const patch = { [field]: newId };
        // For scalar fields like `health` the id IS the value so
        // skipping avoids overwriting it with the human label.
        if (nameField !== field) patch[nameField] = chosen ? chosen.l : null;
        // Keep the legacy `pm_id` mirror in sync so any other consumer
        // reading `project.pm_id` still sees the fresh value.
        if (field === "project_manager_id") patch.pm_id = newId;
        // Backend may echo additional denormed fields — merge if present.
        if (updated && typeof updated === "object") {
          if (updated.client_name !== undefined) patch.client_name = updated.client_name;
          if (updated.brand_name !== undefined) patch.brand_name = updated.brand_name;
          if (updated.pm_name !== undefined) patch.pm_name = updated.pm_name;
        }
        onLocalPatch(project.id, patch);
      } else {
        onSaved && onSaved();
      }
    } catch (e) {
      toast.error(apiErrorMessage(e, `Couldn't update ${label.toLowerCase()}`));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v) setQ(""); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          className={`inline-flex items-center gap-0.5 px-1 py-0.5 border rounded-none text-[11px] max-w-[140px] min-w-0 ${chipCls ? `${chipCls} font-semibold hover:opacity-90` : "border-transparent hover:border-primary/40 hover:bg-primary/5"}`}
          title={`${label}: ${displayName || "unassigned"} · click to change`}
          data-testid={`planning-card-inline-${field}-${project.id}`}
        >
          {/* R159dd · Feb 2026 — Dropped the "LABEL:" prefix on card
              inline pickers. The tooltip still carries the field name;
              this frees up ~30 % of chip width so the actual value fits
              without truncation on tight cards. */}
          <span className="truncate">{displayName || <span className="italic text-muted-foreground/60">{label.toLowerCase()}?</span>}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-64 p-0"
        align="start"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        data-testid={`planning-card-inline-${field}-${project.id}-popover`}
      >
        <div className="p-2 border-b border-border">
          <Input
            autoFocus
            placeholder={`Search ${label.toLowerCase()}…`}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="h-8 text-xs rounded-none"
            data-testid={`planning-card-inline-${field}-${project.id}-search`}
          />
        </div>
        <div className="max-h-56 overflow-y-auto">
          {/* Clear button — only shown when a value is currently set. */}
          {value && (
            <button
              type="button"
              onClick={() => commit(null)}
              disabled={saving}
              className="w-full text-left px-3 py-1.5 text-[11px] italic text-muted-foreground hover:bg-muted"
              data-testid={`planning-card-inline-${field}-${project.id}-clear`}
            >
              — Clear —
            </button>
          )}
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-[11px] text-muted-foreground italic">No matches</div>
          ) : filtered.map((o) => (
            <button
              key={o.v}
              type="button"
              onClick={() => commit(o.v)}
              disabled={saving}
              className={`w-full text-left px-3 py-1.5 text-[11px] hover:bg-muted truncate ${o.v === value ? "bg-primary/10 font-semibold" : ""}`}
              data-testid={`planning-card-inline-${field}-${project.id}-opt-${o.v}`}
            >
              {o.l}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}


// Project card — the star of the show.
function ProjectCard({ project, tasks, filteredTasks, layout, onLayoutChange, onTasksReload, onProjectReload, onProjectMetaReload, onProjectLocalPatch, users, clients, brands, members, projects, dragTaskId, hoverProjectId, onTaskDragStart, onTaskDragEnd, onCardDragOver, onCardDrop, onOpenTask, onOpenAllTasks, watchedIds, onToggleWatch, onChangeTaskStatus, onChangeTaskPriority, onOpenStatusUpdates, projectWatched, onToggleProjectWatch, onOpenNotes, onSnooze, unbilledActive, onToggleUnbilled, accountsReviewOn, accountsNote, accountsNeedsReview, onMarkAccountsReviewed, accountsTaskNotes, onInlineAccountsReply, onOpenTaskNote, onOpenTaskFinance, showTaskFinanceIcons, projectFinanceRollup, projectEffortRollup, mentionCandidates, canBulkAddTask, onOpenBulkAdd, projectMemberIds = [], autoHeight = false, statusTemplates = null, senderName = "" }) {
  // R159ss · Feb 2026 — Build a Set once so `outsideMember` checks
  // per TaskRow are O(1).
  const _memberSet = useMemo(() => new Set(projectMemberIds || []), [projectMemberIds]);
  const [teamDialogOpen, setTeamDialogOpen] = useState(false);
  const nav = useNavigate();
  const { activeWorkspaceId } = useAuth();
  const status = STATUS_META[project.status] || STATUS_META.active;
  // R158x · Feb 2026 — Pending status transition (drives the confirmation dialog).
  const [pendingStatus, setPendingStatus] = useState(null);

  const openCount = tasks.filter((t) => t.status !== "done").length;
  const overdue = tasks.filter((t) => t.status !== "done" && t.due_date && new Date(t.due_date) < new Date()).length;
  const done = tasks.filter((t) => t.status === "done").length;
  const total = tasks.length;
  const progress = total ? Math.round((done / total) * 100) : 0;
  // R159t · Feb 2026 — Billing roll-up. Buckets are mutually
  // exclusive: paid > billed > billable. Displayed only when at
  // least one task in the card is billable so we don't clutter the
  // header on non-billable projects.
  const billBuckets = tasks.reduce((acc, t) => {
    if (t.paid) acc.paid += 1;
    else if (t.billed) acc.billed += 1;
    else if (t.billable) acc.billable += 1;
    return acc;
  }, { billable: 0, billed: 0, paid: 0 });
  const billTotal = billBuckets.billable + billBuckets.billed + billBuckets.paid;

  // R158g · Feb 2026 — Per-card quick task search. Uses the shared
  // /tasks-search endpoint which already scans title, description,
  // assignee name/email and the comment thread. Empty query falls
  // back to the incoming lean `filteredTasks` list.
  const [searchQ, setSearchQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  useEffect(() => {
    const h = setTimeout(() => setDebouncedQ(searchQ.trim()), 250);
    return () => clearTimeout(h);
  }, [searchQ]);
  useEffect(() => {
    if (!debouncedQ) { setSearchResults(null); return; }
    let cancelled = false;
    setSearching(true);
    (async () => {
      try {
        const { data } = await api.get(
          `/workspaces/${activeWorkspaceId}/tasks-search?project_id=${project.id}&q=${encodeURIComponent(debouncedQ)}`,
        );
        if (!cancelled) setSearchResults(Array.isArray(data) ? data : (data?.rows || []));
      } catch { if (!cancelled) setSearchResults([]); }
      finally { if (!cancelled) setSearching(false); }
    })();
    return () => { cancelled = true; };
  }, [debouncedQ, activeWorkspaceId, project.id]);

  const effectiveTasks = searchResults !== null ? searchResults : filteredTasks;

  const updateStatus = async (newStatus) => {
    if (newStatus === project.status) return;
    // R158x · Feb 2026 — Upgraded from window.prompt to shadcn Dialog
    // (ProjectStatusChangeDialog) with preset chips + Textarea. The dialog
    // owns the busy state; on confirm it PATCHes and closes.
    setPendingStatus(newStatus);
  };
  const commitStatusChange = async (reason) => {
    if (!pendingStatus) return;
    // Iter · Feb 2026 — Optimistic update. Instead of an expensive
    // `onProjectReload()` (which refetches the project + all its
    // tasks after the PATCH), we patch this project's status locally
    // and let the parent's `onProjectLocalPatch` broadcast the tiny
    // change. On failure we roll back via the surgical refetch. This
    // keeps the visual "whole board flash" at bay when a PM cycles
    // through statuses.
    const previousStatus = project.status;
    const newStatus = pendingStatus;
    if (onProjectLocalPatch) {
      onProjectLocalPatch(project.id, { status: newStatus });
    }
    setPendingStatus(null);
    try {
      await api.patch(`/projects/${project.id}`, {
        status: newStatus, status_change_reason: reason,
      });
      toast.success(`Status → ${STATUS_META[newStatus]?.label || newStatus}`);
    } catch (e) {
      // Roll back the optimistic patch, then reload the card's real
      // state from the server just in case something else changed.
      if (onProjectLocalPatch) {
        onProjectLocalPatch(project.id, { status: previousStatus });
      }
      toast.error(apiErrorMessage(e, "Update failed"));
      if (onProjectReload) onProjectReload();
    }
  };

  const isDropTarget = !!dragTaskId && hoverProjectId === project.id;

  return (
    <div
      className={`${autoHeight ? "" : "h-full"} w-full flex flex-col border-2 bg-white transition-all overflow-hidden hover:shadow-md ${isDropTarget ? "border-emerald-500 bg-emerald-50/40 shadow-md" : `${HEALTH_BORDER[project.health] || HEALTH_BORDER_FALLBACK} shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.04)]`}`}
      data-testid={`planning-card-${project.id}`}
      data-shortcut-container={`proj-${project.id}`}
      onDragOver={(e) => { if (dragTaskId) { e.preventDefault(); onCardDragOver && onCardDragOver(project.id); } }}
      onDragLeave={() => { if (hoverProjectId === project.id) onCardDragOver && onCardDragOver(null); }}
      onDrop={(e) => { e.preventDefault(); onCardDrop && onCardDrop(project.id); }}
    >
      {/* R158v · Feb 2026 — Two-row header. Row 1: full-width project name
          + subtitle (so long names/brand/PM breadcrumbs never get elbowed by
          buttons). Row 2: action chips (notes, status, watch, refresh, collapse).
          R160hh · Feb 2026 — Header background is tinted with a pastel
          shade of the project's priority so the whole card visually
          inherits its urgency at a glance. */}
      <div className={`planning-card-drag border-b border-border cursor-move ${PRIORITY_HEADER_TINT[project.priority] || "bg-neutral-50"}`} style={{ minHeight: HEADER_H }}>
        <div className="flex items-start gap-2 px-2 pt-2 pb-1">
          <span className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${HEALTH_DOT[project.health] || "bg-neutral-300"}`} title={`Health: ${project.health || "n/a"}`} />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold leading-tight break-words flex items-start gap-1.5 flex-wrap">
              <span className="break-words min-w-0">{project.name}</span>
              {project.department_name && <DepartmentChip name={project.department_name} />}
            </div>
            {!layout.collapsed && (
              /* R159l · Feb 2026 — Inline reassign PM · Client · Brand right
                 from the card header. Each chip is a click-to-edit
                 searchable picker so PMs never have to open the project
                 detail just to swap a client/brand or hand the project
                 off to another owner.
                 R159dd · Feb 2026 — Dropped the per-chip "LABEL:" prefix
                 (see InlineProjectPicker) and tightened the row's
                 horizontal padding on the parent card so all four chips
                 (Client · Brand · PM · Health) fit on a single visual
                 line at 340×400. */
              <div className="text-[10px] text-muted-foreground leading-tight mt-0.5 flex items-center gap-0.5 flex-wrap">
                <InlineProjectPicker
                  project={project}
                  field="client_id"
                  label="Client"
                  value={project.client_id}
                  displayName={project.client_name}
                  options={(clients || []).map((c) => ({ v: c.id, l: c.name }))}
                  onSaved={onProjectReload}
                  onLocalPatch={onProjectLocalPatch}
                />
                <InlineProjectPicker
                  project={project}
                  field="brand_id"
                  label="Brand"
                  value={project.brand_id}
                  displayName={project.brand_name}
                  options={(brands || []).map((b) => ({ v: b.id, l: b.name }))}
                  onSaved={onProjectReload}
                  onLocalPatch={onProjectLocalPatch}
                />
                <InlineProjectPicker
                  project={project}
                  field="project_manager_id"
                  label="PM"
                  value={project.project_manager_id || project.pm_id || project.owner_id}
                  displayName={project.pm_name || project.owner_name}
                  options={(users || []).map((u) => ({ v: u.id, l: u.name || u.email }))}
                  onSaved={onProjectReload}
                  onLocalPatch={onProjectLocalPatch}
                />
                {/* R159t · Feb 2026 — Inline Health picker. Fixed
                    option set so no server round-trip on open. */}
                <InlineProjectPicker
                  project={project}
                  field="health"
                  label="Health"
                  value={project.health}
                  displayName={project.health ? project.health.replace(/_/g, " ") : null}
                  chipCls={HEALTH_CHIP[project.health]}
                  options={[
                    { v: "on_track", l: "On track" },
                    { v: "at_risk",  l: "At risk" },
                    { v: "off_track",l: "Off track" },
                    { v: "blocked",  l: "Blocked" },
                    { v: "new",      l: "New" },
                  ]}
                  onSaved={onProjectReload}
                  onLocalPatch={onProjectLocalPatch}
                />
                {/* R160hh · Feb 2026 — Inline Priority picker. Dark
                    chip with light text so it stands out. Editing
                    only refreshes THIS card via `onProjectReload`
                    (bound to `refetchOneProject(project.id)`). */}
                <InlineProjectPicker
                  project={project}
                  field="priority"
                  label="Priority"
                  value={project.priority}
                  displayName={project.priority ? (PRIORITY_LABEL[project.priority] || project.priority) : null}
                  chipCls={PRIORITY_CHIP[project.priority]}
                  options={[
                    { v: "low",    l: "Low" },
                    { v: "medium", l: "Medium" },
                    { v: "high",   l: "High" },
                    { v: "urgent", l: "Urgent" },
                  ]}
                  onSaved={onProjectReload}
                  onLocalPatch={onProjectLocalPatch}
                />
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-0.5 px-2 pb-1.5 flex-wrap">
          <StatusPicker status={project.status} onChange={updateStatus} />
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onOpenNotes && onOpenNotes({ scope: "project", id: project.id, label: project.name }); }}
            className="p-1 hover:bg-neutral-200 text-neutral-500 hover:text-primary relative"
            title="Send / view Accounts notes for this project"
            data-testid={`planning-card-project-notes-${project.id}`}
          >
            <MessageSquarePlus className="w-3.5 h-3.5" />
            {project.billing_notes_count > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-3.5 h-3.5 px-0.5 text-[8px] font-bold leading-3.5 bg-primary text-primary-foreground rounded-full">
                {project.billing_notes_count > 9 ? "9+" : project.billing_notes_count}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onToggleProjectWatch && onToggleProjectWatch(project.id, projectWatched); }}
            className={`p-1 hover:bg-neutral-200 ${projectWatched ? "text-primary" : "text-neutral-400 hover:text-neutral-700"}`}
            title={projectWatched ? "Remove project from watch list" : "Add project to watch list"}
            data-testid={`planning-card-project-watch-${project.id}`}
          >
            {projectWatched ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
          </button>
          <RefreshCardButton onClick={() => onTasksReload && onTasksReload()} projectId={project.id} />
          {/* R159ss · Feb 2026 — Team management popup. Opens a compact
              dialog listing the project's active roster with add /
              remove controls. Available to all viewers; the actual
              add/remove endpoints enforce the `project.member_manage`
              permission server-side. */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setTeamDialogOpen(true); }}
            className="p-1 hover:bg-neutral-200 text-muted-foreground hover:text-primary"
            title="Manage project team"
            data-testid={`planning-card-team-${project.id}`}
          >
            <Users className="w-3.5 h-3.5" />
          </button>
          {/* R159e · Feb 2026 — Snooze this card. Hides it until any
              filter changes; auto-persisted so refresh/re-login resumes
              the same view. */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onSnooze && onSnooze(); }}
            className="p-1 hover:bg-neutral-200 text-neutral-500 hover:text-primary ml-auto"
            title="Snooze this card — hide it until you change any filter"
            data-testid={`planning-card-snooze-${project.id}`}
          >
            <Moon className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onLayoutChange({ titlesOnly: !layout.titlesOnly }); }}
            className={`p-1 hover:bg-neutral-200 ${layout.titlesOnly ? "text-primary bg-primary/10" : ""}`}
            title={layout.titlesOnly ? "Show full task details" : "Show only task titles (compact)"}
            data-testid={`planning-card-titles-only-${project.id}`}
          >
            {layout.titlesOnly ? <ListChecks className="w-3.5 h-3.5" /> : <AlignLeft className="w-3.5 h-3.5" />}
          </button>
          <button onClick={(e) => { e.stopPropagation(); onLayoutChange({ collapsed: !layout.collapsed }); }} className="p-1 hover:bg-neutral-200" title={layout.collapsed ? "Expand" : "Collapse"} data-testid={`planning-card-collapse-${project.id}`}>
            {layout.collapsed ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronUp className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {/* Iter · Feb 2026 — Task-list shortcut row: Email / WhatsApp /
          Copy text / Copy image. Recipient defaults to the project PM
          when we can resolve their contact from `members`. Hidden when
          the card is collapsed to keep the header slim. */}
      {!layout.collapsed && (
        <div className="px-2 py-1 border-b border-border bg-background flex items-center gap-1 flex-wrap"
             onClick={(e) => e.stopPropagation()}
             data-testid={`planning-card-shortcuts-${project.id}`}>
          {(() => {
            const pm = (members || []).find((m) => (m.user_id || m.id) === project.owner_id);
            const recipient = pm ? {
              name: pm.name || pm.email,
              email: pm.email,
              phone: pm.phone || null,
            } : null;
            const projectsById = new Map((projects || []).map((p) => [p.id, p]));
            return (
              <TaskShortcutsRow
                templates={statusTemplates}
                recipient={recipient}
                ownerName={pm?.name}
                senderName={senderName}
                tasks={filteredTasks || tasks || []}
                projectName={project.name}
                projectsById={projectsById}
                containerId={`proj-${project.id}`}
                compact
              />
            );
          })()}
        </div>
      )}

      {/* R159cc · Feb 2026 — Effort-Overrun ribbon. Renders when the
          project's TOTAL logged effort exceeds the sum of its task
          estimates (any positive baseline needed). Non-clickable — the
          Finance Report already offers deep-dive. */}
      {projectEffortRollup && projectEffortRollup.est > 0 && projectEffortRollup.logged > projectEffortRollup.est && (
        <div
          className="px-3 py-1 border-b border-rose-300 bg-rose-500 text-white text-[10px] uppercase tracking-widest flex items-center gap-2"
          title={`Total logged (manual + timesheet + actual) ${projectEffortRollup.logged.toFixed(1)}h exceeds task estimate sum ${projectEffortRollup.est.toFixed(1)}h`}
          data-testid={`planning-card-overrun-${project.id}`}
        >
          <AlertTriangle className="w-3 h-3" />
          Effort overrun · {projectEffortRollup.logged.toFixed(1)}h / {projectEffortRollup.est.toFixed(1)}h
          <span className="ml-auto font-mono">+{(projectEffortRollup.logged - projectEffortRollup.est).toFixed(1)}h</span>
        </div>
      )}

      {/* Cover metrics — visible even when collapsed */}
      {layout.collapsed && (
        <div className="px-3 py-1 text-[10px] text-muted-foreground flex items-center gap-3 border-b border-border">
          <span title="Open"><Circle className="w-3 h-3 inline mr-0.5 text-neutral-500" />{openCount}</span>
          <span title="Overdue" className={overdue ? "text-rose-600" : ""}><AlertTriangle className="w-3 h-3 inline mr-0.5" />{overdue}</span>
          <span title="Progress"><CheckCircle2 className="w-3 h-3 inline mr-0.5 text-emerald-500" />{progress}%</span>
        </div>
      )}

      {/* R159w · Feb 2026 — Accounts Review strip. Rendered on BOTH
          collapsed and expanded cards so reviewers can scan the whole
          board without expanding every card. Empty state prompts the
          user to add the first note. */}
      {accountsReviewOn && (
        <AccountsReviewStrip
          projectId={project.id}
          projectName={project.name}
          note={accountsNote}
          needsReview={accountsNeedsReview}
          taskNotes={accountsTaskNotes}
          onOpenThread={() => onOpenNotes && onOpenNotes({ scope: "project", id: project.id, label: project.name })}
          onMarkReviewed={onMarkAccountsReviewed}
          onInlineReply={onInlineAccountsReply}
          onOpenTaskNote={onOpenTaskNote}
          projectFinanceRollup={projectFinanceRollup}
          mentionCandidates={mentionCandidates}
        />
      )}

      {!layout.collapsed && (
        <>
          {/* Cover metrics + per-card filter */}
          <div className="px-3 py-1.5 flex items-center gap-2 border-b border-border text-[10px]">
            <span title="Open" className="inline-flex items-center gap-0.5 text-neutral-600"><Circle className="w-3 h-3" />{openCount}</span>
            <span title="Overdue" className={`inline-flex items-center gap-0.5 ${overdue ? "text-rose-600 font-medium" : "text-neutral-600"}`}><AlertTriangle className="w-3 h-3" />{overdue}</span>
            {/* R159t · Feb 2026 — Billing roll-up chip. Hidden when
                no task on the card is billable so non-billable
                projects don't get visual noise.
                R159u · Feb 2026 — Chip is now a toggle: click it to
                filter the board down to cards with outstanding
                billable / billed-not-paid work. Active state shows a
                stronger emerald ring so users know the filter is on. */}
            {billTotal > 0 && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); if (onToggleUnbilled) onToggleUnbilled(); }}
                className={`inline-flex items-center gap-0.5 border px-1 rounded-none text-[9px] font-mono transition-colors ${unbilledActive ? "border-emerald-600 bg-emerald-600 text-white ring-2 ring-emerald-300" : "border-emerald-300 bg-emerald-50 hover:bg-emerald-100"}`}
                title={unbilledActive ? "Filter ON · click to show all cards" : `Billing roll-up · ${billBuckets.billable} billable · ${billBuckets.billed} billed · ${billBuckets.paid} paid · click to filter board to cards with unbilled/unpaid work`}
                data-testid={`planning-card-bill-rollup-${project.id}`}
              >
                {/* Iter · Feb 2026 — Show the SAME icons as the per-task
                    billing status chip (Tag · Receipt · BadgeCheck) so
                    a "$1" no longer reads as one dollar. The icon on
                    the LEFT of each count clarifies bucket at a glance. */}
                <span className={`inline-flex items-center gap-0.5 ${unbilledActive ? "text-white" : "text-emerald-700"}`}>
                  <Tag className="w-2.5 h-2.5" aria-hidden="true" />
                  {billBuckets.billable}
                </span>
                <span className="opacity-40">·</span>
                <span className={`inline-flex items-center gap-0.5 ${unbilledActive ? "text-white" : "text-sky-700"}`}>
                  <Receipt className="w-2.5 h-2.5" aria-hidden="true" />
                  {billBuckets.billed}
                </span>
                <span className="opacity-40">·</span>
                <span className={`inline-flex items-center gap-0.5 ${unbilledActive ? "text-white" : "text-purple-700"}`}>
                  <BadgeCheck className="w-2.5 h-2.5" aria-hidden="true" />
                  {billBuckets.paid}
                </span>
              </button>
            )}
            <div className="flex-1 mx-1 h-1 bg-neutral-100 relative">
              <div className="absolute inset-y-0 left-0 bg-emerald-400" style={{ width: `${progress}%` }} />
            </div>
            <span className="tabular-nums">{progress}%</span>
            <PerCardFilter value={layout.task_filter} onChange={(v) => onLayoutChange({ task_filter: v })} />
          </div>

          {/* R158g · Per-card task search — hits /tasks-search which scans
              title, description, assignee and comment thread. */}
          <div className="px-2 py-1 border-b border-border bg-white">
            <div className="relative">
              <Search className="w-3 h-3 absolute left-2 top-2 text-muted-foreground" />
              <input
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                placeholder="Search tasks (title, description, thread…)"
                className="w-full h-7 text-xs pl-6 pr-6 border border-border focus:border-primary outline-none"
                onClick={(e) => e.stopPropagation()}
                data-testid={`planning-card-search-${project.id}`}
              />
              {searchQ && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setSearchQ(""); }}
                  className="absolute right-1.5 top-1.5 p-0.5 text-muted-foreground hover:text-foreground"
                  title="Clear"
                  data-testid={`planning-card-search-clear-${project.id}`}
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
            {searchResults !== null && (
              <div className="text-[10px] text-muted-foreground pt-0.5 flex items-center gap-1">
                {searching ? (
                  <><Loader2 className="w-3 h-3 animate-spin" /> searching…</>
                ) : (
                  <>{searchResults.length} match{searchResults.length !== 1 ? "es" : ""}</>
                )}
              </div>
            )}
          </div>

          {/* Task list */}
          <div className="flex-1 overflow-y-auto min-h-0">
            {effectiveTasks.length === 0 ? (
              <div className="p-3 text-xs text-muted-foreground italic">
                {debouncedQ ? `No tasks match “${debouncedQ}”.` : "No tasks match this card's filter."}
              </div>
            ) : (
              <ul className="divide-y">
                {/* R160gg · Feb 2026 — Render open subtasks nested
                    under their parent. Parent + direct subtasks only
                    (1 level deep). Orphan subtasks (parent filtered
                    out) render standalone with a parent-name caption. */}
                {(() => {
                  // Fast lookup: children by parent id, restricted to
                  // OPEN status (matches user spec).
                  const childrenByParent = {};
                  (tasks || []).forEach((t) => {
                    if (t.parent_task_id && t.status !== "done" && t.status !== "cancelled") {
                      (childrenByParent[t.parent_task_id] = childrenByParent[t.parent_task_id] || []).push(t);
                    }
                  });
                  // Parent titles for orphan subtask captions.
                  const titleById = {};
                  (tasks || []).forEach((t) => { titleById[t.id] = t.title; });
                  const seenSubtaskIds = new Set();
                  const rows = [];
                  effectiveTasks.slice(0, 40).forEach((t) => {
                    const isSubtask = !!t.parent_task_id;
                    const parentInVisible = isSubtask && effectiveTasks.some((x) => x.id === t.parent_task_id);
                    // Orphan subtask (parent not visible): render standalone with caption.
                    if (isSubtask && !parentInVisible) {
                      rows.push({ t, kind: "orphan", parentTitle: titleById[t.parent_task_id] });
                      seenSubtaskIds.add(t.id);
                      return;
                    }
                    // Regular subtask that shares a parent still in view — skip; will
                    // be emitted when we hit that parent so ordering is parent-first.
                    if (isSubtask) return;
                    // Parent row.
                    rows.push({ t, kind: "parent" });
                    (childrenByParent[t.id] || []).forEach((c) => {
                      if (seenSubtaskIds.has(c.id)) return;
                      rows.push({ t: c, kind: "child" });
                      seenSubtaskIds.add(c.id);
                    });
                  });
                  return rows.map(({ t, kind, parentTitle }) => (
                    <TaskRow
                      key={t.id}
                      task={t}
                      users={users}
                      onOpen={() => onOpenTask && onOpenTask(t.id)}
                      onReload={onTasksReload}
                      onDragStart={onTaskDragStart}
                      onDragEnd={onTaskDragEnd}
                      onChangeStatus={onChangeTaskStatus}
                      onChangePriority={onChangeTaskPriority}
                      watched={watchedIds ? watchedIds.has(t.id) : false}
                      onToggleWatch={onToggleWatch}
                      onOpenNotes={onOpenNotes}
                      onOpenFinance={onOpenTaskFinance}
                      showFinanceIcon={showTaskFinanceIcons}
                      titlesOnly={!!layout.titlesOnly}
                      outsideMember={!!t.assignee_id && !_memberSet.has(t.assignee_id)}
                      isSubtask={kind === "child" || kind === "orphan"}
                      isOrphanSubtask={kind === "orphan"}
                      parentTitle={kind === "child" || kind === "orphan" ? (titleById[t.parent_task_id] || parentTitle) : null}
                    />
                  ));
                })()}
                {effectiveTasks.length > 40 && (
                  <li className="px-3 py-2 text-[10px] text-muted-foreground italic">Showing 40 of {effectiveTasks.length}. Click &ldquo;All tasks&rdquo; to open the full view.</li>
                )}
              </ul>
            )}
          </div>

          {/* Footer actions
              R159y · Feb 2026 — Add task + Bulk add rendered as compact
              icon buttons so a third action can sit next to them
              without crowding the footer. Bulk button opens the shared
              BulkTaskCreator scoped to this project (gated by
              `task.bulk_create` / workspace admin). */}
          <div className="border-t border-border px-2 py-1.5 flex items-center gap-1 bg-neutral-50">
            <QuickAddTaskPopover
              wsId={project.workspace_id}
              preset={{ project_id: project.id }}
              projects={projects || []}
              members={members || []}
              onCreated={onTasksReload}
              testid={`planning-add-task-popover-${project.id}`}
              trigger={
                <Button size="sm" variant="ghost" className="rounded-none h-7 w-8 px-0" title="Add task to this project" data-testid={`planning-add-task-${project.id}`}>
                  <Plus className="w-4 h-4" />
                </Button>
              }
            />
            {canBulkAddTask && (
              <Button
                size="sm" variant="ghost"
                className="rounded-none h-7 w-8 px-0"
                onClick={() => onOpenBulkAdd && onOpenBulkAdd(project)}
                title="Bulk-add tasks to this project (paste multiple rows at once)"
                data-testid={`planning-bulk-add-task-${project.id}`}
              >
                <LayoutGrid className="w-4 h-4" />
              </Button>
            )}
            <Button size="sm" variant="ghost" className="rounded-none h-7 text-xs flex-1" onClick={() => onOpenAllTasks && onOpenAllTasks()} data-testid={`planning-view-tasks-${project.id}`}>
              <ListChecks className="w-3 h-3 mr-1" /> All tasks
            </Button>
            <Button
              size="sm" variant="ghost"
              className="rounded-none h-7 text-xs relative"
              onClick={() => onOpenStatusUpdates && onOpenStatusUpdates()}
              title="Post / view status updates"
              data-testid={`planning-status-updates-${project.id}`}
            >
              <MessageSquare className="w-3 h-3" />
              {project.status_updates_count > 0 && (
                <span className="ml-1 inline-flex items-center justify-center min-w-4 h-4 px-1 text-[10px] leading-4 font-medium bg-primary/10 text-primary rounded-full">
                  {project.status_updates_count > 99 ? "99+" : project.status_updates_count}
                </span>
              )}
            </Button>
            <Button
              size="sm" variant="ghost"
              className="rounded-none h-7 text-xs"
              onClick={() => window.open(`/app/projects/${project.id}`, "_blank")}
              title="Open project in new tab"
              data-testid={`planning-open-project-${project.id}`}
            >
              <ExternalLink className="w-3 h-3" />
            </Button>
          </div>
        </>
      )}
      {/* R158x · Feb 2026 — Status change confirmation dialog */}
      <ProjectStatusChangeDialog
        open={!!pendingStatus}
        project={project}
        fromStatus={project.status}
        toStatus={pendingStatus}
        statusMeta={STATUS_META}
        onConfirm={commitStatusChange}
        onCancel={() => setPendingStatus(null)}
      />
      {/* R159ss · Feb 2026 — Team management popup, wired to the Users
          icon in the card header row. */}
      <ProjectTeamDialog
        open={teamDialogOpen}
        onClose={() => setTeamDialogOpen(false)}
        project={project}
        workspaceMembers={members || []}
        onChanged={() => {
          // R138j · surgical refresh — only the project meta (roster,
          // member ids), NOT the whole board and NOT the tasks list.
          if (onProjectMetaReload) onProjectMetaReload();
          else if (onProjectReload) onProjectReload();
        }}
      />
    </div>
  );
}

// R158f · Canonical picker order (matches the Projects module column
// options). Legacy status keys still render correctly via STATUS_META
// but are hidden from the dropdown so users don't pick them anew.
const STATUS_PICKER_ORDER = [
  "active", "planned", "in_progress", "hold", "paused",
  "awaiting_work", "inactive", "at_risk", "blocked", "done",
  "idle", "archived",
];

function StatusPicker({ status, onChange }) {
  const [open, setOpen] = useState(false);
  const m = STATUS_META[status] || STATUS_META.active;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className={`text-[9px] uppercase tracking-widest border px-1.5 py-0.5 ${m.cls}`} onClick={(e) => e.stopPropagation()} data-testid="planning-card-status-picker">
          {m.label}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-44 p-1 rounded-none max-h-72 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {STATUS_PICKER_ORDER.map((k) => {
          const v = STATUS_META[k];
          if (!v) return null;
          return (
            <button
              key={k}
              onClick={() => { onChange(k); setOpen(false); }}
              className={`w-full text-left text-xs px-2 py-1 hover:bg-muted ${status === k ? "bg-primary/10" : ""}`}
              data-testid={`planning-card-status-opt-${k}`}
            >
              <span className={`inline-block text-[9px] uppercase tracking-widest border px-1 py-0.5 mr-2 ${v.cls}`}>{v.label}</span>
            </button>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}

function PerCardFilter({ value, onChange }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className="text-[10px] border border-border h-6 px-1" onClick={(e) => e.stopPropagation()} data-testid="planning-card-task-filter">
      {Object.entries(TASK_FILTER_LABELS).map(([k, l]) => (
        <option key={k} value={k}>{l}</option>
      ))}
    </select>
  );
}

// R158e · Feb 2026 — Per-card refresh button (header). Spins the icon
// while the fetch is in flight so users get feedback that the refresh
// actually ran (Planning Board's task refresh is nearly-instant).
function RefreshCardButton({ onClick, projectId }) {
  const [spinning, setSpinning] = useState(false);
  const trigger = async (e) => {
    e.stopPropagation();
    if (spinning) return;
    setSpinning(true);
    try { await onClick(); } finally {
      setTimeout(() => setSpinning(false), 400);
    }
  };
  return (
    <button
      type="button"
      onClick={trigger}
      className="p-1 hover:bg-neutral-200 text-muted-foreground hover:text-foreground"
      title="Refresh this card's tasks"
      data-testid={`planning-card-refresh-${projectId}`}
    >
      <RefreshCw className={`w-3.5 h-3.5 ${spinning ? "animate-spin" : ""}`} />
    </button>
  );
}

function TaskRow({ task, users, onOpen, onDragStart, onDragEnd, onChangeStatus, onChangePriority, watched, onToggleWatch, onOpenNotes, onOpenFinance, showFinanceIcon, titlesOnly = false, outsideMember = false, isSubtask = false, isOrphanSubtask = false, parentTitle = null }) {
  const overdue = task.due_date && new Date(task.due_date) < new Date() && task.status !== "done";
  const assignee = users.find((u) => u.id === task.assignee_id);
  const [statusOpen, setStatusOpen] = useState(false);
  const [prioOpen, setPrioOpen] = useState(false);
  const [billOpen, setBillOpen] = useState(false);
  // R159s · Feb 2026 — Inline billing state (paid > billed > billable
  // > none). Optimistic local override so the icon updates instantly
  // and only this row re-renders — no board reload.
  const [billLocal, setBillLocal] = useState({});
  const merged = { ...task, ...billLocal };
  // R159bb · Feb 2026 — Use distinct lucide icons so this control
  // never gets confused with the currency-specific Wallet/₹/$ icon on
  // the same row. Icons: Tag (billable) · Receipt (billed) · Check
  // (paid) · Ban (not billable).
  const billIcon = merged.paid
    ? { Icon: BadgeCheck, cls: "text-purple-700 bg-purple-50 border-purple-400", label: "Paid" }
    : merged.billed
      ? { Icon: Receipt, cls: "text-sky-700 bg-sky-50 border-sky-300", label: "Billed" }
      : merged.ready_to_bill
        ? { Icon: ReadyIcon, cls: "text-amber-700 bg-amber-50 border-amber-400", label: "Ready to bill" }
        : merged.billable
          ? { Icon: Tag, cls: "text-emerald-700 bg-emerald-50 border-emerald-300", label: "Billable" }
          : { Icon: Ban, cls: "text-neutral-300 bg-transparent border-neutral-200", label: "Not billable" };
  const setBill = async (patch) => {
    setBillLocal((prev) => ({ ...prev, ...patch }));
    setBillOpen(false);
    try {
      await api.patch(`/tasks/${task.id}`, patch);
      toast.success("Billing status updated");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Update failed"));
      setBillLocal({}); // revert
    }
  };
  const dotCls = task.status === "done" ? "bg-emerald-500" : task.status === "blocked" ? "bg-rose-500" : task.status === "in_progress" ? "bg-amber-400" : "bg-neutral-400";
  // R159nn · Feb 2026 — Titles-only rendering. When the card is in
  // "titles-only" compact mode, skip every chip and control except
  // a small status dot + the task title (click to open the detail
  // dialog for full editing).
  // R160gg · Feb 2026 — Nested-subtask visual: indent + soft muted
  // background + a small "↳" glyph before the title. Orphan subtasks
  // (parent hidden by current filter) additionally get an amber
  // left-border and a tiny "subtask of …" caption so the row still
  // tells the whole story on its own.
  const subtaskWrap = isSubtask
    ? (isOrphanSubtask
        ? "pl-6 bg-muted/20 border-l-2 border-amber-300"
        : "pl-6 bg-muted/20 border-l-2 border-primary/30")
    : "";
  if (titlesOnly) {
    return (
      <TaskHoverCard task={task} users={users} side="right" align="start" parentTitle={parentTitle}>
        <li
          onClick={onOpen}
          className={`px-3 py-1 hover:bg-emerald-50/40 cursor-pointer text-xs flex items-center gap-2 ${overdue ? "text-rose-700" : ""} ${subtaskWrap}`}
          data-testid={isSubtask ? (isOrphanSubtask ? `planning-orphan-subtask-${task.id}` : `planning-subtask-${task.id}`) : `planning-task-${task.id}`}
        >
          <span className={`w-2 h-2 rounded-full shrink-0 ${dotCls}`} />
          {isSubtask && <span className="text-muted-foreground shrink-0" aria-hidden>↳</span>}
          <span className={`flex-1 truncate ${task.status === "done" ? "line-through text-muted-foreground" : ""}`}>
            {task.title}
          </span>
        </li>
      </TaskHoverCard>
    );
  }
  return (
    <TaskHoverCard task={task} users={users} side="right" align="start" parentTitle={parentTitle}>
      <li
        onClick={onOpen}
        className={`px-3 py-1.5 hover:bg-emerald-50/40 cursor-pointer text-xs flex items-center gap-2 ${subtaskWrap}`}
        data-testid={isSubtask ? (isOrphanSubtask ? `planning-orphan-subtask-${task.id}` : `planning-subtask-${task.id}`) : `planning-task-${task.id}`}
        draggable
        onDragStart={(e) => {
          e.stopPropagation();
          e.dataTransfer.effectAllowed = "move";
          e.dataTransfer.setData("text/plain", task.id);
          onDragStart && onDragStart(task.id);
        }}
        onDragEnd={() => onDragEnd && onDragEnd()}
      >
        {/* R159s · Feb 2026 — Inline billing indicator + dropdown */}
        <Popover open={billOpen} onOpenChange={setBillOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={`w-4 h-4 shrink-0 flex items-center justify-center font-bold text-[10px] border rounded-full ${billIcon.cls} hover:ring-2 hover:ring-primary/40`}
              title={`${billIcon.label} · click to change`}
              onClick={(e) => { e.stopPropagation(); setBillOpen(true); }}
              data-testid={`planning-task-bill-${task.id}`}
            >
              {billIcon.Icon ? <billIcon.Icon className="w-3 h-3" /> : billIcon.icon}
            </button>
          </PopoverTrigger>
          <PopoverContent
            side="right" align="start" className="w-44 p-1 rounded-none"
            onClick={(e) => e.stopPropagation()}
            data-testid={`planning-task-bill-menu-${task.id}`}
          >
            {[
              { label: "Not billable", patch: { billable: false, billed: false, paid: false }, dot: "bg-neutral-300" },
              { label: "Billable",     patch: { billable: true,  billed: false, paid: false }, dot: "bg-emerald-500" },
              { label: "Billed",       patch: { billable: true,  billed: true,  paid: false }, dot: "bg-sky-500" },
              { label: "Paid",         patch: { billable: true,  billed: true,  paid: true },  dot: "bg-purple-500" },
            ].map((o) => {
              const active =
                (o.label === "Paid" && merged.paid) ||
                (o.label === "Billed" && merged.billed && !merged.paid) ||
                (o.label === "Billable" && merged.billable && !merged.billed && !merged.paid) ||
                (o.label === "Not billable" && !merged.billable && !merged.billed && !merged.paid);
              return (
                <button
                  key={o.label}
                  onClick={(e) => { e.stopPropagation(); setBill(o.patch); }}
                  className={`w-full text-left text-xs px-2 py-1 hover:bg-muted flex items-center gap-2 ${active ? "bg-primary/10 font-medium" : ""}`}
                  data-testid={`planning-task-bill-opt-${task.id}-${o.label.toLowerCase().replace(/ /g, "-")}`}
                >
                  <span className={`w-2 h-2 rounded-full ${o.dot}`} /> {o.label}
                </button>
              );
            })}
          </PopoverContent>
        </Popover>
        {/* R159y · Feb 2026 — Task Finance shortcut. Only renders when
            Accounts Review mode is on and the user has finance perms;
            opens the TaskFinanceDialog to edit est_amount, paid_amount
            and currency on this specific task.
            R159aa · Feb 2026 — Icon now reflects the task's OWN
            currency (₹, $, €, £, AED…) so multi-currency workspaces
            can tell at a glance which task is priced where. Falls
            back to a neutral Wallet icon when no currency / amount
            has been set yet. */}
        {showFinanceIcon && (() => {
          const hasFin = (Number(task.est_amount) || 0) > 0 || (Number(task.paid_amount) || 0) > 0;
          const sym = task.currency ? symbolFor(task.currency) : null;
          return (
            <button
              type="button"
              className={`min-w-4 h-4 px-0.5 shrink-0 flex items-center justify-center font-bold text-[9px] border rounded-full transition-colors ${hasFin ? "border-amber-500 bg-amber-100 text-amber-800" : "border-amber-300 bg-white text-amber-500 hover:bg-amber-50"}`}
              title={
                hasFin
                  ? `Task finance · Est ${task.est_amount || 0} · Paid ${task.paid_amount || 0} · ${task.currency || "INR"}`
                  : "Add task finance (Est / Paid / Currency)"
              }
              onClick={(e) => { e.stopPropagation(); onOpenFinance && onOpenFinance(task); }}
              data-testid={`planning-task-finance-${task.id}`}
            >
              {sym ? sym : <Wallet className="w-3 h-3" />}
            </button>
          );
        })()}
        {/* R159aa · Feb 2026 — Total effort chip on every task row so
            reviewers see at a glance where the task stands vs. its
            estimate. Sums manual + timesheet + actual hours.
            R159tt · Feb 2026 — Always render (fall back to `0.00h`)
            so PMs can spot tasks that have no reported hours yet. */}
        {(() => {
          // Iter · Feb 2026 — Prefer the server-annotated
          // `total_effort_hours` (manual + timesheet aggregate) when
          // present so old tasks whose `logged_hours` was never
          // denormalised still surface their real reported effort.
          // Falls back to the manual + logged/timesheet sum + actual
          // that older payloads used.
          const total = Number.isFinite(Number(task.total_effort_hours))
            ? Number(task.total_effort_hours)
            : (Number(task.manual_effort_hours) || 0)
              + (Number(task.timesheet_hours) || Number(task.logged_hours) || 0)
              + (Number(task.actual_hours) || 0);
          const est = Number(task.est_hours) || 0;
          const over = est > 0 && total > est;
          return (
            <span
              className={`shrink-0 h-4 px-1 border rounded-full text-[9px] font-mono ${over ? "border-rose-400 bg-rose-50 text-rose-700" : "border-neutral-200 bg-neutral-50 text-neutral-600"}`}
              title={`Effort · Est ${est || "?"}h · Total logged ${total.toFixed(2)}h${over ? " · Over estimate" : ""}`}
              data-testid={`planning-task-total-effort-${task.id}`}
            >
              {total.toFixed(2)}h
            </span>
          );
        })()}
        <Popover open={statusOpen} onOpenChange={setStatusOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={`w-2.5 h-2.5 rounded-full shrink-0 ${dotCls} hover:ring-2 hover:ring-primary/40`}
              title={`Status: ${task.status} · click to change`}
              onClick={(e) => { e.stopPropagation(); setStatusOpen(true); }}
              data-testid={`planning-task-status-dot-${task.id}`}
            />
          </PopoverTrigger>
          <PopoverContent
            side="right" align="start" className="w-36 p-1 rounded-none"
            onClick={(e) => e.stopPropagation()}
          >
            {[
              { k: "open", label: "Open", dot: "bg-neutral-400" },
              { k: "in_progress", label: "In progress", dot: "bg-amber-400" },
              { k: "blocked", label: "Blocked", dot: "bg-rose-500" },
              { k: "done", label: "Done", dot: "bg-emerald-500" },
            ].map((s) => (
              <button
                key={s.k}
                onClick={(e) => { e.stopPropagation(); setStatusOpen(false); onChangeStatus && onChangeStatus(task.id, s.k); }}
                className={`w-full text-left text-xs px-2 py-1 hover:bg-muted flex items-center gap-2 ${task.status === s.k ? "bg-primary/10" : ""}`}
                data-testid={`planning-task-status-opt-${task.id}-${s.k}`}
              >
                <span className={`w-2 h-2 rounded-full ${s.dot}`} /> {s.label}
              </button>
            ))}
          </PopoverContent>
        </Popover>
        <Popover open={prioOpen} onOpenChange={setPrioOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="shrink-0 hover:bg-muted rounded p-0.5"
              onClick={(e) => { e.stopPropagation(); setPrioOpen(true); }}
              title={`Priority: ${task.priority || "medium"} · click to change`}
              data-testid={`planning-task-prio-btn-${task.id}`}
            >
              <PriorityIcon priority={task.priority || "medium"} className="w-3 h-3" />
            </button>
          </PopoverTrigger>
          <PopoverContent
            side="right" align="start" className="w-36 p-1 rounded-none"
            onClick={(e) => e.stopPropagation()}
          >
            {["urgent", "high", "medium", "low"].map((p) => (
              <button
                key={p}
                onClick={(e) => { e.stopPropagation(); setPrioOpen(false); onChangePriority && onChangePriority(task.id, p); }}
                className={`w-full text-left text-xs px-2 py-1 hover:bg-muted flex items-center gap-2 capitalize ${task.priority === p ? "bg-primary/10" : ""}`}
                data-testid={`planning-task-prio-opt-${task.id}-${p}`}
              >
                <PriorityIcon priority={p} className="w-3 h-3" /> {p}
              </button>
            ))}
          </PopoverContent>
        </Popover>
        {isSubtask && (
          <span className="shrink-0 text-muted-foreground text-[11px]" aria-hidden title={parentTitle ? `Subtask of "${parentTitle}"` : "Subtask"}>↳</span>
        )}
        <span className={`flex-1 min-w-0 flex items-center gap-1.5 ${task.status === "done" ? "line-through text-muted-foreground" : ""}`}>
          <span className="truncate">{task.title}</span>
          {isOrphanSubtask && parentTitle && (
            <span className="shrink-0 text-[9px] uppercase tracking-widest text-amber-700 border border-amber-300 bg-amber-50 px-1"
                  title={`Subtask of "${parentTitle}"`}
                  data-testid={`planning-orphan-subtask-caption-${task.id}`}>
              of {parentTitle.length > 22 ? parentTitle.slice(0, 22) + "…" : parentTitle}
            </span>
          )}
        </span>
        {/* R158s · Feb 2026 — Accounts Notes button (task scope) */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onOpenNotes && onOpenNotes({ scope: "task", id: task.id, label: task.title }); }}
          className="shrink-0 p-0.5 hover:bg-muted rounded text-neutral-400 hover:text-primary relative"
          title="Send / view Accounts notes for this task"
          data-testid={`planning-task-notes-${task.id}`}
        >
          <MessageSquarePlus className="w-3 h-3" />
          {task.billing_notes_count > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-3 h-3 px-0.5 text-[8px] font-bold leading-3 bg-primary text-primary-foreground rounded-full">
              {task.billing_notes_count > 9 ? "9+" : task.billing_notes_count}
            </span>
          )}
        </button>
        {task.due_date && (
          <span className={`text-[10px] font-mono ${overdue ? "text-rose-600 font-medium" : "text-muted-foreground"}`}>{task.due_date.slice(5, 10)}</span>
        )}
        {assignee && (
          <span
            className={`text-[10px] truncate max-w-[80px] ${outsideMember ? "text-rose-600 font-bold" : "text-muted-foreground"}`}
            title={outsideMember ? `${assignee.name} is not a member of this project — reassign or add them to fix access` : assignee.name}
            data-testid={outsideMember ? `planning-task-outside-assignee-${task.id}` : undefined}
          >
            {outsideMember && <AlertTriangle className="w-2.5 h-2.5 inline mr-0.5" />}
            {(assignee.name || "").split(" ")[0]}
          </span>
        )}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onToggleWatch && onToggleWatch(task.id, watched); }}
          className={`shrink-0 p-0.5 hover:bg-muted rounded ${watched ? "text-primary" : "text-neutral-400 hover:text-neutral-700"}`}
          title={watched ? "Remove from watch list" : "Add to watch list"}
          data-testid={`planning-task-watch-${task.id}`}
        >
          {watched ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
        </button>
      </li>
    </TaskHoverCard>
  );
}


function StickyNoteCard({ note, onChange, onDelete }) {
  const [editing, setEditing] = useState(!note.text);
  const cls = STICKY_COLORS[note.color] || STICKY_COLORS.amber;
  return (
    <div className={`h-full w-full border shadow-sm ${cls} flex flex-col`} data-testid={`planning-sticky-${note.id}`}>
      <div className="sticky-drag cursor-move px-2 py-1 flex items-center gap-1 border-b border-black/10">
        <StickyNote className="w-3 h-3 opacity-60" />
        <div className="flex-1 flex items-center gap-0.5">
          {["amber", "emerald", "blue", "rose", "neutral"].map((c) => (
            <button key={c} onClick={() => onChange({ color: c })} className={`w-3 h-3 border ${STICKY_COLORS[c].replace("border-", "border-black/20 ")}`} title={c} />
          ))}
        </div>
        <button onClick={onDelete} className="opacity-60 hover:opacity-100" data-testid={`planning-sticky-delete-${note.id}`}><X className="w-3 h-3" /></button>
      </div>
      {editing ? (
        <textarea
          value={note.text}
          onChange={(e) => onChange({ text: e.target.value })}
          onBlur={() => setEditing(false)}
          autoFocus
          className="flex-1 bg-transparent p-2 text-xs resize-none outline-none"
          placeholder="Type a note…"
          data-testid={`planning-sticky-text-${note.id}`}
        />
      ) : (
        <div onClick={() => setEditing(true)} className="flex-1 p-2 text-xs whitespace-pre-wrap overflow-y-auto cursor-text">
          {note.text || <span className="opacity-40 italic">Click to write…</span>}
        </div>
      )}
    </div>
  );
}

// R158b · Feb 2026 — Swimlane group-by toggle in the toolbar.
function SwimlaneToggle({ value, onChange }) {
  const OPTIONS = [
    { v: "none", l: "Free (no groups)" },
    { v: "client", l: "By Client" },
    { v: "brand", l: "By Brand" },
    { v: "department", l: "By Department" },
    { v: "pm", l: "By PM" },
    { v: "health", l: "By Health" },
    { v: "status", l: "By Status" },
  ];
  const current = OPTIONS.find((o) => o.v === value) || OPTIONS[0];
  // R159ee · Feb 2026 — Consolidated the old 6-pill row into a single
  // compact dropdown so the toolbar keeps room for the more important
  // Filter and Bulk actions. Same testids preserved for backwards-
  // compatible automation, plus the new wrapper testid for the new UI.
  return (
    <div className="inline-flex items-center gap-1" data-testid="planning-group-by-wrapper">
      <span className="text-[10px] uppercase tracking-widest text-muted-foreground hidden md:inline">Group</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`h-8 border rounded-none px-2 text-[11px] uppercase tracking-widest ${
          value === "none"
            ? "border-neutral-300 bg-white text-neutral-700"
            : "border-primary bg-primary/10 text-primary font-semibold"
        }`}
        title="Group projects into horizontal lanes"
        data-testid="planning-swim-toggle"
      >
        {OPTIONS.map((o) => (
          <option key={o.v} value={o.v}>
            {o.l}
          </option>
        ))}
      </select>
      {value !== "none" && (
        <span
          className="hidden md:inline text-[9px] text-muted-foreground"
          title={`Grouping ${current.l.toLowerCase()}`}
        >
          <Rows className="w-2.5 h-2.5 inline" />
        </span>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// R159f · Feb 2026 — Mobile-first stacked view for Planning Board.
// Renders every ProjectCard in a plain vertical flow (no Rnd, no
// absolute positioning) so cards never overlap swimlane labels and
// per-lane collapse works cleanly on phones. Groups by swimlane when
// swimBy != "none", falls back to a flat list otherwise.
// ══════════════════════════════════════════════════════════════════
function MobileStackedBoard({
  projects, tasksByProject, savedLayout, snoozedIds, hideEmptyCards,
  onlyWatched, watchedProjectIds, watchedIds, filters, swimBy, laneOrders,
  laneCollapse, setLaneCollapse, options, members, user, quickDueFilter, flashProjectId,
  unbilledFilter, unbilledActive, onToggleUnbilled,
  accountsReviewOn, accountsSummary, taskNotesByProject, postInlineAccountsReply,
  needsReviewOnly, needsReviewForProject, onMarkAccountsReviewed,
  onOpenTask, onOpenAllTasks, onOpenStatusUpdates, onToggleProjectWatch,
  onToggleWatch, onChangeTaskStatus, onChangeTaskPriority, onOpenNotes,
  onSnooze, onCardLayoutChange, onTasksReload, onProjectReload, onProjectMetaReload, onProjectLocalPatch,
  canBulkAddTask, onOpenBulkAdd,
  statusTemplates,
  // Iter Feb2026 R297 · Thread finance props (icons + rollups + mention
  // candidates) through to each ProjectCard. The canvas layout already
  // passes these at L3184-3189; without them on the flow layout the
  // Task Finance dialog is unreachable from the main planning board.
  onOpenTaskFinance,
  showTaskFinanceIcons = false,
  projectFinanceByPid = {},
  projectEffortByPid = {},
  mentionCandidates = [],
  desktop = false,
}) {
  const applyFilters = (list) => list.filter((p) => {
    if (onlyWatched && !watchedProjectIds.has(p.id)) return false;
    if (snoozedIds.has(p.id)) return false;
    if (unbilledFilter && !hasUnbilledWork(tasksByProject[p.id])) return false;
    if (accountsReviewOn && needsReviewOnly && needsReviewForProject && !needsReviewForProject(p.id)) return false;
    const layout = savedLayout[p.id] || { task_filter: "all_open" };
    const tasks = applyQuickDueFilter(
      filterTasks(tasksByProject[p.id] || [], layout.task_filter, user?.id),
      quickDueFilter,
    );
    if (hideEmptyCards && tasks.length === 0) return false;
    return true;
  });
  const renderCard = (p) => {
    const layout = savedLayout[p.id] || { collapsed: false, task_filter: "all_open" };
    const tasks = applyQuickDueFilter(
      filterTasks(tasksByProject[p.id] || [], layout.task_filter, user?.id),
      quickDueFilter,
    );
    // ProjectCard's outer div uses `h-full` — provide an explicit height
    // on the wrapper so it lays out inside the flow (no Rnd). Collapsed
    // cards shrink to just the header row.
    // Iter · Feb 2026 — When accounts-review mode is ON the collapsed
    // card ALSO renders the AccountsReviewStrip (finance block + note
    // preview + inline reply row) which has variable height depending
    // on note length. A fixed collapsed height left dead whitespace
    // below the reply input on short cards. Switch to `auto` height
    // when accountsReviewOn is on so the strip drives the size and
    // there's no extra blank real-estate.
    const collapsed = !!layout.collapsed;
    const h = collapsed
      ? (accountsReviewOn ? undefined : HEADER_H + 40)
      : Math.max(320, 200 + Math.min(6, (tasksByProject[p.id] || []).length) * 34);
    // R159ee · Feb 2026 — Desktop grid cell: fixed width, mobile: full width.
    const wrapperStyle = desktop
      ? (h !== undefined ? { width: 360, height: h } : { width: 360 })
      : (h !== undefined ? { height: h } : {});
    const wrapperCls = desktop
      ? `${flashProjectId === p.id ? "acq-flash-card" : ""}`
      : `mb-3 ${flashProjectId === p.id ? "acq-flash-card" : ""}`;
    return (
      <div key={p.id} className={wrapperCls} style={wrapperStyle}>
        <ProjectCard
          project={p}
          tasks={tasksByProject[p.id] || []}
          filteredTasks={tasks}
          layout={layout}
          onLayoutChange={(patch) => onCardLayoutChange(p.id, patch)}
          onTasksReload={() => onTasksReload(p.id)}
          onProjectReload={() => onProjectReload(p.id)}
          onProjectMetaReload={() => onProjectMetaReload && onProjectMetaReload(p.id)}
          onProjectLocalPatch={onProjectLocalPatch}
          users={options.users}
          clients={options.clients}
          brands={options.brands}
          members={members}
          projects={projects}
          onOpenTask={onOpenTask}
          onOpenAllTasks={() => onOpenAllTasks(p)}
          onOpenStatusUpdates={() => onOpenStatusUpdates(p)}
          projectWatched={watchedProjectIds.has(p.id)}
          onToggleProjectWatch={onToggleProjectWatch}
          watchedIds={watchedIds}
          onToggleWatch={onToggleWatch}
          onChangeTaskStatus={onChangeTaskStatus}
          onChangeTaskPriority={onChangeTaskPriority}
          onOpenNotes={onOpenNotes}
          onSnooze={() => onSnooze(p.id, p.name)}
          unbilledActive={unbilledActive}
          onToggleUnbilled={onToggleUnbilled}
          accountsReviewOn={accountsReviewOn}
          accountsNote={accountsSummary?.[p.id]}
          accountsNeedsReview={accountsReviewOn && needsReviewForProject && needsReviewForProject(p.id)}
          onMarkAccountsReviewed={() => onMarkAccountsReviewed && onMarkAccountsReviewed(p.id)}
          accountsTaskNotes={taskNotesByProject?.[p.id] || []}
          onInlineAccountsReply={postInlineAccountsReply}
          onOpenTaskNote={(taskId) => onOpenTask && onOpenTask(taskId)}
          onOpenTaskFinance={onOpenTaskFinance}
          showTaskFinanceIcons={showTaskFinanceIcons}
          projectFinanceRollup={projectFinanceByPid[p.id] || {}}
          projectEffortRollup={projectEffortByPid[p.id] || null}
          mentionCandidates={mentionCandidates}
          canBulkAddTask={canBulkAddTask}
          onOpenBulkAdd={onOpenBulkAdd}
          projectMemberIds={p.project_member_ids || []}
          autoHeight={collapsed && accountsReviewOn}
          statusTemplates={statusTemplates}
          senderName={user?.name || ""}
        />
      </div>
    );
  };

  if (swimBy === "none") {
    return (
      <div className="p-2 pb-8" data-testid="planning-mobile-stack">
        <div className={desktop ? "flex flex-wrap gap-3" : ""}>
          {applyFilters(projects).map(renderCard)}
        </div>
      </div>
    );
  }

  // Group by lane.
  const groups = new Map();
  projects.forEach((p) => {
    let key = "—";
    if (swimBy === "client") key = p.client_name || "No client";
    else if (swimBy === "brand") key = p.brand_name || "No brand";
    else if (swimBy === "department") key = p.department_name || "No department";
    else if (swimBy === "pm") key = p.pm_name || p.owner_name || "Unassigned PM";
    else if (swimBy === "health") key = p.health || "no health";
    else if (swimBy === "status") key = p.status || "no status";
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(p);
  });
  const rawKeys = [...groups.keys()];
  const custom = (laneOrders && laneOrders[swimBy]) || [];
  const listed = custom.filter((k) => groups.has(k));
  const rest = rawKeys.filter((k) => !listed.includes(k)).sort((a, b) => a.localeCompare(b));
  const orderedKeys = [...listed, ...rest];

  return (
    <div className="p-2 pb-8" data-testid="planning-mobile-stack">
      {orderedKeys.map((key) => {
        const laneProjects = applyFilters(groups.get(key) || []);
        const collapsed = !!laneCollapse[key];
        return (
          <section key={`m-lane-${key}`} className="mb-4 border border-primary/30 bg-primary/5" data-testid={`planning-mobile-lane-${key}`}>
            <button
              type="button"
              onClick={() => setLaneCollapse((prev) => ({ ...prev, [key]: !collapsed }))}
              className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-primary/10"
              data-testid={`planning-mobile-lane-toggle-${key}`}
            >
              {collapsed ? <ChevronRight className="w-4 h-4 text-primary shrink-0" /> : <ChevronDown className="w-4 h-4 text-primary shrink-0" />}
              <span className="text-[11px] uppercase tracking-widest font-semibold text-primary truncate flex-1">{key}</span>
              <span className="text-[10px] text-muted-foreground shrink-0">{laneProjects.length}</span>
            </button>
            {!collapsed && (
              <div className="p-2 bg-background">
                {laneProjects.length === 0 ? (
                  <div className="text-[11px] text-muted-foreground italic py-3 text-center">No projects</div>
                ) : (
                  <div className={desktop ? "flex flex-wrap gap-3" : ""}>
                    {laneProjects.map(renderCard)}
                  </div>
                )}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}

// R158b · Feb 2026 — Presentation mode overlay. Full-screen single card
// with left/right arrow navigation. Great as a standup script.
// R158d · Feb 2026 — Click task → TaskDetail popup, quick-status dot,
// watch-list icon, Start-over button, keyboard-friendly "Jump to project"
// searchable command palette. Resume-from-last-position is handled by
// the parent page persisting `presentIdx` to localStorage.

// ══════════════════════════════════════════════════════════════════
// R159o · Feb 2026 — Present-mode voice status update. Hold-to-record
// (or click-to-toggle) button that captures audio via MediaRecorder,
// pipes it through /sops/transcribe + /voice/clean, then POSTs the
// cleaned text straight to /projects/{pid}/status-updates. Refreshes
// the parent board on success so the Present card's status pill
// updates live.
// ══════════════════════════════════════════════════════════════════
function PresentVoiceUpdateButton({ projectId, onPosted }) {
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [posting, setPosting] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const recRef = useRef(null);
  const tickRef = useRef(null);
  const startedRef = useRef(0);

  useEffect(() => {
    if (recording) {
      startedRef.current = Date.now();
      setElapsed(0);
      tickRef.current = setInterval(() => setElapsed(Math.floor((Date.now() - startedRef.current) / 1000)), 500);
    } else {
      clearInterval(tickRef.current);
    }
    return () => clearInterval(tickRef.current);
  }, [recording]);

  const busy = recording || transcribing || posting;
  const label = recording ? `Stop · ${elapsed}s` : transcribing ? "Transcribing…" : posting ? "Posting…" : "Voice update";

  const start = async () => {
    if (recording) { recRef.current?.stop(); return; }
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Voice capture isn't supported on this browser"); return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : undefined,
      });
      const chunks = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const blob = new Blob(chunks, { type: mr.mimeType || "audio/webm" });
        if (blob.size < 1000) { toast.error("Too short — hold for at least 1 second"); return; }
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "present-voice.webm");
          const { data } = await api.post("/sops/transcribe", fd);
          let text = (data.text || "").trim();
          if (!text) { toast.error("No speech detected"); return; }
          try {
            const { data: clean } = await api.post("/voice/clean", { text });
            if (clean?.cleaned) text = clean.cleaned;
          } catch { /* fall through with raw transcript */ }
          setTranscribing(false);
          setPosting(true);
          await api.post(`/projects/${projectId}/status-updates`, {
            body_html: `<p>${text.replace(/[<>]/g, "")}</p>`,
            visibility: "internal",
            kind: "manual",
            mention_user_ids: [],
          });
          toast.success("Voice update posted");
          onPosted && onPosted();
        } catch (e) { toast.error(apiErrorMessage(e, "Voice update failed")); }
        finally { setTranscribing(false); setPosting(false); }
      };
      mr.start();
      recRef.current = mr;
      setRecording(true);
      // Auto-stop after 30 seconds so we don't accidentally record
      // hours of ambient audio if the user forgets.
      setTimeout(() => { if (recRef.current === mr && mr.state === "recording") mr.stop(); }, 30000);
    } catch { toast.error("Microphone access denied"); }
  };

  return (
    <button
      type="button"
      onClick={start}
      disabled={transcribing || posting}
      className={`inline-flex items-center gap-1 px-2 py-1 border rounded-none text-[11px] transition-colors ${recording ? "bg-red-600 border-red-700 text-white animate-pulse" : "border-emerald-500/60 text-emerald-300 hover:bg-emerald-500/20 disabled:opacity-50"}`}
      title={recording ? "Stop recording" : "Record a 15–30 s voice status update"}
      data-testid="planning-present-voice-btn"
    >
      <span className="relative flex items-center justify-center w-3 h-3">
        <span className={`w-2 h-2 rounded-full ${recording ? "bg-white" : "bg-emerald-500"} ${busy && !recording ? "animate-ping" : ""}`}></span>
      </span>
      {label}
    </button>
  );
}


function PresentationOverlay({ project, idx, total, tasks, users, allProjects, watchedIds, wsId, members, projectWatched, onToggleProjectWatch, onOpenTask, onToggleWatch, onChangeStatus, onChangePriority, onOpenNotes, onOpenStatusUpdates, onProjectRefresh, onTaskCreated, onTasksReload, onJumpTo, onRestart, onPrev, onNext, onClose }) {
  const [jumpOpen, setJumpOpen] = useState(false);
  const [jumpQ, setJumpQ] = useState("");
  const [statusOpenFor, setStatusOpenFor] = useState(null); // task id of currently-open status popover
  const [prioOpenFor, setPrioOpenFor] = useState(null); // task id of currently-open priority popover
  const [assigneeOpenFor, setAssigneeOpenFor] = useState(null); // task id of currently-open assignee popover
  const [assigneeQuery, setAssigneeQuery] = useState("");
  // R159kk · Feb 2026 — Present-mode dark/light theme toggle.
  // Dark (default) keeps the original cinematic look; light flips
  // to a white background + dark text for high-glare rooms (bright
  // conference rooms, sunny co-working spaces) where dark UI washes
  // out on projectors. Persisted per browser.
  const [presentTheme, setPresentTheme] = useState(() => {
    try { return localStorage.getItem("co_present_theme") || "dark"; }
    catch { return "dark"; }
  });
  useEffect(() => {
    try { localStorage.setItem("co_present_theme", presentTheme); }
    catch { /* ignore */ }
  }, [presentTheme]);
  const isDark = presentTheme === "dark";
  // R159rr · Feb 2026 — Board-level light theme. Presenting on a
  // bright projector or in a lit conference room shouldn't feel like
  // dumped grey boxes on white — it should be crisp, high-contrast
  // and elegant. `tt` collects theme-aware class fragments so every
  // pill / card / panel / input reads correctly in both modes.
  const tt = {
    overlay: isDark ? "bg-neutral-900/95 text-white" : "bg-neutral-50 text-neutral-900",
    barBorder: isDark ? "border-neutral-800" : "border-neutral-200",
    // Chrome buttons in the top bar / footer / jump palette
    btnGhost: isDark
      ? "border border-neutral-700 hover:bg-neutral-800"
      : "border border-neutral-300 bg-white hover:bg-neutral-100",
    btnClose: isDark ? "hover:bg-neutral-800" : "hover:bg-neutral-200",
    // Meta pills (Start / Due) — sit against the page background
    pill: isDark
      ? "border-neutral-700 text-neutral-300"
      : "border-neutral-300 text-neutral-700 bg-white",
    pillMutedLbl: isDark ? "text-neutral-500" : "text-neutral-500",
    // KPI numbered cards
    kpiCard: isDark
      ? "border border-neutral-700 bg-transparent"
      : "border border-neutral-200 bg-white shadow-sm",
    kpiLabel: isDark ? "text-neutral-400" : "text-neutral-500",
    // Panels (Top open tasks wrapper)
    panel: isDark ? "border border-neutral-800" : "border border-neutral-200 bg-white shadow-sm",
    panelHead: isDark
      ? "border-b border-neutral-800 text-neutral-400"
      : "border-b border-neutral-200 text-neutral-500 bg-neutral-50",
    panelHint: isDark ? "text-neutral-500" : "text-neutral-500",
    quickAddRow: isDark ? "border-b border-neutral-800 bg-neutral-900/40" : "border-b border-neutral-200 bg-neutral-50/60",
    // Task cards inside "Top open tasks"
    taskCard: isDark
      ? "border border-neutral-700/70 bg-neutral-800/40 hover:bg-neutral-800/70"
      : "border border-neutral-200 bg-white hover:shadow-md hover:border-neutral-300 shadow-sm transition-shadow",
    taskNotesBtn: isDark
      ? "hover:bg-neutral-700 text-neutral-400"
      : "hover:bg-neutral-100 text-neutral-500",
    // Assignee avatar placeholder
    avatarBlank: isDark ? "bg-neutral-700 text-neutral-400" : "bg-neutral-100 text-neutral-500",
    // Jump-to palette
    palettePanel: isDark
      ? "bg-neutral-900 border border-neutral-700"
      : "bg-white border border-neutral-200 shadow-2xl",
    paletteInput: isDark
      ? "bg-neutral-800 text-white border-neutral-700"
      : "bg-neutral-50 text-neutral-900 border-neutral-200",
    paletteItem: isDark ? "hover:bg-neutral-800" : "hover:bg-neutral-100",
    paletteBorder: isDark ? "border-neutral-800" : "border-neutral-200",
    // Footer hint text
    hintText: isDark ? "text-neutral-500" : "text-neutral-500",
  };
  // R159o · Feb 2026 — Unread Accounts-notes badge on the Present-mode
  // Notes button. Refetches whenever the current project changes so
  // walking through the deck always shows a fresh count. Fails
  // silently for users without billing perms (the endpoint returns 0).
  const [unreadNotes, setUnreadNotes] = useState(0);
  useEffect(() => {
    let cancelled = false;
    if (!wsId || !project?.id) { setUnreadNotes(0); return; }
    (async () => {
      try {
        const { data } = await api.get(
          `/workspaces/${wsId}/billing-notes/unread-count`,
          { params: { scope: "project", scope_id: project.id } },
        );
        if (!cancelled) setUnreadNotes(data?.count || 0);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [wsId, project?.id]);
  // R158v · Feb 2026 — Sort open tasks NEWEST first STRICTLY by
  // `created_at` (backend guarantees ISO string on every task, including
  // ones returned from POST /tasks). Previous version fell back to
  // `due_date` when created_at was missing, which mixed timestamp
  // scales and could push a just-created task below tasks with a
  // future due_date — the exact bug users reported. Tasks with no
  // created_at (extremely rare) sort to the bottom.
  const openTasks = tasks
    .filter((t) => t.status !== "done")
    .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))
    .slice(0, 20);
  const done = tasks.filter((t) => t.status === "done").length;
  const overdue = tasks.filter((t) => t.status !== "done" && t.due_date && new Date(t.due_date) < new Date()).length;
  const progress = tasks.length ? Math.round((done / tasks.length) * 100) : 0;
  const status = STATUS_META[project.status] || STATUS_META.active;

  // R158r · Feb 2026 — Assignee change handler; PATCHes the task and
  // triggers onTasksReload so the assignee chip on the card updates
  // immediately.
  const changeAssignee = async (taskId, assigneeId, assigneeName) => {
    try {
      await api.patch(`/tasks/${taskId}`, { assignee_id: assigneeId || null });
      setAssigneeOpenFor(null);
      setAssigneeQuery("");
      if (onTasksReload) onTasksReload();
      toast.success(assigneeName ? `Reassigned to ${assigneeName}` : "Assignee cleared");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Reassign failed"));
    }
  };

  // Keyboard: ArrowLeft/Right prev/next, Esc close, "/" or "g" opens jump-to.
  useEffect(() => {
    const h = (e) => {
      if (jumpOpen) return; // let the search input own the keys
      // Don't hijack navigation keys while typing in the inline quick-add
      // input or any other form control.
      const tag = (e.target && e.target.tagName) || "";
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if (e.key === "Escape") { e.preventDefault(); onClose(); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); onPrev(); }
      else if (e.key === "ArrowRight" || e.key === " ") { e.preventDefault(); onNext(); }
      else if (e.key === "/" || (e.key.toLowerCase() === "g" && !e.metaKey && !e.ctrlKey)) {
        e.preventDefault(); setJumpOpen(true); setJumpQ("");
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose, onPrev, onNext, jumpOpen]);

  // R158q · Feb 2026 — Swipe navigation on touch devices (prev/next by
  // horizontal swipe). Threshold: 60px; ignore if the swipe started
  // inside a scrollable list to avoid conflicts with vertical scroll.
  const touchRef = useRef({ x: 0, y: 0, active: false });
  const onTouchStart = (e) => {
    const t = e.touches[0];
    touchRef.current = { x: t.clientX, y: t.clientY, active: true };
  };
  const onTouchEnd = (e) => {
    if (!touchRef.current.active) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchRef.current.x;
    const dy = t.clientY - touchRef.current.y;
    touchRef.current.active = false;
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      if (dx < 0 && idx < total - 1) onNext();
      else if (dx > 0 && idx > 0) onPrev();
    }
  };

  const filteredProjects = jumpQ.trim()
    ? (allProjects || []).map((p, i) => ({ p, i }))
        .filter(({ p }) => (p.name || "").toLowerCase().includes(jumpQ.toLowerCase()))
        .slice(0, 20)
    : (allProjects || []).map((p, i) => ({ p, i })).slice(0, 20);

  return (
    <div
      className={`fixed inset-0 z-[100] backdrop-blur-sm flex flex-col ${tt.overlay}`}
      data-testid="planning-present-overlay"
      data-present-theme={presentTheme}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Top bar — two-row layout on mobile so nothing gets clipped. */}
      <div className={`border-b px-3 sm:px-6 py-2 sm:py-3 ${tt.barBorder}`}>
        <div className="flex items-center gap-2 sm:gap-3">
          <span className={`w-3 h-3 rounded-full shrink-0 ${HEALTH_DOT[project.health] || "bg-neutral-500"}`} />
          <span className={`text-[10px] uppercase tracking-widest border px-2 py-0.5 shrink-0 ${status.cls}`}>{status.label}</span>
          <div className={`hidden sm:block text-xs truncate flex-1 ${tt.kpiLabel}`}>
            {project.client_name || ""}{project.brand_name ? ` · ${project.brand_name}` : ""}
            {(project.pm_name || project.owner_name) ? ` · PM: ${project.pm_name || project.owner_name}` : ""}
          </div>
          <span className={`font-mono text-xs ml-auto sm:ml-0 shrink-0 ${tt.kpiLabel}`}>{idx + 1} / {total}</span>
          {/* R159qq · Feb 2026 — Dark / Light theme toggle. Lifted out
              of the `hidden sm:flex` group so mobile users can flip
              the presentation theme too (Present mode is often used on
              a phone shown to a client). Cinematic dark stays default;
              light mode reads better on bright projectors. */}
          <button
            type="button"
            onClick={() => setPresentTheme(isDark ? "light" : "dark")}
            className={`inline-flex items-center gap-1 h-7 px-2 border rounded-none text-[10px] uppercase tracking-widest shrink-0 ${isDark ? "border-neutral-700 text-neutral-200 hover:bg-neutral-800" : "border-neutral-300 text-neutral-700 bg-white hover:bg-neutral-100"}`}
            title={isDark ? "Switch to Light mode" : "Switch to Dark mode"}
            data-testid="planning-present-theme-toggle"
          >
            {isDark ? <Sun className="w-3 h-3" /> : <Moon className="w-3 h-3" />}
            <span className="hidden sm:inline">{isDark ? "Light" : "Dark"}</span>
          </button>
          <div className="hidden sm:flex items-center gap-2">
            {/* R159o · Feb 2026 — Voice status update (dictate + auto-post). */}
            <PresentVoiceUpdateButton projectId={project.id} onPosted={onProjectRefresh} />
            {/* R159m · Feb 2026 — Open Project Status Updates from inside
                Present mode. The dialog is z-[130], so it renders above
                the presentation overlay (z-[100]). */}
            <button
              onClick={() => onOpenStatusUpdates && onOpenStatusUpdates(project)}
              className="inline-flex items-center gap-1 px-2 py-1 border border-primary/60 hover:bg-primary/20 text-primary text-[11px]"
              title="Open project status updates without leaving Present mode"
              data-testid="planning-present-status-btn"
            >
              <MessageSquare className="w-3 h-3" /> Project status
            </button>
            {/* R159n · Feb 2026 — Open Accounts Notes thread from inside
                Present mode (BillingNotesDialog is already z-[130]). */}
            <button
              onClick={() => onOpenNotes && onOpenNotes({ scope: "project", id: project.id, label: project.name })}
              className="relative inline-flex items-center gap-1 px-2 py-1 border border-primary/60 hover:bg-primary/20 text-primary text-[11px]"
              title={unreadNotes > 0 ? `${unreadNotes} unread account note${unreadNotes === 1 ? "" : "s"} on this project` : "Read & post Accounts notes for this project without leaving Present mode"}
              data-testid="planning-present-notes-btn"
            >
              <MessageSquarePlus className="w-3 h-3" /> Notes
              {unreadNotes > 0 && (
                <span className="ml-0.5 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold bg-red-600 text-white" data-testid="planning-present-notes-badge">{unreadNotes}</span>
              )}
            </button>
            <button
              onClick={() => { setJumpOpen(true); setJumpQ(""); }}
              className={`inline-flex items-center gap-1 px-2 py-1 ${tt.btnGhost} text-[11px]`}
              title="Jump to project (press / or g)"
              data-testid="planning-present-jump-btn"
            >
              <Search className="w-3 h-3" /> Jump to
            </button>
            <button
              onClick={onRestart}
              disabled={idx === 0}
              className={`inline-flex items-center gap-1 px-2 py-1 ${tt.btnGhost} text-[11px] disabled:opacity-40`}
              title="Start over from first project"
              data-testid="planning-present-restart"
            >
              <RotateCcw className="w-3 h-3" /> Start over
            </button>
          </div>
          <button
            onClick={onClose}
            className={`p-2 sm:p-1 rounded shrink-0 ${tt.btnClose}`}
            title="Close (Esc)"
            data-testid="planning-present-close"
          >
            <X className="w-5 h-5 sm:w-4 sm:h-4" />
          </button>
        </div>
        {/* Mobile-only sub-row: client · brand · PM + jump/restart */}
        <div className="sm:hidden flex items-center gap-2 mt-2">
          <div className={`text-[11px] truncate flex-1 ${tt.kpiLabel}`}>
            {project.client_name || ""}{project.brand_name ? ` · ${project.brand_name}` : ""}
            {(project.pm_name || project.owner_name) ? ` · PM: ${project.pm_name || project.owner_name}` : ""}
          </div>
          <button
            onClick={() => onOpenStatusUpdates && onOpenStatusUpdates(project)}
            className="inline-flex items-center gap-1 px-2 py-1.5 border border-primary/60 text-primary hover:bg-primary/20 text-[11px] shrink-0"
            data-testid="planning-present-status-btn-mobile"
          >
            <MessageSquare className="w-3 h-3" /> Status
          </button>
          <button
            onClick={() => onOpenNotes && onOpenNotes({ scope: "project", id: project.id, label: project.name })}
            className="relative inline-flex items-center gap-1 px-2 py-1.5 border border-primary/60 text-primary hover:bg-primary/20 text-[11px] shrink-0"
            data-testid="planning-present-notes-btn-mobile"
          >
            <MessageSquarePlus className="w-3 h-3" /> Notes
            {unreadNotes > 0 && (
              <span className="ml-0.5 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold bg-red-600 text-white">{unreadNotes}</span>
            )}
          </button>
          <button
            onClick={() => { setJumpOpen(true); setJumpQ(""); }}
            className={`inline-flex items-center gap-1 px-2 py-1.5 ${tt.btnGhost} text-[11px] shrink-0`}
            data-testid="planning-present-jump-btn-mobile"
          >
            <Search className="w-3 h-3" /> Jump
          </button>
        </div>
      </div>

      {/* Body — tighter padding on mobile so the H1 + KPIs still fit above the fold. */}
      <div className="flex-1 overflow-auto px-4 sm:px-8 md:px-12 py-5 sm:py-8 max-w-5xl mx-auto w-full">
        {/* R158s · Feb 2026 — Project name + Accounts Notes button
            R158v · Feb 2026 — Added Watch toggle + Project due date pill. */}
        <div className="flex items-start gap-3 mb-2 sm:mb-3">
          <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold leading-tight break-words flex-1 min-w-0">{project.name}</h1>
          <button
            type="button"
            onClick={() => onToggleProjectWatch && onToggleProjectWatch(project.id, !!projectWatched)}
            className={`shrink-0 mt-1 inline-flex items-center gap-1.5 px-3 py-1.5 border rounded-none text-xs sm:text-sm ${projectWatched ? "border-primary text-primary bg-primary/10" : "border-neutral-600 hover:border-primary hover:bg-primary/10 hover:text-primary"}`}
            title={projectWatched ? "Stop watching this project" : "Add project to watch list"}
            data-testid={`planning-present-project-watch-${project.id}`}
          >
            {projectWatched ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
            <span className="hidden sm:inline">{projectWatched ? "Watching" : "Watch"}</span>
          </button>
          <button
            type="button"
            onClick={() => onOpenNotes && onOpenNotes({ scope: "project", id: project.id, label: project.name })}
            className="shrink-0 mt-1 inline-flex items-center gap-1.5 px-3 py-1.5 border border-neutral-600 hover:border-primary hover:bg-primary/10 hover:text-primary text-xs sm:text-sm rounded-none relative"
            title="Send / view Accounts notes for this project"
            data-testid={`planning-present-project-notes-${project.id}`}
          >
            <MessageSquarePlus className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Notes</span>
            {project.billing_notes_count > 0 && (
              <span className="min-w-4 h-4 px-1 text-[9px] font-bold leading-4 bg-primary text-primary-foreground rounded-full">
                {project.billing_notes_count > 9 ? "9+" : project.billing_notes_count}
              </span>
            )}
          </button>
        </div>
        {/* R158v · Project meta row (due date, start date) */}
        {(project.due_date || project.end_date || project.start_date) && (
          <div className="flex flex-wrap items-center gap-2 mb-4 sm:mb-6 text-[11px]">
            {project.start_date && (
              <span className={`inline-flex items-center gap-1.5 px-2 py-1 border rounded-full font-mono ${tt.pill}`} data-testid={`planning-present-project-start-${project.id}`}>
                <Calendar className="w-3 h-3 opacity-70" />
                <span className={`uppercase tracking-widest text-[9px] ${tt.pillMutedLbl}`}>Start</span> {(project.start_date || "").slice(0, 10)}
              </span>
            )}
            {(project.due_date || project.end_date) && (() => {
              const dueRaw = project.due_date || project.end_date;
              const overdueProj = dueRaw && new Date(dueRaw) < new Date() && project.status !== "done";
              return (
                <span className={`inline-flex items-center gap-1.5 px-2 py-1 border rounded-full font-mono ${overdueProj ? "border-rose-500/50 text-rose-500 bg-rose-500/10" : tt.pill}`} data-testid={`planning-present-project-due-${project.id}`}>
                  <Calendar className="w-3 h-3 opacity-70" />
                  <span className="uppercase tracking-widest text-[9px] opacity-70">Due</span> {(dueRaw || "").slice(0, 10)}
                </span>
              );
            })()}
          </div>
        )}

        {/* KPI strip — 2 columns on mobile (tighter), 4 on md+ */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4 mb-5 sm:mb-8">
          <div className={`${tt.kpiCard} p-3 sm:p-4`}>
            <div className={`text-[10px] uppercase tracking-widest ${tt.kpiLabel}`}>Open</div>
            <div className="text-2xl sm:text-3xl font-mono mt-1">{openTasks.length}</div>
          </div>
          <div className={`${tt.kpiCard} p-3 sm:p-4`}>
            <div className={`text-[10px] uppercase tracking-widest ${tt.kpiLabel}`}>Overdue</div>
            <div className={`text-2xl sm:text-3xl font-mono mt-1 ${overdue ? (isDark ? "text-rose-400" : "text-rose-600") : ""}`}>{overdue}</div>
          </div>
          <div className={`${tt.kpiCard} p-3 sm:p-4`}>
            <div className={`text-[10px] uppercase tracking-widest ${tt.kpiLabel}`}>Progress</div>
            <div className="text-2xl sm:text-3xl font-mono mt-1">{progress}%</div>
          </div>
          <div className={`${tt.kpiCard} p-3 sm:p-4`}>
            <div className={`text-[10px] uppercase tracking-widest ${tt.kpiLabel}`}>Total</div>
            <div className="text-2xl sm:text-3xl font-mono mt-1">{tasks.length}</div>
          </div>
        </div>

        {/* Open tasks list */}
        <div className={tt.panel}>
          <div className={`px-3 sm:px-4 py-2 text-[10px] uppercase tracking-widest ${tt.panelHead} flex items-center justify-between gap-2`}>
            <span>Top open tasks</span>
            <span className={`${tt.panelHint} normal-case hidden sm:inline`}>Tap chips to edit inline</span>
          </div>
          {/* R158q · Feb 2026 — Inline "+ New task" row so the presenter can
              capture action items live from a standup without leaving the
              overlay. Uses the shared QuickAddTaskInline component so the
              inherited-client/brand toast + validation match everywhere. */}
          {wsId && (
            <div className={`px-3 sm:px-4 py-2 ${tt.quickAddRow}`} data-testid="planning-present-quick-add-row">
              <QuickAddTaskInline
                wsId={wsId}
                preset={{ project_id: project.id }}
                projects={allProjects || []}
                members={members || []}
                onCreated={(newTask) => {
                  // R158t · Feb 2026 — Optimistic insert first, then
                  // reconcile. Guarantees the just-added task shows up
                  // in the top-N list even if the reload races or the
                  // user has already navigated to a different project
                  // before the POST resolved.
                  if (onTaskCreated) onTaskCreated(newTask);
                  else if (onTasksReload) onTasksReload();
                }}
                testid={`planning-present-quick-add-${project.id}`}
                placeholder="+ New task for this project…"
              />
            </div>
          )}
          {openTasks.length === 0 ? (
            <div className={`p-6 text-center text-sm italic ${tt.panelHint}`}>No open tasks.</div>
          ) : (
            <ul className="p-2 sm:p-3 space-y-2 sm:space-y-2.5">
              {openTasks.map((t) => {
                const overdueTask = t.due_date && new Date(t.due_date) < new Date();
                // R158r · Feb 2026 — Look up assignee by id in both the
                // `users` list (planning-board-data projection) and the
                // `members` list (workspace directory), because the picker
                // draws from members but users may lag until the next
                // full board refresh. This keeps the pill label in sync
                // right after a reassign.
                const assignee = users.find((u) => u.id === t.assignee_id)
                  || (members || []).find((m) => m.id === t.assignee_id)
                  || (t.assignee_id ? { id: t.assignee_id, name: t.assignee_name || "Assigned" } : null);
                const statusMeta = isDark ? ({
                  open:        { label: "Open",        dot: "bg-neutral-400", pill: "border-neutral-600 text-neutral-200" },
                  in_progress: { label: "In progress", dot: "bg-amber-400",   pill: "border-amber-500/50 text-amber-300" },
                  blocked:     { label: "Blocked",     dot: "bg-rose-500",    pill: "border-rose-500/50 text-rose-300" },
                  done:        { label: "Done",        dot: "bg-emerald-500", pill: "border-emerald-500/50 text-emerald-300" },
                })[t.status] || { label: t.status || "Open", dot: "bg-neutral-400", pill: "border-neutral-600 text-neutral-200" } : ({
                  open:        { label: "Open",        dot: "bg-neutral-400", pill: "border-neutral-300 text-neutral-700 bg-white" },
                  in_progress: { label: "In progress", dot: "bg-amber-400",   pill: "border-amber-400 text-amber-700 bg-amber-50" },
                  blocked:     { label: "Blocked",     dot: "bg-rose-500",    pill: "border-rose-400 text-rose-700 bg-rose-50" },
                  done:        { label: "Done",        dot: "bg-emerald-500", pill: "border-emerald-400 text-emerald-700 bg-emerald-50" },
                })[t.status] || { label: t.status || "Open", dot: "bg-neutral-400", pill: "border-neutral-300 text-neutral-700 bg-white" };
                const isWatched = watchedIds ? watchedIds.has(t.id) : false;
                const filteredMembers = (members || []).filter((m) => {
                  if (!assigneeQuery.trim()) return true;
                  const q = assigneeQuery.toLowerCase();
                  return (m.name || "").toLowerCase().includes(q) || (m.email || "").toLowerCase().includes(q);
                }).slice(0, 12);
                return (
                  <TaskHoverCard key={t.id} task={t} users={users} side="left" align="start">
                    <li
                      className={`${tt.taskCard} cursor-pointer rounded-sm p-3 sm:p-3.5 flex flex-col gap-2.5 relative ${overdueTask ? "border-l-2 border-l-rose-500" : ""}`}
                      onClick={() => onOpenTask && onOpenTask(t.id)}
                      data-testid={`planning-present-task-${t.id}`}
                    >
                      {/* Row 1 — title (wraps freely) + notes + watch toggle */}
                      <div className="flex items-start gap-2">
                        <span className="flex-1 min-w-0 text-base sm:text-lg font-medium leading-snug break-words">{t.title}</span>
                        <button
                          onClick={(e) => { e.stopPropagation(); onOpenNotes && onOpenNotes({ scope: "task", id: t.id, label: t.title }); }}
                          className={`shrink-0 p-1.5 rounded relative ${tt.taskNotesBtn} hover:text-primary`}
                          title="Send / view Accounts notes for this task"
                          data-testid={`planning-present-task-notes-${t.id}`}
                          aria-label={`Open Accounts notes for ${t.title}`}
                        >
                          <MessageSquarePlus className="w-4 h-4" />
                          {t.billing_notes_count > 0 && (
                            <span className="absolute -top-0.5 -right-0.5 min-w-3.5 h-3.5 px-0.5 text-[8px] font-bold leading-3.5 bg-primary text-primary-foreground rounded-full">
                              {t.billing_notes_count > 9 ? "9+" : t.billing_notes_count}
                            </span>
                          )}
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); onToggleWatch && onToggleWatch(t.id, isWatched); }}
                          className={`shrink-0 p-1.5 rounded ${isDark ? "hover:bg-neutral-700" : "hover:bg-neutral-100"} ${isWatched ? "text-primary" : (isDark ? "text-neutral-500 hover:text-neutral-200" : "text-neutral-500 hover:text-neutral-800")}`}
                          title={isWatched ? "Remove from watch list" : "Add to watch list"}
                          data-testid={`planning-present-task-watch-${t.id}`}
                          aria-label={isWatched ? "Unwatch task" : "Watch task"}
                        >
                          {isWatched ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </button>
                      </div>

                      {/* Row 2 — interactive pills (wrap on small screens) */}
                      <div className="flex flex-wrap items-center gap-2 text-xs">
                        {/* Status pill */}
                        <Popover open={statusOpenFor === t.id} onOpenChange={(v) => setStatusOpenFor(v ? t.id : null)}>
                          <PopoverTrigger asChild>
                            <button
                              type="button"
                              className={`inline-flex items-center gap-1.5 px-2 py-1 border rounded-full ${isDark ? "hover:bg-neutral-700/50" : "hover:bg-neutral-100"} transition-colors ${statusMeta.pill}`}
                              onClick={(e) => { e.stopPropagation(); setStatusOpenFor(t.id); }}
                              title={`Status: ${statusMeta.label} · tap to change`}
                              data-testid={`planning-present-task-status-dot-${t.id}`}
                            >
                              <span className={`w-2 h-2 rounded-full ${statusMeta.dot}`} />
                              {statusMeta.label}
                            </button>
                          </PopoverTrigger>
                          <PopoverContent
                            side="bottom" align="start" className="w-44 p-1 rounded-none z-[120]"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {[
                              { k: "open", label: "Open", d: "bg-neutral-400" },
                              { k: "in_progress", label: "In progress", d: "bg-amber-400" },
                              { k: "blocked", label: "Blocked", d: "bg-rose-500" },
                              { k: "done", label: "Done", d: "bg-emerald-500" },
                            ].map((s) => (
                              <button
                                key={s.k}
                                onClick={(e) => { e.stopPropagation(); setStatusOpenFor(null); onChangeStatus && onChangeStatus(t.id, s.k); }}
                                className={`w-full text-left text-xs px-2 py-1.5 hover:bg-muted flex items-center gap-2 text-neutral-800 ${t.status === s.k ? "bg-primary/10" : ""}`}
                                data-testid={`planning-present-task-status-opt-${t.id}-${s.k}`}
                              >
                                <span className={`w-2 h-2 rounded-full ${s.d}`} /> {s.label}
                              </button>
                            ))}
                          </PopoverContent>
                        </Popover>

                        {/* Priority pill */}
                        <Popover open={prioOpenFor === t.id} onOpenChange={(v) => setPrioOpenFor(v ? t.id : null)}>
                          <PopoverTrigger asChild>
                            <button
                              type="button"
                              className={`inline-flex items-center gap-1.5 px-2 py-1 border rounded-full capitalize ${isDark ? "border-neutral-600 text-neutral-200 hover:bg-neutral-700/50" : "border-neutral-300 text-neutral-700 bg-white hover:bg-neutral-100"}`}
                              onClick={(e) => { e.stopPropagation(); setPrioOpenFor(t.id); }}
                              title={`Priority: ${t.priority || "medium"} · tap to change`}
                              data-testid={`planning-present-task-prio-btn-${t.id}`}
                            >
                              <PriorityIcon priority={t.priority || "medium"} className="w-3.5 h-3.5" />
                              {t.priority || "medium"}
                            </button>
                          </PopoverTrigger>
                          <PopoverContent
                            side="bottom" align="start" className="w-36 p-1 rounded-none z-[120]"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {["urgent", "high", "medium", "low"].map((p) => (
                              <button
                                key={p}
                                onClick={(e) => { e.stopPropagation(); setPrioOpenFor(null); onChangePriority && onChangePriority(t.id, p); }}
                                className={`w-full text-left text-xs px-2 py-1.5 hover:bg-muted flex items-center gap-2 capitalize text-neutral-800 ${t.priority === p ? "bg-primary/10" : ""}`}
                                data-testid={`planning-present-task-prio-opt-${t.id}-${p}`}
                              >
                                <PriorityIcon priority={p} className="w-3 h-3" /> {p}
                              </button>
                            ))}
                          </PopoverContent>
                        </Popover>

                        {/* Assignee pill (opens picker) */}
                        <Popover
                          open={assigneeOpenFor === t.id}
                          onOpenChange={(v) => { setAssigneeOpenFor(v ? t.id : null); if (!v) setAssigneeQuery(""); }}
                        >
                          <PopoverTrigger asChild>
                            <button
                              type="button"
                              className={`inline-flex items-center gap-1.5 px-2 py-1 border rounded-full max-w-[180px] ${isDark ? "border-neutral-600 text-neutral-200 hover:bg-neutral-700/50" : "border-neutral-300 text-neutral-700 bg-white hover:bg-neutral-100"}`}
                              onClick={(e) => { e.stopPropagation(); setAssigneeOpenFor(t.id); setAssigneeQuery(""); }}
                              title={assignee ? `Assigned to ${assignee.name}` : "Unassigned · tap to assign"}
                              data-testid={`planning-present-task-assignee-btn-${t.id}`}
                            >
                              <Users className="w-3 h-3 shrink-0" />
                              <span className="truncate">{assignee ? assignee.name : "Unassigned"}</span>
                            </button>
                          </PopoverTrigger>
                          <PopoverContent
                            side="bottom" align="start" className="w-64 p-0 rounded-none z-[120]"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="p-2 border-b">
                              <input
                                autoFocus
                                value={assigneeQuery}
                                onChange={(e) => setAssigneeQuery(e.target.value)}
                                placeholder="Search member…"
                                className="w-full text-xs px-2 py-1.5 border border-input outline-none focus:border-primary bg-white text-neutral-800"
                                data-testid={`planning-present-task-assignee-search-${t.id}`}
                              />
                            </div>
                            <div className="max-h-64 overflow-y-auto">
                              <button
                                onClick={(e) => { e.stopPropagation(); changeAssignee(t.id, null, null); }}
                                className={`w-full text-left text-xs px-3 py-2 hover:bg-muted flex items-center gap-2 text-neutral-800 ${!t.assignee_id ? "bg-primary/10" : ""}`}
                                data-testid={`planning-present-task-assignee-opt-${t.id}-none`}
                              >
                                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] ${tt.avatarBlank}`}>—</span>
                                <span className="italic text-neutral-500">Unassigned</span>
                              </button>
                              {filteredMembers.length === 0 ? (
                                <div className="px-3 py-3 text-center text-[11px] text-neutral-500 italic">No members match.</div>
                              ) : filteredMembers.map((m) => (
                                <button
                                  key={m.id}
                                  onClick={(e) => { e.stopPropagation(); changeAssignee(t.id, m.id, m.name); }}
                                  className={`w-full text-left text-xs px-3 py-2 hover:bg-muted flex items-center gap-2 text-neutral-800 ${t.assignee_id === m.id ? "bg-primary/10" : ""}`}
                                  data-testid={`planning-present-task-assignee-opt-${t.id}-${m.id}`}
                                >
                                  <span className="w-6 h-6 rounded-full bg-primary/15 text-primary flex items-center justify-center text-[10px] font-semibold shrink-0">{(m.name || m.email || "?").slice(0, 2).toUpperCase()}</span>
                                  <span className="flex-1 min-w-0 truncate">{m.name || m.email}</span>
                                </button>
                              ))}
                            </div>
                          </PopoverContent>
                        </Popover>

                        {/* Due date */}
                        {t.due_date && (
                          <span className={`inline-flex items-center gap-1 px-2 py-1 border rounded-full font-mono ${overdueTask ? (isDark ? "border-rose-500/50 text-rose-300 bg-rose-500/10" : "border-rose-400 text-rose-700 bg-rose-50") : (isDark ? "border-neutral-600 text-neutral-300" : "border-neutral-300 text-neutral-700 bg-white")}`}>
                            <span className="w-1.5 h-1.5 rounded-full bg-current opacity-60" />
                            {t.due_date}
                          </span>
                        )}
                      </div>
                    </li>
                  </TaskHoverCard>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {/* Nav footer — bigger tap targets on mobile; hint text hidden below sm. */}
      <div className={`flex items-center justify-between gap-2 px-3 sm:px-6 py-3 border-t text-sm ${tt.barBorder}`}>
        <button onClick={onPrev} disabled={idx === 0} className={`inline-flex items-center gap-1 px-4 sm:px-3 py-2 sm:py-1.5 ${tt.btnGhost} disabled:opacity-30`} data-testid="planning-present-prev">
          <ChevronLeft className="w-4 h-4" /> <span className="hidden sm:inline">Prev</span>
        </button>
        <div className={`text-xs hidden md:block ${tt.hintText}`}>
          ← / → to navigate · / or G to jump · Esc to close · swipe on mobile
        </div>
        <div className={`text-[11px] md:hidden font-mono ${tt.hintText}`}>{idx + 1} / {total}</div>
        <button onClick={onNext} disabled={idx >= total - 1} className={`inline-flex items-center gap-1 px-4 sm:px-3 py-2 sm:py-1.5 ${tt.btnGhost} disabled:opacity-30`} data-testid="planning-present-next">
          <span className="hidden sm:inline">Next</span> <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Jump-to-project palette */}
      {jumpOpen && (
        <div
          className="absolute inset-0 z-[110] bg-black/60 flex items-start justify-center pt-24"
          onClick={() => setJumpOpen(false)}
          data-testid="planning-present-jump-overlay"
        >
          <div
            className={`w-[560px] max-w-[92vw] ${tt.palettePanel}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`p-2 border-b ${tt.paletteBorder}`}>
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-neutral-500" />
                <input
                  autoFocus
                  value={jumpQ}
                  onChange={(e) => setJumpQ(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") { setJumpOpen(false); }
                    else if (e.key === "Enter" && filteredProjects.length > 0) {
                      const first = filteredProjects[0];
                      onJumpTo(first.i);
                      setJumpOpen(false);
                    }
                  }}
                  placeholder="Search a project to jump to…"
                  className={`w-full pl-8 pr-2 h-9 text-sm outline-none border focus:border-primary ${tt.paletteInput}`}
                  data-testid="planning-present-jump-input"
                />
              </div>
            </div>
            <ul className="max-h-80 overflow-y-auto">
              {filteredProjects.length === 0 ? (
                <li className={`px-3 py-4 text-center text-xs italic ${tt.hintText}`}>No projects match.</li>
              ) : (
                filteredProjects.map(({ p, i }) => {
                  const st = STATUS_META[p.status] || STATUS_META.active;
                  return (
                    <li key={p.id}>
                      <button
                        onClick={() => { onJumpTo(i); setJumpOpen(false); }}
                        className={`w-full text-left px-3 py-2 flex items-center gap-2 text-sm ${tt.paletteItem} ${i === idx ? "bg-primary/20" : ""}`}
                        data-testid={`planning-present-jump-opt-${p.id}`}
                      >
                        <span className={`w-2 h-2 rounded-full ${HEALTH_DOT[p.health] || "bg-neutral-500"}`} />
                        <span className="flex-1 truncate">{p.name}</span>
                        <span className={`text-[9px] uppercase tracking-widest border px-1.5 py-0.5 ${st.cls}`}>{st.label}</span>
                        <span className={`text-[10px] font-mono w-10 text-right ${tt.hintText}`}>{i + 1}</span>
                      </button>
                    </li>
                  );
                })
              )}
            </ul>
            <div className={`px-3 py-1.5 border-t text-[10px] flex items-center gap-3 ${tt.paletteBorder} ${tt.hintText}`}>
              <span>Enter · pick top</span>
              <span>Esc · close</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

